<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useRoute, onBeforeRouteUpdate } from 'vue-router'
import { $router } from '@/router'
import { $bus, mqtt } from '@/common/events'
import { defaultServiceOptions } from '@/common/config'
import { dialog } from '@/common/dialog'
import { toast } from '@/common/toast'
import { $virtualKeyboard, utc, Stopwatch, buildSearchKeywords } from '@/common/helper'
import { formatRelativeTime, formatLocaleDate, formatLocaleTime } from '@/common/format'
import { hcu } from '@/common/hcu'
import { useTitle } from '@/composables/title'
import { useTranslate } from '@/composables/language'
import { useMenu, useSubmenu, useTopBar } from '@/composables/menu'
import { useVirtualKeyboard } from '@/composables/keyboard'
import { useAppState } from '@/composables/app'
import { useSession } from '@/composables/session'
import { useSettings } from '@/composables/settings'
import { useSystemStatus } from '@/composables/system'
import { useCabinetAlarm } from '@/composables/alarm'
import { canInterrupt } from '@/composables/interrupt'
import { useSessionTimer } from '@/composables/session'
import { useCabinetUnits } from "@/composables/cabinet.js"
import { useAuthState } from '@/composables/auth'
import { useWeightScale } from '@/composables/hardware/weightScale'
import { onBarcodeReceived } from '@/composables/hardware/barcode'
import { B414CabinetControl, alphabet } from '@/drivers/cabinet/PLT-B414.js'
import { L18CabinetControl } from '@/drivers/cabinet/PLT-L18.js'
import { L64CabinetControl } from '@/drivers/cabinet/PLT-L64.js'
import { M100CabinetControl } from '@/drivers/cabinet/PLT-M100.js'
import { M280CabinetControl } from '@/drivers/cabinet/PLT-M280.js'
import { M280TCabinetControl } from '@/drivers/cabinet/PLT-M280T.js'
import { OrganizationService } from '@/services/OrganizationService.js'
import { ArticleService } from '@/services/ArticleService.js'
import { CabinetService } from '@/services/CabinetService.js'
import { ConfigService } from '@/services/ConfigService.js'
import { CacheService } from '@/services/CacheService.js'
import { UserService } from '@/services/UserService.js'
import { LogService } from '@/services/LogService.js'
import Breadcrumbs from '@/components/Breadcrumbs.vue'
import Pagination from '@/components/Pagination.vue'
import Drawer from '@/components/Drawer.vue'
import Toast from '@/components/Toast.vue'
import Fullscreen from '@/components/Fullscreen.vue'
import WarningBanner from '@/components/WarningBanner.vue'
import AutocompleteTextBox from '@/components/AutocompleteTextBox.vue'
import TextAreaTouchFriendly from '@/components/TextAreaTouchFriendly.vue'
import ReferencePosition from '@/components/Maintenance/ReferencePosition.vue'
import PushButton from '@/components/PushButton.vue'
import TextArea from '@/components/TextArea.vue'

const catalogStopwatch = new Stopwatch();

const { t } = useTranslate();
const { isKeyboardVisible } = useVirtualKeyboard();
const { weightScale } = useWeightScale();
const { alarmBus } = useCabinetAlarm();
const { latestStatus, isServiceMode } = useSystemStatus();
const { showUserItemsDisplayedOnce } = useSession();
const { settings } = useSettings();
const { appState } = useAppState();

const isModalVisible = ref(false);
const isClosing = ref(null); //Undefined
const isOpening = ref(null); //Undefined
const isOpen = ref(null); //Undefined
const isReopen = ref(null);
const isReferencing = ref(false);
const isReferenced = ref(false);
const isPrevOpen = ref(false);
const isLoading = ref(true);
const user = ref(null);
const users = ref([ ]);

let organizationService = null;
let articleService = null;
let cabinetService = null;
let configService = null;
let cacheService = null;
let userService = null;
let logService = null;

let reportEmptyStock = false;
let bestTrayPreference = "lowest-quantity-first"; //lowest-quantity-first, highest-quantity-first

const isAdmin = ref(false);
const currentUserId = ref(null);
const hasPrivateTrays = ref(false);
const canCalibrateWeightScale = ref(false);
const canTransferStock = ref(false);

const authStore = useAuthState();

const cabinetId = ref(null);
const cabinetIds = ref();
const isMultiCabinet = ref(false);
//const settings = ref({ });
const error = ref();

const autoConfirm = ref(true);
const autoReopenDelay = ref(null);
const autoLogoutConfirmed = ref(false);

const categoryDesign = ref(1);

//paging = pagination or scroll lazy-load
const searchText = ref("");

const categoryBreadcrumbs = ref([
  { id: "_root", name: "Kategorije" },
  //{ id: "CT123", name: "Kovinski" },
  //{ id: "CT234", name: "Vijaki" },
]);

const isFilterVisible = ref(false);

const isLargeImageView = ref(false);

//Borrow and return article mode
const isCabinetBorrowMode = computed(() => {
  if (selectedTrayItem.value && selectedTrayItem.value.countMode && selectedTrayItem.value.countMode != "default") {
    return selectedTrayItem.value.countMode == "vault"; //storage, vault, private
  }
  return isCabinetBorrowModeDefault.value;
});
const isCabinetBorrowModeDefault = ref(false);

const autoSelectPrivateTray = ref(true);

//User's private vault mode
const isCabinetPrivateMode = computed(() => {
  return selectedCategoryId.value == "_private";
});

//const isSingleArticle = computed(() => rows.value?.length == 1);
const isSingleArticle = computed(() => false);

const isSearchFilter = computed(() => {
  return searchText.value?.length > 0;
});

//Must be reactive variable to trigger "computed"
const allPossibleCategories = ref([ ]);
const allPossibleParameters = ref([ ]);
const allPossibleArticles = ref([ ]);

//Current category list
const parentCategoryId = ref("_root"); //null for root
const selectedCategoryId = ref("_root"); //null for root
const currentCategory = ref(null); //null for category data

const allowImageFallback = ref(true); //Allow to take image from category if article does not have it's own image defined
const currentCategoryImageUrl = computed(() => currentCategory.value?.smallImageUrl);
const nearestParentCategorySmallImageUrl = ref();
const nearestParentCategoryLargeImageUrl = ref();

const canOpenTrayStatus = ref(false);
const canOpenTrayPermissions = ref(false);
const isTakenByOtherUser = ref(false);

const isRootCategory = computed(() => selectedCategoryId.value == null || selectedCategoryId.value == "_root");
const isLastCategory = computed(() => !categoriesFiltered.value || categoriesFiltered.value.length == 0); //No subcategories

const isCategorySectionVisible = computed(() => {
  //No category loaded or defined
  if (!allPossibleCategories.value || allPossibleCategories.value.length == 0) {
    return false;
  }

  //Explicitly set by user to hide categories
  if (settings.value?.catalog?.categoryVisibility == "never") {
    return false;
  }

  //Filter, user's preference
  if (isShowAllArticles.value) {
    return false;
  }

  //When only one article available for selection, no need to display categories
  //Commented: need to display categories when settings to display articles on last category is set
  //if (isSingleArticle.value) {
  //  return false;
  //}

  return true;
});

const isPrivateVisible = computed(() => {
  return selectedCategoryId.value == "_private" && !isUserItemsVisible.value;
  //return isPrivateView.value;
});

const isUserItemsVisible = ref(false);
const isAutoUserItems = ref(false);

const userItems = ref(null);

const isParameterSectionVisible = computed(() => {
  //No parameter loaded or defined
  if (!parameters || parameters.length == 0) {
    return false;
  }

  //Hidden in private tray mode
  if (isPrivateVisible.value) {
    return false;
  }

  //Hidden on user's items list
  if (isUserItemsVisible.value) {
    return false;
  }

  //Only one article available for selection, no need to display parameters
  if (isSingleArticle.value) {
    return false;
  }

  //Explicitly set by user to hide parameters
  if (settings.value?.catalog?.parameterVisibility == "never") {
    return false;
  }

  //Show only when inside the last level category (i.e. category without children)
  if (settings.value?.catalog?.parameterVisibility == "last category") {
    return isLastCategory.value;
  }

  return true;
});

const isArticleSectionVisible = computed(() => {
  //Show only when inside the last level category (i.e. category without children)
  if (settings.value?.catalog?.articleVisibility == "last category") {
    return isLastCategory.value;
  }

  return true;
});

/*
const categoryTree = ref({
  id: "_root",
  name: "Vsi izdelki",
  items: [
    {
      id: "CT123",
      name: "Kovinski",
      items: [
        {
          id: "CT234",
          name: "Vijaki",
          items: [
            { id: "CT235", name: "Vijaki M3" },
            { id: "CT236", name: "Vijaki M4" },
            { id: "CT237", name: "Vijaki M5" },
          ]
        },
      ]
    },
    {
      id: "CT124",
      name: "Plastični",
    },
    {
      id: "CT125",
      name: "Lesni",
    },
  ]
});
*/

//Flat list (not tree)
const categories = ref([
  // { id: "CT235", name: "Vijaki M3" },
  // { id: "CT236", name: "Vijaki M4" },
  // { id: "CT237", name: "Vijaki M5" },
]);

const categoriesFiltered = computed(() => {
  let choice = settings.value?.catalog?.catalogDataVisibility;
  switch (choice) {
    //Display all defined categories (even if empty)
    case "all":
      return categories.value;

    //Display only categories that have articles inside
    default:
    case "filtered":
      return categories.value?.filter(x => x.articlesCount > 0) || [ ];
  }
});

const parameters = ref([
  /*
  {
    id: "PR123",
    name: "Dolžina",
    options: [
      { id: "PR-D1", name: "1 mm" },
      { id: "PR-D2", name: "2 mm" },
      { id: "PR-D3", name: "3 mm" },
    ]
  },
  {
    id: "PR234",
    name: "Masa",
    options: [
      { id: "PR-M1", name: "1 kg" },
      { id: "PR-M2", name: "2 kg" },
      { id: "PR-M3", name: "3 kg" },
    ]
  }
  */
]);

const allCabinetIds = ref([ ]);

//const isShowAllArticlesInOrganization = ref(true); //May take long time to load
const isShowAllArticlesInOrganization = computed(() => {
  const articleListMode = settings.value?.catalog?.articleListMode;
  switch (articleListMode) {
    //Display articles from all cabinets in the organization (warning: may take longer time to load)
    case "all-cabinets":
      return true;

    //Display only articles mapped with the current cabinet (master and its satellite units)
    default:
    case "current-cabinet":
      return false;
  }
});

const isShowAllArticles = ref(false);
const isUserPrefSize = ref(false);

const selectedSortOption = ref("alphanum-asc"); //default, alphanum-asc, ...
const sortOptions = ref([
  //{ id: "default", name: "Priporočeno" },
  { id: "alphanum-asc", name: "Po abecedi naraščajoče (A-Ž)" }, //Upošteva številke, npr. M3 je pred M12
  //{ id: "alpha-asc", name: "Po abecedi naraščajoče (A-Ž)" }, //Ne upošteva številk, npr. M12 je pred M3
  { id: "alphanum-desc", name: "Po abecedi padajoče (Ž-A)" },
  // { id: "alpha-desc", name: "Po abecedi padajoče (Ž-A)" },
  { id: "quantity-asc", name: "Po zalogi naraščajoče" },
  { id: "quantity-desc", name: "Po zalogi padajoče" },
  { id: "created-desc", name: "Nazadnje dodani artikli" },
  //{ id: "used-desc", name: "Nazadnje uporabljeni" },
]);

const displayMode = ref("list");

function setDisplayMode(mode) {
  switch (mode) {
    case "list":
    case "grid":
      displayMode.value = mode;
      break;
  }
}

const isWarningBannerVisible = ref(false);
const warningBannerModal = ref();

const isDevelopmentMode = ref(false);
const isLocalCacheEnabled = ref(false);

const suggestions = ref([
  //{ name: "Zaščitne rokavice" },
  //{ name: "Alu profili" },
  //{ name: "Sveder" },
]);

const quantity = ref("");

const isStockMismatch = ref(false);
const isEditStockQuantity = ref(false);
const hasStockQuantityChanged = computed(() => !stockQuantityChange.value || stockQuantityChange.value !== 0);
const stockQuantityChange = computed(() => {
  return (virtualStockQuantity.value || 0) - (initialStockQuantity.value || 0);
});
const initialStockQuantity = ref(null); //Stock quantity before the change
const virtualStockQuantityNumeric = ref(null); //Stock quantity when changed, but not applied yet (edit mode)
const virtualStockQuantity = computed({
  get() {
    return virtualStockQuantityNumeric.value;
  },
  set(value) {
    if (value != virtualStockQuantityNumeric.value && !isNaN(value)) {
      virtualStockQuantityNumeric.value = value;
    }
  }
});

const articleLowStockThreshold = computed(() => selected.value?.reorderPoint?.minQuantity || 0);

const virtualStockQuantityState = computed(() => {
  let quantity = virtualStockQuantity.value;
  if (!quantity || quantity <= 0) {
    return "empty";
  }
  if (quantity < articleLowStockThreshold.value) {
    return "low"; //Below threshold
  }
  if (isStockMismatch.value) {
    return "warning"; //Stored and measured value does not match
  }
  return "normal";
});

let openingTimer = null;

let localCabinets = null;

let cabinetControllers = { };

//Get cabinet controller by specific model
function resolveCabinetController(cabinet) {
  switch (cabinet.model) {
    case "plt-b414":
      return new B414CabinetControl({ mqtt: mqtt, mac: cabinet.mac });

    case "plt-l18":
      return new L18CabinetControl({ mqtt: mqtt, mac: cabinet.mac });

    case "plt-l64":
      return new L64CabinetControl({ mqtt: mqtt, mac: cabinet.mac });

    case "plt-p9":
      return new L18CabinetControl({ mqtt: mqtt, mac: cabinet.mac }); //TODO: Replace with its own logic; for now it borrows from L18

    case "plt-m100":
    case "plt-m280":
      return new M280CabinetControl({ mqtt: mqtt, mac: cabinet.mac });

    case "plt-m280t":
      return new M280TCabinetControl({ mqtt: mqtt, mac: cabinet.mac })

    default:
      console.error("[catalog] Unsupported cabinet model", cabinet.model, cabinet.id, cabinet.mac);
      break;
  }
}

async function initializeCabinetControllers() {
  if (!localCabinets || localCabinets.length == 0) {
    console.error("[catalog] No cabinet defined!");
    return;
  }

  cabinetIds.value = localCabinets.map(x => x.id);
  isMultiCabinet.value = (localCabinets.length > 1);

  //Loop through each cabinet definition
  for (let cabinet of localCabinets) {
    //MAC address is required for identifying a cabinet topic
    if (!cabinet.mac) {
      console.error("[catalog] Cabinet has no 'mac' address defined!", cabinet.id, cabinet);
      continue;
    }

    console.log(`[catalog] Initializing cabinet controller ${cabinet.model} [${cabinet.mac}] ${cabinet.id}: ${cabinet.name}`);

    //Initialize cabinet controller by specific model
    let controller = resolveCabinetController(cabinet);

    //Attach common events and add to the list
    if (controller) {
      controller.model = cabinet.model;
      controller.cabinet = cabinet;

      //Not sure if needed but just in case
      controller.removeAllListeners();
      controller.removeAllListeners("connect");
      controller.removeAllListeners("disconnect");
      controller.removeAllListeners("timeout");
      controller.removeAllListeners("status");
      controller.removeAllListeners("open");
      controller.removeAllListeners("opening");
      controller.removeAllListeners("closing");
      controller.removeAllListeners("closed");
      controller.removeAllListeners("status-change");
      controller.removeAllListeners("delivered");
      controller.removeAllListeners("stored");

      controller.on("connect", () => {
        console.log("[catalog] Cabinet controller connected", controller.mac);
      });
      controller.on("disconnect", () => {
        console.log("[catalog] Cabinet controller disconnected", controller.mac);
      });
      controller.on("timeout", () => {
        console.log("[catalog] timeout", controller.mac);
        if (controller.model == "plt-m100" || controller.model == "plt-m280" || controller.model == "plt-m280t") {
          return; //Skip message for m100/m280 cabinets
        }

        //Avoid settings those two values for m100/m280 because emergency stop button may still be pressed in
        isOpening.value = false;
        isClosing.value = false;

        toast(new Error(t('catalog.open_door_not_signaled')));
      });

      //Cabinet status changed
      controller.on("status", (data) => {
        console.log("[catalog] Cabinet status", controller.mac, data);
        if (data.status == "error" && data.substatus == "alarm_curtain") { //plt-m100 and plt-m280
          if (isOpening.value || isClosing.value) {
            // isCurtainAlarm.value = true;
            // useCabinetAlarm({ alarm: "curtain" });
          }
        }
        else if (data.status == "error" && data.substatus == "alarm_halt") { //plt-m100 and plt-m280
          isHaltAlarm.value = true;
          useCabinetAlarm({ alarm: "halt" });
        }
        else if (data.status == "error" && data.substatus == "disrupt") { //plt-m100 and plt-m280
          isDisruptAlarm.value = true;
          useCabinetAlarm({ alarm: "disrupt" });
        }
        else if (data.status == "error" && data.substatus == "not_ref" && (controller.model == "plt-b414" || controller.model == "plt-m100" || controller.model == "plt-m280")) {
          if (lastRequest.value) {
            console.log("[re-open] not_ref");
            lastRequest.value.openAfterRef = true;
            lastRequest.value.controller.goToRef();
            canOpenTray.value = false;
          }
          else if (!isNotRef.value) {
            //dialog(new Error(t('catalog.not_ref')));
            toast(new Error(t('catalog.not_ref')));
          }
        }
        else if (data.status == "success" && (data.substatus == "ref_ok" || data.substatus == "ready") && (controller.model == "plt-b414" || controller.model == "plt-m100" || controller.model == "plt-m280")) {
          updateMachineReferenceStatus().then(x => { }).catch(err => { });
          if (lastRequest.value?.openAfterRef) {
            //console.log("[re-open] ref_ok => openTray", lastRequest.value.tray);
            lastRequest.value.openAfterRef = false;
            setTimeout(() => {
              //canOpenTray.value = true;
              //openTray(lastRequest.value.tray);
              if (lastRequest.value?.controller) {
                lastRequest.value.controller.openTray(lastRequest.value.payload)
                  .then(x => { canOpenTray.value = true; })
                  .catch(err => { canOpenTray.value = true; });
              }
            }, 300);
          }
        }
      });

      //Tray has just been opened
      controller.on("open", (data) => {
        console.log("TRAY OPEN")
        console.log("[catalog] open event", getAnyOpen(), isAnyOpen.value, data.value);
        for (let key in cabinetControllers) {
          console.log("[catalog] open controller", cabinetControllers[key].mac, cabinetControllers[key].isOpen, typeof cabinetControllers[key].isOpen)
        }
        //isOpen.value = isAnyOpen.value; //data.value would give only for the current controller while isAnyOpen.value gives for all
        isOpen.value = getAnyOpen();
        console.log("TRAY OPEN isOpen", isOpen.value)
        //isPrevOpen.value = isOpen.value && !selectedTrayItem.value;
        if (isOpen.value) {
          isOpening.value = false;
          isClosing.value = false;
          controller.isOpening = false;
          controller.isClosing = false;
          console.log("TRAY OPEN isReopen", isReopen.value)
          if (!isReopen.value) {
            isEditStockQuantity.value = true;
            console.log("TRAY OPEN isEditStockQuantity", isEditStockQuantity.value)
          }

          //Analyze
          for (let key in cabinetControllers) {
            if (cabinetControllers[key].isOpen) {
              console.log("[catalog] cabinet controller", key, "isOpen =", cabinetControllers[key].isOpen);
            }
          }

          //Tare
          if (isScaleReady.value && !isReopen.value) {
            tare();
          }

        }
      });

      //Only for moving trays
      controller.on("opening", (data) => {
        console.log("[catalog] opening");
        //console.log("[catalog] opening", getAnyOpening(), isAnyOpening.value);
        for (let key in cabinetControllers) {
          console.log("opening controller", cabinetControllers[key].mac, cabinetControllers[key].isOpening, typeof cabinetControllers[key].isOpening)
        }
        //isOpening.value = isAnyOpening.value; //data.value would give only for the current controller while isAnyOpening.value gives for all
        //isOpening.value = getAnyOpening();
        isOpening.value = true; //Obviously true, because it is the "opening" event
        isClosing.value = false;
      });

      //Only for moving trays
      controller.on("closing", () => {
        console.log("[catalog] closing");
        isOpening.value = false;
        isClosing.value = true;

        //Commented: moved auto log out to "stored" event (in case if someone hits the big red button during the procedure - this page needs to handle interruptions, not the login page)
        /*
        //Auto log out after requested to close the tray
        if (settings.value?.session?.trayAutoLogout == true) {
          console.log("[catalog] auto log out");
          const autoLogOutDelay = 1000;
          setTimeout(() => {
            isEditStockQuantity.value = false;
            isOpening.value = false;
            isOpen.value = false;
            logout();
          }, autoLogOutDelay);
        }
        */
      });

      //Door closed
      controller.on("closed", (data) => {
        console.log("[catalog] closed", data);

        console.log("[catalog] autoLogoutConfirmed.value", autoLogoutConfirmed.value);

        //Situation when user manually closes the tray without measuring and applying the weight count
        if (controller.model == "plt-b414" && isEditStockQuantity.value && !isCabinetBorrowMode.value && isScaleReady.value && !isScaleCalibration.value && !isScaleValueApplied.value && autoReopenDelay.value > 0) {
          startReopenTimer();
        }
        //Auto logout
        else if (settings.value?.session?.trayAutoLogout == true && hasIssuedOpenTrayAtLeastOnce.value && autoLogoutConfirmed.value) {
          hideWarningBanner();
          canInterrupt(true);
          useSessionTimer(true);
          logout();
          return;
        }
      });

      //Controller status changed
      controller.on("status-change", (data) => {
        console.log("[catalog] status-change", data);
        applyStatus(controller, data);
      });

      //PLT-M100 and PLT-M280
      controller.on("delivered", () => {
        console.log("[catalog] delivered");
        isEditStockQuantity.value = true;
        isOpening.value = false;
        isClosing.value = false;
        isOpen.value = true;

        //Tare
        if (isScaleReady.value) {
          tare();
        }
      });

      //PLT-M100 and PLT-M280
      controller.on("stored", () => {
        console.log("[catalog] stored");

        //Auto log out after requested to close the tray
        if (settings.value?.session?.trayAutoLogout == true) {
          console.log("[catalog] auto log out");
          const autoLogOutDelay = 1000;
          setTimeout(() => {
            isEditStockQuantity.value = false;
            isOpening.value = false;
            isOpen.value = false;
            logout();
          }, autoLogOutDelay);
        }
        else {
          isEditStockQuantity.value = false;
          isOpening.value = false;
          isClosing.value = false;
          isOpen.value = false;
        }
      });

      //Map cabinet id with controller instance
      cabinetControllers[cabinet.id] = controller;

      //Get last status now
      applyStatus(controller, await hcu.cabinet(cabinet.mac).getStatus2());

    }
  }

  //Get status of each cabinet controller
  for (let key in cabinetControllers) {
    if (typeof cabinetControllers[key].requestStatus == "function") {
      cabinetControllers[key].requestStatus();
    }
  }
}

function applyStatus(controller, data) {
  console.log("[cabinet] applyStatus", data);
  controller.lastStatus = data;
  controller.isOpen = data.isOpen;
  controller.isOpening = data.isOpening;
  controller.isClosing = data.isClosing;
  controller.isReferencing = data.isReferencing;
  controller.isReferenced = data.isReferenced;

  let debug = { };
  let i = 1;
  for (let key in cabinetControllers) {
    let c = cabinetControllers[key];
    debug["c" + i] = (c.isReferencing ? 1 : 0) + "," + (c.isReferenced ? 1 : 0) + "," + (c.isOpening ? 1 : 0) + "," + (c.isOpen ? 1 : 0);
    i++;
  }
  $bus.emit("debug footer", debug);

  isOpen.value = getAnyOpen();
  if (isOpen.value) {
    isOpening.value = false;
    //isClosing.value = false;
    controller.isOpening = false;
    controller.isClosing = false;
    isEditStockQuantity.value = true;
  }
  else {
    isOpening.value = getAnyOpening();
  }

  isReferenced.value = getAnyReferenced();
  isReferencing.value = getAnyReferencing();
}

//Detach from any event to prevent running in background
function disposeCabinetControllers() {
  console.log("[catalog] Disposing cabinet controllers...");
  let keys = Object.keys(cabinetControllers);
  for (let key of keys) {
    try {
      let controller = cabinetControllers[key];
      controller.removeAllListeners();
      controller.dispose();
      delete cabinetControllers[key];
    }
    catch (err) {
      console.error("[catalog] Dispose", key, err);
    }
  }
}

const isNotRef = ref(false);
async function updateMachineReferenceStatus() {
  let { controllers } = await hcu.cabinet().getInstances2();
  for (let controller of controllers) {
    if (controller.model == "plt-b414" && controller.ref !== true) {
      //isNotRef.value = true;
    }
  }
}

/******************************************************************************************
 * UI component mounted/unmounted
 ******************************************************************************************/
onMounted(async () => {
  useTitle(t('menu.catalog'));
  useMenu("catalog");
  useSubmenu(false);
  useTopBar(true);

  useSessionTimer(true);

  alarmBus.on("continue", continueAfterAlarm);

  initializeComponents();
  initializeWeightScale();
});

onUnmounted(async () => {  
  alarmBus.off("continue", continueAfterAlarm);
  disposeCabinetControllers();
  disposeWeightScale();
});

/******************************************************************************************
 * Initialization
 ******************************************************************************************/
async function initializeComponents() {
  try {
    let sw = new Stopwatch();
    isLoading.value = true;

    console.log("[benchmark] Check point 1:", catalogStopwatch.elapsed() + "ms (initializeComponents)", catalogStopwatch.delta());

    //Initialize services
    const serviceOptions = defaultServiceOptions();
    organizationService = new OrganizationService(serviceOptions);
    articleService = new ArticleService(serviceOptions);
    cabinetService = new CabinetService(serviceOptions);
    configService = new ConfigService(); //Local only
    //console.log("default service options", serviceOptions)
    //cacheService = new CacheService({ accessToken: serviceOptions?.accessToken }); //Local only
    cacheService = new CacheService({ apiKey: serviceOptions?.apiKey }); //Local only
    userService = new UserService(serviceOptions);
    logService = new LogService(serviceOptions);

    console.log("[benchmark] Check point 2:", catalogStopwatch.elapsed() + "ms (init)", catalogStopwatch.delta());

    //Check if cabinet admin
    let permissions = authStore.permissions.value || [];
    isAdmin.value = permissions.includes("cabinet.admin");
    canCalibrateWeightScale.value = permissions.includes("cabinet.admin") || permissions.includes("cabinet.service");
    canTransferStock.value = permissions.includes("cabinet.admin") || permissions.includes("cabinet.service");
    currentUserId.value = authStore.userId.value;

    console.log("[benchmark] Check point 3:", catalogStopwatch.elapsed() + "ms (variables)", catalogStopwatch.delta());

    //Get cabinet definitions
    // const cabinetStore = useCabinetStore();
    // localCabinets = cabinetStore.getCabinets();
    const { cabinetUnits } = useCabinetUnits();
    localCabinets = cabinetUnits.value;

    console.log("[benchmark] Check point 4:", catalogStopwatch.elapsed() + "ms (cabinetStore)", catalogStopwatch.delta());

    //Initialize hardware communication
    await initializeCabinetControllers();

    console.log("[benchmark] Check point 5:", catalogStopwatch.elapsed() + "ms (initializeCabinetControllers)", catalogStopwatch.delta());

    //Load user defined settings for this cabinet
    await loadSettings();

    console.log("[benchmark] Check point 6:", catalogStopwatch.elapsed() + "ms (loadSettings)", catalogStopwatch.delta());

    //Display mode: list or grid
    if (settings.value?.catalog?.displayMode) {
      setDisplayMode(settings.value?.catalog?.displayMode);
    }

    //Debug flag
    isDevelopmentMode.value = settings.value?.debug?.enabled;

    //Get cabinet reference position status
    setTimeout(async () => {
      try {
        await updateMachineReferenceStatus();
      }
      catch (err) {
        if (err.name == "SyntaxError" || err.message.startsWith("JSON.parse")) {
          toast(new Error(t('catalog.hcu_not_responding')));
        }
        else {
          toast(err);
        }
      }
    }, 200);

    //Load with delay
    setTimeout(async () => {
      try {      
        console.log("[benchmark] Check point 7:", catalogStopwatch.elapsed() + "ms (setTimeout)", catalogStopwatch.delta());

        //await loadCabinetsSlow();
        await loadCabinets(); //Slightly faster

        console.log("[benchmark] Check point 8:", catalogStopwatch.elapsed() + "ms (loadCabinets)", catalogStopwatch.delta());



        const isPromiseAll = ref(false);
        if (!isPromiseAll.value) {
          //Sequential loading
          await loadParameters();
          console.log("[benchmark] Check point 9a:", catalogStopwatch.elapsed() + "ms (loadParameters)", catalogStopwatch.delta());

          await loadCabinetStock(); //Takes 2s!
          console.log("[benchmark] Check point 9b:", catalogStopwatch.elapsed() + "ms (loadCabinetStock)", catalogStopwatch.delta());

          await loadArticles();
          console.log("[benchmark] Check point 9c:", catalogStopwatch.elapsed() + "ms (loadArticles)", catalogStopwatch.delta());

          await loadUserInventory();
          console.log("[benchmark] Check point 9d:", catalogStopwatch.elapsed() + "ms (loadUserInventory)", catalogStopwatch.delta());
        }
        else {
          //Parallel loading can improve loading performance for 0-500 ms
          await Promise.all([ loadParameters(), loadCabinetStock(), loadArticles(), loadUserInventory() ]);
          console.log("[benchmark] Check point 9:", catalogStopwatch.elapsed() + "ms (articles, stock, parameters, user inventory)", catalogStopwatch.delta());
        }
        

        



        await loadCategories();
        selectCategory("_root");
        console.log("[benchmark] Check point 10:", catalogStopwatch.elapsed() + "ms (loadCategories)", catalogStopwatch.delta());

        await loadUser();
        isUserPrefSize.value = true;
        console.log("[benchmark] Check point 11:", catalogStopwatch.elapsed() + "ms (loadUser)", catalogStopwatch.delta());
        
        //await loadUserItems();
        await loadUsers(); //Can be postponed

        console.log("[benchmark] Check point 12:", catalogStopwatch.elapsed() + "ms (loadUsers)", catalogStopwatch.delta());

        //Immeditely show list of current user's borrowed tools (if settings are set so)
        if (settings.value?.preferences?.listUsersItems && !showUserItemsDisplayedOnce.value) {
          showUserItems({ mustHaveItems: true, isAutoLoad: true });
          console.log("[benchmark] Check point 13:", catalogStopwatch.elapsed() + "ms (showUserItems)", catalogStopwatch.delta());
          showUserItemsDisplayedOnce.value = true;
          setTimeout(() => {
            isLoading.value = false;
          }, 500);
        }
        else {
          isLoading.value = false;
        }

        console.log("[benchmark] Total load time:", catalogStopwatch.elapsed() + "ms");
      }
      catch (err) {
        console.error("[benchmark] Loading finished with error:", catalogStopwatch.elapsed() + "ms", err);
        if (err.name == "SyntaxError" || err.message.startsWith("JSON.parse")) {
          //toast(new Error(t('catalog.component_not_responding')));
          error.value = new Error(t('catalog.component_not_responding'));
        }
        else if (err.message.includes("Cabinet not found")) { //TODO: Error code
          toast(new Error(t('catalog.cabinet_not_configured')));
        }
        else {
          toast(err);
        }
      }
    }, 1);

    /*
    //Load category tree
    try {
      await loadCategories();
      selectCategory("_root");
    }
    catch (err) {
      toast(err); //dialog(err);
    }
    */

    //console.log("[benchmark] Total init time:", catalogStopwatch.elapsed() + "ms", catalogStopwatch.delta());
  }
  catch (err) {
    console.error("[catalog] Initialization error", err)
    if (err.name == "SyntaxError" || err.message.startsWith("JSON.parse")) {
      error.value = new Error(t('catalog.component_not_responding'));
    }
    else {
      //toast(err);
      error.value = err;
    }
  }
}

/******************************************************************************************
 * Barcode scanner
 ******************************************************************************************/
onBarcodeReceived((data) => {
  let code = data?.code;
  lastBarcode.value = code;
  if (code && !isModalVisible.value && !isUserItemsVisible.value) {
    let primary = rows.value.filter(x => x.sku && x.sku == code);
    let secondary = rows.value.filter(x => x.sku2 && x.sku2 == code && primary.find(p => p.id != x.id)); //Exclude primary if the same item has sku = sku2
    let list = [ ...primary, ...secondary ];
    if (list.length == 1) {
      //Show the only one
      showModal(list[0]);
    }
    else if (list.length > 0) {
      //Show options
      showBarcode(list);
    }
    else {
      //Show message
      toast({
        type: "warning",
        title: code,
        text: "Artikel s to črtno kodo ne obstaja.", //Article with this barcode not found.
        canClose: true,
        timeout: 3000
      });
    }
  }
});

const lastBarcode = ref();

/******************************************************************************************
 * Weight scale
 ******************************************************************************************/
function initializeWeightScale() {
  weightScale.on("weight measure", onScaleMeasure);
  weightScale.on("weight device info", onScaleReady);

  //Send 3 times just to be sure the device responds with info
  console.log("[scale] requestDeviceInfo", true);

  setTimeout(() => {
    weightScale.requestDeviceInfo();
  }, 10);

  setTimeout(() => {
    weightScale.requestDeviceInfo();
  }, 100);

  setTimeout(() => {
    weightScale.requestDeviceInfo();
  }, 2000);
}

function disposeWeightScale() {
  weightScale.removeListener("weight measure", onScaleMeasure);
  weightScale.removeListener("weight device info", onScaleReady);
}

const canScaleFilterValue = ref(false);
const canScaleAverageValue = ref(true);
const lastScaleFilteredValue = ref(0);
const scaleFilterCoefficient = ref(0.1); //Filter coefficient (0 < alpha < 1)  

function filterWeightScaleValue(value) {
  //Reference: https://www.youtube.com/watch?v=cxJzCWz6xCA
  const alpha = scaleFilterCoefficient.value; //Filter coefficient (0 < alpha < 1)  
  lastScaleFilteredValue.value = alpha * value + (1 - alpha) * lastScaleFilteredValue.value;
  return lastScaleFilteredValue.value;
}

//A value indicating whether weight scale device is present (attached and ready)
const isScaleReady = ref(false);

//Calibration mode
const isScaleCalibration = ref(false);
const isScaleBanner = ref(true);

const isItemWeightCalibrated = ref(false);

const scaleItemsUserCount = ref(0);
const scaleItemsUserCountFormatted = computed(() => {
  if (!isNaN(scaleItemsUserCount.value)) {
    return scaleItemsUserCount.value;
  }
  return null;
});

const scaleLastValue = ref();
const scaleLastValueFormatted = computed(() => {
  if (!isNaN(scaleLastValue.value) && scaleLastUnit.value == "g") {
    return parseFloat(scaleLastValue.value).toFixed(1);
  }
  return null;
});
const scaleLastValueIntegerFormatted = computed(() => {
  if (!isNaN(scaleLastValue.value) && scaleLastUnit.value == "g") {
    return parseFloat(Math.round(scaleLastValue.value)).toFixed(0);
  }
  return null;
});
const scaleLastUnit = ref();
const scaleInsertWeight = ref(118);
const scaleInsertWeightFormatted = computed(() => {
  if (!isNaN(scaleInsertWeight.value)) {
    return parseFloat(scaleInsertWeight.value).toFixed(1);
  }
  return null;
});

const scaleItemWeight = computed(() => {
  //Count method by removing items
  if (scaleItemsUserCount.value < 0) {
    let total = virtualStockQuantity.value;
    if (total > 0 && scaleLastValue.value > 0) {
      let contentCount = parseInt(total) + parseInt(scaleItemsUserCount.value);
      let contentWeight = scaleLastValue.value - parseFloat(scaleInsertWeight.value);
      return contentCount > 0 ? contentWeight / contentCount : null;
    }
  }
  //Count method by adding items
  else {
    let contentCount = parseInt(scaleItemsUserCount.value);
    let contentWeight = scaleLastValue.value - parseFloat(scaleInsertWeight.value);
    return contentCount > 0 ? contentWeight / contentCount : null;
  }
  return null;
});

const scaleItemWeightFormatted = computed(() => {
  let value = scaleItemWeight.value;
  return value > 0 ? value.toFixed(1) : null;
});

const scaleItemsCount = computed(() => {
  let measuredWeight = scaleStableValue.value || scaleLastValue.value;
  if (measuredWeight > 0 && scaleItemWeightCalibrated.value > 0) {
    let contentWeight = measuredWeight - parseFloat(scaleInsertWeight.value);
    return contentWeight / scaleItemWeightCalibrated.value;
  }
  return null;
});

const scaleItemsCountFormatted = computed(() => {
  let value = scaleItemsCount.value;
  return value > 0 ? Math.abs(value).toFixed(0) : null;
});

//Weight of a single item unit (when counting pieces)
const scaleItemWeightCalibrated = ref();

//Number of items taken from the scale
const scaleItemsCountTaken = computed(() => {
  let itemsCountOnScale = scaleItemsCount.value;
  let itemsCountStart = initialScaleItemsCount.value;
  if (itemsCountOnScale !== null && itemsCountStart !== null /* && itemsCountStart >= itemsCountOnScale */) {
    return parseInt(Math.round(itemsCountStart - itemsCountOnScale));
  }
  return null;
});

const scaleItemsCountTakenFormatted = computed(() => {
  let value = scaleItemsCountTaken.value;
  if (value > 0) {
    return "- " + Math.abs(value);
  }
  if (value < 0) {
    return "+ " + Math.abs(value);
  }
  return null;
});

//Measured consequential values to detect stability of the load
let scaleMeasureSequence = [ ];
const isScaleValueStable = ref();
const scaleStableValue = ref(); //Either stable value or null
const initialScaleItemsCount = ref(); //Tare will reset the counter
const minInitialLoad = 5; //5g minimum initial load to detect initial items count after tare
const isScaleValueApplied = ref();
const isScaleValueRequired = ref(false);
const isScaleAllowContinue = computed(() => {
  //If weighting articles is required by the settings
  if (isScaleReady.value && isScaleValueRequired.value) {
    return isScaleValueApplied.value ? true : false;
  }
  //Allow to continue
  return true;
});

let reopenTimer = null;

function startReopenTimer() {
  console.log("[catalog] startReopenTimer");
  clearTimeout(reopenTimer);
  reopenTimer = setTimeout(() => {
    //Check again if after the timeout is still in edit mode
    if (isEditStockQuantity.value && !isScaleValueApplied.value) {
      reopenTray();
    }
  }, autoReopenDelay.value || 2000);
}

function stopReopenTimer() {
  console.log("[catalog] stopReopenTimer");
  clearTimeout(reopenTimer);
  reopenTimer = null;
}

//or if used clicks on a button up/down for quantity
watch(virtualStockQuantity, () => {
  isScaleValueApplied.value = true;
  stopReopenTimer();
});

watch(isScaleValueApplied, () => {
  stopReopenTimer();
});

const scaleStableSamplesCount = computed(() => {
  let sampleRate = scaleSampleRate.value;
  if (sampleRate == 1) {
    return 20; //2 seconds
  }
  else if (sampleRate < 10) {
    return 30 / sampleRate; //About 3 seconds
  }
  else if (sampleRate == 10) {
    return 3; //3 seconds
  }
  return 2; //2 measurements
});

//On weight scale measurement reply
function onScaleMeasure(data) {
  const value = canScaleFilterValue.value ? filterWeightScaleValue(data.value) : data.value;
  scaleLastValue.value = value;
  scaleLastUnit.value = data.unit;

  //Measure few consequential values to detect weight stability
  const maxSamplesCount = scaleStableSamplesCount.value;
  scaleMeasureSequence.push(value);

  //Collect enough samples...
  if (scaleMeasureSequence.length > maxSamplesCount) {
    scaleMeasureSequence.splice(0, 1);
  }

  //Then determine stability and calculate average
  if (scaleMeasureSequence.length >= maxSamplesCount) {
    let seq = scaleMeasureSequence;
    let len = seq.length;
    let stable = true;
    const tolerance = 0.05; //5% tolerance - if all consequential values are within the tolerance the value is considered stable
    const safeOffset = 30; //Artificially shift 30g above zero, because it is hard to calculate exact tolerance near zero values
    for (let i = 1; i < len; i++) {
      let prev = seq[i - 1] + safeOffset;
      let cur = seq[i] + safeOffset;
      let p = Math.abs((cur - prev) / cur);
      if (p > tolerance) {
        stable = false;
        break;
      }
    }

    //Calculate average weight from array of samples
    let avg = value;
    if (canScaleAverageValue.value) {
      let sum = 0;
      let count = Math.min(Math.ceil(maxSamplesCount / 2), len); //Better UX, more respnsive if taking half of the samples to average, not more
      for (let i = 0; i < count; i++) {
        sum += seq[len - i - 1];
      }
      avg = count > 1 ? sum / count : value; //Take last measured value if only 1 sample
    }

    //Detect missing stock: measure when stable load after tare - compare against stored items count from database
    if (!isScaleValueStable.value && stable && initialScaleItemsCount.value == null && avg > minInitialLoad) {
      initialScaleItemsCount.value = scaleItemsCount.value;

      if (!isScaleValueApplied.value && !isScaleCalibration.value) {
        //There must be some minimal amount to compare, because with lower integer values it is not practical to measure tolerance, otherwise changes 0 => 1 would respond in 100% change or infinite if div by zero
        let minWeight = 30; //Below that value no mismatch check will be performed
        if (avg >= minWeight) {
          let tolerancePrcent = 0.01; //1% in change would result in mismatch, i.e. 10g at 1000g load
          let toleranceWeight = 10; //10g

          //Calculate missing stock by items count - inaccurate if low amount of items that are heavy
          // let diff = initialStockQuantity.value - (initialScaleItemsCount.value || 0);
          // let ratio = Math.abs(diff / initialStockQuantity.value);
          // isStockMismatch.value = ratio > tolerancePrcent;

          //Calculate missing stock by items weight - more accurate
          let databaseValueWeight = initialStockQuantity.value * scaleItemWeight.value;
          let contentWeight = avg - parseFloat(scaleInsertWeight.value);
          let diff = databaseValueWeight - contentWeight;
          if (avg < 1000) { //Less than 1kg - by weight
            //isStockMismatch.value = Math.abs(diff) > toleranceWeight;
          }
          else { //More or equal to 1kg - by percent
            let ratio = Math.abs(diff / databaseValueWeight);
            //isStockMismatch.value = ratio > tolerancePrcent;
          }
        }
      }
    }

    //Apply stable value
    isScaleValueStable.value = stable;
    scaleStableValue.value = avg || value;
  }

  //Note: some weight scales do not respond on "device info" command.
  //Therefore enable weight scale on first successful measurement.
  if (!isScaleReady.value) {
    isScaleReady.value = true;
    console.log("[scale] isScaleReady", true);
  }
}

//On weight scale hardware info report
function onScaleReady(data) {
  isScaleReady.value = true;
  console.log("[scale] isScaleReady", true, data);
  scaleSampleRate.value = data?.sampleRate || data?.measurementCount || 10;
}

const scaleSampleRate = ref(10); //1 sample = 100ms, 10 samples = 1s

//Zero out current weight
function tare() {
  weightScale.tare();
  initialScaleItemsCount.value = null;
  isScaleValueApplied.value = false;
  lastScaleFilteredValue.value = 0;
}

function scaleItemUpdate(delta) {
  if (scaleItemsUserCount.value + delta > 0) {
    scaleItemsUserCount.value += delta;
  }
}

function applyEmptyInsertWeight() {
  scaleInsertWeight.value = scaleLastValue.value;

  let cid = primaryCabinetId.value;
  updateEmptyInsertWeight(cid, scaleInsertWeight.value);
}

function applyScaleItemWeight() {
  scaleItemWeightCalibrated.value = scaleItemWeight.value;
}

function applyScaleItemsCount() {
  virtualStockQuantity.value = parseInt(scaleItemsCountFormatted.value);
  initialScaleItemsCount.value = null;
  isScaleValueApplied.value = true;
  isStockMismatch.value = false;
}

function applyScaleCalibration() {
  let value = parseFloat(scaleItemWeightFormatted.value);

  isItemWeightCalibrated.value = value > 0 ? true : false;
  scaleItemWeightCalibrated.value = value > 0 ? value : null;

  let articleId = selected.value?.articleId;
  updateArticleUnitWeight(articleId, scaleItemWeightCalibrated.value);

  setWeightScaleCalibrationMode(false);
}

function cancelScaleCalibration() {
  setWeightScaleCalibrationMode(false);
}

function setWeightScaleCalibrationMode(enable) {
  scaleItemsUserCount.value = 1; //Reset
  isScaleCalibration.value = enable || false;
  isStockMismatch.value = false;
}

/******************************************************************************************
 * Resolve image URL from relative path
 ******************************************************************************************/
function resolveImageUrl(imagePath) {
  if (isLocalCacheEnabled.value) {
    return imagePath ? (cacheService.options.baseUrl || "") + "/hcu/v1/cache/file/" + imagePath : null; //Cached
  }
  return imagePath ? (articleService.options.baseUrl || "") + "/api/v1/file/" + imagePath : null; //Live
  
}

/******************************************************************************************
 * Load all articles
 ******************************************************************************************/

let isLoadArticlesOnceStarted = false;
let isLoadArticlesOnceDone = false;
const allPossibleArticles2 = ref();

async function preloadArticlesOnce() {
  //Load only once
  if (isLoadArticlesOnceDone) {
    return;
  }

  if (isLoadArticlesOnceStarted) {
    return;
  }

  isLoadArticlesOnceStarted = true;

  //if (!allPossibleArticles.value || allPossibleArticles.value.length == 0) {
  let sw = new Stopwatch();

  let query = { };

  if (cabinetIds.value?.length > 0) {
    query.cabinets = cabinetIds.value.join(",");
  }

  //Query all articles
  if (isShowAllArticlesInOrganization.value) {
    query.all = true;
  }

  //query.take = 1500; //Suppose database contains up to 1500 records
  query.take = 10000;

  //Calculate performance cost
  let keywordsCost = 0;
  let userTimeCost = 0;
  let quantityCost = 0;
  let vaultItemCost = 0;

  //let result = await articleService.getArticles(query);
  let result = isLocalCacheEnabled.value ? await cacheService.getArticles(query) : await articleService.getArticles(query);
  console.log("[loadCabinetStock] isLocalCacheEnabled", isLocalCacheEnabled.value);
  console.log("[loadCabinetStock] getArticles", sw.elapsed() + "ms");

  allPossibleArticles2.value = (result?.items || []).map(x => {
    let sw1 = new Stopwatch();
    let traysSearchKeyword = x.trays && x.trays.length ? x.trays.map(t => t.name).join(" ") : "";
    keywordsCost += sw1.elapsed();

    //Print article and its trays for debug
    // if (x.sku == "V12" || x.sku == "debug") {
    //   console.log("[article]", x.sku, x.title);
    //   console.log("[trays]", x.trays);
    // }

    let sw2 = new Stopwatch();
    //Convert string to Date object - required for formatting
    if (x.trays && x.trays.length) {
      for (let t of x.trays) {
        if (t.userTime && typeof t.userTime == "string") {
          t.userTime = new Date(t.userTime);
        }
      }
    }
    userTimeCost += sw2.elapsed();

    let sw3 = new Stopwatch();
    let quantity = stockSum(x.trays);
    quantityCost += sw3.elapsed();

    let sw4 = new Stopwatch();
    let vault = quantity > 0 ? false : isVaultItem(x.trays);
    vaultItemCost += sw4.elapsed();

    return {
      id: x.id,
      name: x.title,
      sku: x.sku,
      sku2: x.sku2,
      tray: x.tray, //TODO: What if multiple trays?
      trays: x.trays,
      stock: x.stock, //TODO: Stock in which tray
      totalQuantity: quantity,
      isVaultItem: vault,
      itemWeight: x.itemWeight,
      permissions: x.permissions,
      description: x.description,
      originalImageId: x.originalImageId,
      largeImagePath: x.largeImagePath,
      largeImageUrl: resolveImageUrl(x.largeImagePath),
      mediumImagePath: x.mediumImagePath,
      mediumImageUrl: resolveImageUrl(x.mediumImagePath),
      smallImagePath: x.smallImagePath,
      smallImageUrl: resolveImageUrl(x.smallImagePath),
      thumbnailPath: x.thumbnailPath,
      thumbnailUrl: resolveImageUrl(x.thumbnailPath),
      categories: x.categories,
      parameters: x.parameters,
      related: x.related,
      keywords: `${x.id} ${x.title} ${x.tray} ${traysSearchKeyword}`.trim().toLowerCase(),
      isArticle: true, //For search
    };
  });

  console.log("[loadCabinetStock] Consumed time for building search words", keywordsCost + "ms");
  console.log("[loadCabinetStock] Consumed time for mapping user time", userTimeCost + "ms");
  console.log("[loadCabinetStock] Consumed time for counting quantity", quantityCost + "ms");
  console.log("[loadCabinetStock] Consumed time for detecting vault items", vaultItemCost + "ms");

  //Find all possible parameter values
  //await mapParameters();

  console.log("[preloadArticlesOnce] Total load time", sw.elapsed().toFixed(2) + "ms");

  isLoadArticlesOnceDone = true;
}

async function mapParameters() {
  //Find all possible parameter values
  if (allPossibleParameters.value?.length > 0) {
    //Convert list to object with parameter id keys
    let parameterMap = { };
    let parameterIdList = [ ];
    for (let parameter of allPossibleParameters.value) {
      if (!parameter.options) {
        parameter.options = [ ];
      }
      parameterMap[parameter.id] = parameter;
      parameterIdList.push(parameter.id);
    }

    function sortable(value) {
      //Sort by numeric value
      if (!isNaN(value)) {
        return parseFloat(value);
      }

      //M + number => sort by number
      let m1 = /^M(\d+)$/.exec(value);
      if (m1) {
        return parseInt(m1[1]);
      }

      //Number + unit => sort by number
      let m2 = /^([\+\-]?\d+(\.\d)*)\s*(m|cm|mm|g|kg)$/.exec(value);
      if (m2) {
        return parseFloat(m2[1]);
      }
      
      //Default alphabetic sort
      return value;
    }

    //Find all possible parameter values
    for (let article of allPossibleArticles.value) {
      if (article.parameters) {
        let keys = Object.keys(article.parameters);
        for (let key of keys) {
          let value = article.parameters[key];

          //Insert only unique
          if (parameterMap[key] && !parameterMap[key].options.find(x => x.name == value)) {
            parameterMap[key].options.push({ id: value, name: value, selected: false, sort: sortable(value) });
          }
        }
      }
    }

    //Unique and sort
    for (let parameterId of parameterIdList) {
      if (parameterMap[parameterId].options && parameterMap[parameterId].options.length) {
        parameterMap[parameterId].options.sort((a, b) => {
          if (a.sort < b.sort) return -1;
          if (a.sort > b.sort) return +1;
          return 0;
        });
      }
    }
  }
}

async function loadArticles() {
  let filter = { };
  if (!isRootCategory.value) {
    filter.categoryId = selectedCategoryId.value;
  }

  /*
  if (searchText.value && searchText.value.trim().length) {
    //TODO: Search also categories
    filter.q = searchText.value;
  }
  */

  //Note: replaced by loadCabinetStock()
  //await preloadArticlesOnce();

  let list = [ ...allPossibleArticles.value ];

  //Only articles within the category or its subcategories
  if (filter.categoryId) {

    //Find subcategories
    let categories = [ filter.categoryId ];
    function findSubcategories(parentId) {
      let subcategories = allPossibleCategories.value.filter(x => x.parentId == parentId);
      if (subcategories && subcategories.length) {
        for (let subcategory of subcategories) {
          categories.push(subcategory.id);
          findSubcategories(subcategory.id);
        }
      }
    }
    findSubcategories(filter.categoryId);

    //List only those items that fit category and its subcategories
    list = list.filter(x => {
      if (!x.categories) {
        return false;
      }
      for (let cat of x.categories) {
        if (categories.includes(cat)) {
          return true;
        }
      }
      return false;
    });
  }

  //Filter by parameters
  if (parameters.value) {
    let isAnyParameterSelected = false;
    for (let item of list) {
      item.parameterSelected = false;
    }

    let selectedParameters = { };
    for (let parameter of parameters.value) {

      for (let option of parameter.options) {
        if (option.selected) {
          isAnyParameterSelected = true;
          selectedParameters[parameter.id] = true;

          let accepted = list.filter(x => x.parameters && x.parameters[parameter.id] == option.name);
          for (let item of accepted) {
            if (!item.parameterSelected) {
              item.parameterSelected = [ ];
            }
            item.parameterSelected.push(parameter.id);
            item.parameterSelected.sort();
          }

        }
      }

    }
    if (isAnyParameterSelected) {
      let selectedParametersKeys = Object.keys(selectedParameters);
      selectedParametersKeys.sort();
      let parameterMatch = selectedParametersKeys.join(" ");
      list = list.filter(x => x.parameterSelected && x.parameterSelected.length && x.parameterSelected.join(" ") == parameterMatch);
    }
  }

  /*
  //Filter by name
  if (filter.q) {
    function escapeRegExp(s) {
      return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string
    }

    list = list.filter(x => new RegExp(escapeRegExp(filter.q), "i").test(x.name));
  }
  */
 
  rows.value = list;
  rowsLoaded.value = rows.value;

  //Sort
  sortArticles();
}

function applySort(list, sort) {
  switch (sort) {
    default:
    case "default":
      //list = list; //Natural order
      break;

    case "alphanum-asc":
      {
        let a, b, a1, b1, rx = /(\d+)|(\D+)/g, rd = /\d+/;
        list.sort(function(as, bs){
          a = String(as.name).toLowerCase().match(rx);
          b = String(bs.name).toLowerCase().match(rx);
          while (a.length && b.length) {
            a1 = a.shift();
            b1 = b.shift();
            if (rd.test(a1) || rd.test(b1)) {
                if (!rd.test(a1)) return 1;
                if (!rd.test(b1)) return -1;
                if (a1!= b1) return a1-b1;
            }
            else if (a1 != b1) {
              return a1 > b1 ? 1 : -1;
            }
          }
          return a.length - b.length;
        });
      }
      break;

    case "alphanum-desc":
      {
        let a, b, a1, b1, rx = /(\d+)|(\D+)/g, rd = /\d+/;
        list.sort(function(as, bs){
          a = String(as.name).toLowerCase().match(rx);
          b = String(bs.name).toLowerCase().match(rx);
          while (a.length && b.length) {
            a1 = a.shift();
            b1 = b.shift();
            if (rd.test(a1) || rd.test(b1)) {
                if (!rd.test(a1)) return -1;
                if (!rd.test(b1)) return 1;
                if (a1!= b1) return a1-b1;
            }
            else if (a1 != b1) {
              return a1 > b1 ? -1 : 1;
            }
          }
          return a.length - b.length;
        });
      }
      break;

    case "alpha-asc":
      list.sort((a, b) => {
        if (a.name > b.name) {
          return +1;
        }
        if (a.name < b.name) {
          return -1;
        }
        return 0;
      })
      break;

    case "alpha-desc":
      list.sort((a, b) => {
        if (a.name > b.name) {
          return -1;
        }
        if (a.name < b.name) {
          return +1;
        }
        return 0;
      })
      break;

    case "quantity-asc":
      list.sort((a, b) => {
        if (a.totalQuantity < b.totalQuantity) {
          return -1;
        }
        if (a.totalQuantity > b.totalQuantity) {
          return +1;
        }
        return 0;
      })
      break;

    case "quantity-desc":
      list.sort((a, b) => {
        if (a.totalQuantity < b.totalQuantity) {
          return +1;
        }
        if (a.totalQuantity > b.totalQuantity) {
          return -1;
        }
        return 0;
      })
      break;

    case "used-asc":
    case "created-asc":
      list.sort((a, b) => {
        if (a.created < b.created) {
          return +1;
        }
        if (a.created > b.created) {
          return -1;
        }
        return 0;
      })
      break;

    case "used-desc":
    case "created-desc":
      list.sort((a, b) => {
        if (a.created < b.created) {
          return +1;
        }
        if (a.created > b.created) {
          return -1;
        }
        return 0;
      })
      break;
  }
}

function sortArticles() {
  let list = rows.value;
  if (!list || list.length <= 1) {
    return;
  }

  applySort(list, selectedSortOption.value);

  rows.value = list;
  rowsLoaded.value = rows.value;
}

//Search by text - optimized, to avoid refiltering by category and parameters
const rowsFiltered = computed(() => {
  let list = rows.value; //Already prefiltered by category and parameters, already applied sorting

  if (isShowAllArticles.value) {
    list = [ ...allPossibleArticles.value ];
    applySort(list, selectedSortOption.value);
  }

  let filter = { };
  if (searchText.value && searchText.value.trim().length) {
    //TODO: Search also categories
    filter.q = searchText.value;
  }

  //Filter by name
  if (filter.q) {
    function escapeRegExp(s) {
      return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string
    }

    list = list.filter(x => new RegExp(escapeRegExp(filter.q), "i").test(x.name));
  }

  return list;
});

const searchResults = computed(() => {
  let list = [ ...allPossibleCategories.value, ...allPossibleArticles.value ];

  let text = null;
  if (searchText.value && searchText.value.trim().length) {
    text = searchText.value.trim().toLowerCase();
    list = list.filter(x => x.keywords.includes(text));
  }

  return list;
});

async function applyStock(item, opt) {
  let tray = selectedTrayItem.value;
  let userId = currentUserId.value;
  let cabinetId = tray.cabinetId;

  let now = utc();

  try {
    if (!cabinetId) {
      throw new Error(t('catalog.cabinet_id_not_defined'));
    }

    let setItem = null;
    
    if (isCabinetBorrowMode.value) {
      //Borrow
      setItem = {
        type: "article",
        itemId: tray.itemId,
        articleId: item.articleId,
        quantity: null, //Always reset this value so it doesnt get included in the total quantity sum
        unit: "pcs",
        itemWeight: item.itemWeight,
        name: item.tray, 
        usage: selectedTrayUsage.value,
        userId: userId,
        userTime: now,
        comment: stockComment.value
      }
    }
    else {
      //Quantity
      setItem = {
        type: "article",
        itemId: tray.itemId,
        articleId: item.articleId,
        quantity: virtualStockQuantity.value,
        unit: "pcs",
        itemWeight: item.itemWeight,
        userId: userId,
        userTime: now,
        comment: stockComment.value
      }
    }

    let changes = {
      trays: [
        {
          trayId: tray.trayId,
          name: tray.name, //Tray name, e.g. A1
          item: setItem, //Single item to update
          userId: userId, //Audit
        }
      ]
    };

    //Stock transfer
    if (isStockTransferRequest.value) {
      changes.trays[0].action = "startTransfer";
      //changes.trays[0].reason = null;
    }
    else if (isStockTransferApplied.value) {
      console.log("applying stock transfer", pendingTransferRecord.value)
      let stockMovementId = pendingTransferRecord.value.articleItem.id;
      let appliedQuantity = Math.abs(pendingTransferQuantity.value || 0);

      if (!stockMovementId) {
        throw new Error("Manjka podatek za prenos zaloge!");
      }

      changes.trays[0].action = "applyTransfer";
      changes.trays[0].transfer = {
        stockMovementId: stockMovementId,
        quantity: appliedQuantity,
        before: initialStockQuantity.value, //virtualStockQuantity.value - appliedQuantity, //TODO: May not be accurate in case of weighting new value + apply stock transfer, use other variable to apply before
        after: virtualStockQuantity.value,
      };
    }

    //TODO: What about subdivision - is it included in trayId? tray.subdivisionId

    const reportCommentChange = true; //TODO: Read from preferences or apply server-side settings

    let result = await cabinetService.updateCabinetStock(cabinetId, changes, { reportEmptyStock, reportCommentChange });

    //Update current state on UI
    let trayName = selectedTrayName.value;
    let trayItem = selected.value?.trays?.find(x => x.name == trayName);
    if (trayItem) {
      console.log("found local tray to update comment", userId, stockComment.value)
      //TODO: The following states are based on article in this tray; not the whole tray per se
      trayItem.comment = stockComment.value;
      trayItem.userId = userId;
      trayItem.userTime = now;
    }
  }
  catch (err) {
    console.error("applyStock", err);
    toast(err); //dialog(err);
    return;
  }

  //Commented: Moved logic above to updateCabinetStock
  /*
  //Apply stock transfer
  if (isStockTransferApplied.value) {
    try {
      console.log("applying stock transfer", pendingTransferRecord.value)
      let stockMovementId = pendingTransferRecord.value.articleItem.id;
      let appliedQuantity = Math.abs(pendingTransferQuantity.value || 0);

      if (!stockMovementId) {
        throw new Error("Manjka podatek za prenos zaloge!");
      }
      
      console.log("stock transfer", stockMovementId, appliedQuantity)
      
      //TODO: What about subdivision - is it included in trayId? tray.subdivisionId
      let result = await userService.updateUserInventory(userId, {
        action: "applyTransfer",
        id: stockMovementId,
        cabinetId: cabinetId,
        cabinetTrayId: tray.trayId,
        subdivisionId: null, //TODO: Subdivision id
        quantity: appliedQuantity,
        before: virtualStockQuantity.value - appliedQuantity, //TODO: May not be accurate in case of weighting new value + apply stock transfer, use other variable to apply before
        after: virtualStockQuantity.value,
      });

      console.log("stock transfer applied", result);
    }
    catch (err) {
      console.error("[stock transfer]", err)
      toast(err);
    }
  }
  */

  try {
    let message = null;

    let action = null;
    let actionType = null;

    let previousQuantity = null;
    let newQuantity = null;

    let usageStatus = null;

    let userId = currentUserId.value;
    if (isCabinetBorrowMode.value) {
      let translated = null;
      actionType = "vault";
      /*
      switch (selectedUsage.value) {
        case "new": translated = "novo"; break;
        case "used": translated = "uporabljeno"; break;
        case "empty": translated = "prazen predal"; break;
        case "borrowed": translated = "izposojeno"; break;
      }
      */
      usageStatus = selectedTrayUsage.value;
      switch (usageStatus) {
        case "none": translated = "prazno"; action = "empty"; break;
        case "present": translated = "vrnjeno"; action = "inserted"; break;
        case "taken": translated = "izposojeno"; action = "removed"; break;
      }
      
      //message = "Sprememba stanja na '" + translated +  "' za " + item.name;
      message = 'Sprememba stanja na "{usageStatus}" za "{articleName}"'
        .replace("{usageStatus}", translated || usageStatus || "???")
        .replace("{articleName}", item.name || "???");
    }
    else {
      actionType = "storage";

      //TODO: What about subdivision? tray.subdivisionId
      previousQuantity = tray.quantity || item.totalQuantity || 0;
      newQuantity = virtualStockQuantity.value || 0;
      message = 'Sprememba količine {previousValue} -> {newValue} za "{articleName}"'
        .replace("{previousValue}", previousQuantity) //'"brez vrednosti"'
        .replace("{newValue}", newQuantity) //'"brez vrednosti"'
        .replace("{articleName}", item.name || "???");

      if (previousQuantity > newQuantity) {
        action = "removed";
      }
      else if (previousQuantity < newQuantity) {
        action = "inserted";
      }
    }

    //TODO: action = transfer

    let extra = {
      hint: action, //Backward compatible: empty, inserted, removed
      action: action,
      actionType: actionType,
      articleId: item.articleId,
      subdivisionId: tray?.subdivisionId,
    };

    switch (actionType) {
      case "storage":
        extra.beforeQuantity = previousQuantity;
        extra.newQuantity = newQuantity;
        break;

      case "vault":
        extra.usageStatus = usageStatus;
        break;
    }

    if (appState.isLogEnabled) {
      await logService.insertLog({
        a: "cabinet.applyStock",
        t: "cabinet",
        s: "info",
        uid: userId,
        cid: cabinetId,
        tid: tray?.trayId,
        did: item.subdivision?.id || tray?.subdivisionId,
        msg: message,
        data: extra,
        ts: new Date()
      });
    }
  }
  catch (err) {
    console.error(err);
  }

  console.log("STOCK CHANGE Go out of edit mode");

  //Go out of edit mode
  if (opt?.keepEditMode !== true) {
    isEditStockQuantity.value = false; //Note: do NOT change the position of this isEditStockQuantity line otherwise watch can detect changes and resave wrong status
  }
  canSaveStockQuantity.value = false;
  
  //Update local variables
  let trayItem = selected.value?.trays?.find(x => x.itemId == tray.itemId); //|| selected.value?.trays?.find(x => x.name == tray.name); //Backward compatible
  if (trayItem) {
    trayItem.quantity = virtualStockQuantity.value;
    tray.quantity = virtualStockQuantity.value;

     //Live update the icon on tray button
    if (isCabinetBorrowMode.value) {
      trayItem.usage = selectedTrayUsage.value;
      trayItem.quantity = null; //Reset quantity, not need in vault mode, but should also not affect the total quantity
      //trayItem.itemWeight = x.itemWeight,
      trayItem.userId = userId;
      trayItem.userTime = now; //TODO: Get applied user time
      trayItem.comment = stockComment.value;
    }
  }

  //Live update the icon on tray button
  // if (isCabinetBorrowMode.value && tray) {
  //   tray.usage = selectedTrayUsage.value;
  //   tray.quantity = 0; //Reset quantity, not need in vault mode, but should also not affect the total quantity
  //   tray.userId = userId;
  //   tray.userTime = utc(); //TODO: Get applied user time
  //   tray.comment = stockComment.value;
  // }

  //Update total stock quantity
  selected.value.totalQuantity = stockSum(selected.value.trays);

  //Close tray: store/return to tray
  try {
    //Find tray record by name
    if (!trayItem.cabinetId) {
      throw new Error(t('catalog.tray_no_cabinet'));
    }

    let controller = cabinetControllers[trayItem.cabinetId];
    if (!controller) {
      throw new Error(t('catalog.controller_not_defined'));
    }

    //TODO: cabinet features, if needsClose by controller command (not manual closing by user)
    if (controller.model == "plt-m100" || controller.model == "plt-m280" || controller.model == "plt-m280t") {
      await closeTray(trayItem);
    }
  }
  catch (err) {
    console.error("M280 store closeTray error", err);
    return;
  }

  //Door status may change with some delay. Watch isOpen instead and close the modal window.
  if (isCabinetBorrowMode.value) {
    canAutoCloseModal.value = true;
  }

  //Expected to log out on tray closed
  autoLogoutConfirmed.value = (settings.value?.session?.trayAutoLogout == true);

  //Close modal window if cabinet door is already closed
  if (isModalVisible.value && !isOpen.value) {

    //Auto logout if tray is closed at this moment - otherwise auto logout on tray closed event
    if (settings.value?.session?.trayAutoLogout == true && autoLogoutConfirmed.value) {
      hideWarningBanner();
      canInterrupt(true);
      useSessionTimer(true);
      logout();
      return;
    }

    //Automatically close modal window when door is closed
    closeModal();
  }
}

async function cancelStock(item) {
  let trayName = selectedTrayName.value || item.tray; //TODO: item.tray is deprecated, use selectedTrayName.value

  let trayItem = selected.value?.trays?.find(x => x.name == trayName);

  isEditStockQuantity.value = false;

  //Close tray: store/return to tray
  try {
    //Find tray record by name
    if (!trayItem.cabinetId) {
      throw new Error(t('catalog.tray_no_cabinet'));
    }

    let controller = cabinetControllers[trayItem.cabinetId];
    if (!controller) {
      throw new Error(t('catalog.controller_not_defined'));
    }

    //TODO: cabinet features needsClose
    if (controller.model == "plt-m100" || controller.model == "plt-m280" || controller.model == "plt-m280t") {
      await closeTray(trayItem);
    }
  }
  catch (err) {
    console.error("M280 store closeTray error", err);
    return;
  }

  //Close modal window if cabinet door is already closed
  if (isModalVisible.value) {
    closeModal(); //Automatically close modal window when door is closed
  }
}

async function updateArticleUnitWeight(articleId, itemWeight) {
  try {
    //Save to database
    let result = await articleService.updateArticle(articleId, { itemWeight });

    //Update unit weight among all articles
    let article = allPossibleArticles.value?.find(x => x.articleId == articleId);
    if (article) {
      article.itemWeight = itemWeight;
    }

    //Update unit weight among displayed articles
    article = rows.value?.find(x => x.articleId == articleId);
    if (article) {
      article.itemWeight = itemWeight;
    }
  }
  catch (err) {
    toast(err); //dialog(err);
  }
}

async function updateEmptyInsertWeight(cabinetId, insertWeight) {
  try {
    let result = await cabinetService.updateCabinet(cabinetId, { "config.insertWeight": insertWeight });
  }
  catch (err) {
    toast(err); //dialog(err);
  }
}

/******************************************************************************************
 * Algorithm to select best tray, e.g. offer a tray that has most pieces available
 ******************************************************************************************/
function findBestTray(trays) {
  //Select first by default
  let best = trays[0];
  if (bestTrayPreference == "always-first") {
    return best;
  }

  //First algorithm mode
  //TODO: Select by nearest rotation

  //Second algorithm mode
  for (let tray of trays) {
    if (tray.quantity > 0) {
      //Better if tray has at least one item than empty
      if (!best.quantity) {
        best = tray;
        continue;
      }

      //Least quantity goes first to empty the tray as soon as possible
      if (bestTrayPreference == "lowest-quantity-first") {
        if (tray.quantity < best.quantity) {
          best = tray;
          continue;
        }
      }
      //Most abundant quantity goes first
      else if (bestTrayPreference == "highest-quantity-first") {
        if (tray.quantity < best.quantity) {
          best = tray;
          continue;
        }
      }

    }

    //Article available to borrow
    if (tray.usage == "present") {
      best = tray;
    }

    //Return article back if taken by the same user
    if (tray.usage == "taken" && tray.userId && tray.userId == selectedUserId.value) {
      best = tray;
      break; //Stop searching here, this article has the most priority
    }
  }

  return best;
}

const canSaveStockQuantity = ref();

/******************************************************************************************
 * Show modal window to open article
 ******************************************************************************************/
function showModal(row) {
  selected.value = row;

  canSaveStockQuantity.value = true;
  canAutoCloseModal.value = false;
  autoLogoutConfirmed.value = false;
  isStockTransferRequest.value = false;

  //showWarningBanner(isCabinetPrivateMode.value ? "privateWindow" : "articleWindow", { type: "weightMeasure" });

  //Auto-select best tray
  let bestTray = null;
  if (row && row.trays && row.trays.length == 1) {
    bestTray = row.trays[0]; //In case if only a single tray available
  }
  else if (row && row.trays && row.trays.length > 1) {
    bestTray = findBestTray(row.trays); //Find best by algorithm (e.g. to empty stock asap, to use the least used tray, etc.)
  }

  // console.log("showModal row", row) //reoderPoint.minQuantity
  // console.log("showModal bestTray", bestTray) //reoderPoint.minQuantity

  selectedTrayItem.value = bestTray;
  selectedTrayName.value = bestTray?.name;
  availableTraySelect.value = bestTray ? getTrayOptionId(bestTray) : null;

  selectedUsage.value = bestTray ? bestTray.usage : null;
  stockComment.value = bestTray ? bestTray.comment : null;

  //isCabinetBorrowMode.value = getCabinetMode(bestTray?.cabinetId) == "vault";
  isCabinetBorrowModeDefault.value = getCabinetMode(bestTray?.cabinetId) == "vault";

  isLargeImageView.value = false;
  isRelatedArticlesView.value = false;

  //Apply current stock value
  isStockMismatch.value = false;
  initialStockQuantity.value = null;
  virtualStockQuantity.value = null; //Cannot tell the quantity until the tray is selected
  if (bestTray?.countMode != "vault" && !isCabinetBorrowModeDefault.value) {
    virtualStockQuantity.value = bestTray?.quantity || null;
    initialStockQuantity.value = virtualStockQuantity.value;
  }

  //Apply calibrated values
  //Reset scale values
  scaleItemsUserCount.value = 1;
  //scaleItemWeightCalibrated.value = 0;
  //scaleInsertWeight.value = cabinet.config.insertWeight;
  //console.log("Article weight", row.itemWeight);
  scaleItemWeightCalibrated.value = row.itemWeight > 0 ? row.itemWeight : 0;
  scaleItemWeight.value = scaleItemWeightCalibrated.value;
  isItemWeightCalibrated.value = scaleItemWeightCalibrated.value > 0;

  //Stock transfer
  pendingTransferRecord.value = (userItems.value || []).find(x => x.type == "transfer" && x.articleItem?.articleId == row.articleId);
  pendingTransferQuantity.value = Math.abs(pendingTransferRecord.value?.articleItem?.quantity || 0);
  isStockTransferApplied.value = false;
  isStockTransferRequest.value = false;
  
  isEditStockQuantity.value = false;
  //isOpening.value = false;
  isOpen.value = false;

  //Put scale in normal reading mode
  setWeightScaleCalibrationMode(false);

  //Hide other modal windows
  hideLocation();

  //Show modal window
  isModalVisible.value = true;

  canInterrupt(false);

  useSessionTimer(false);

  $virtualKeyboard.hide();

  //setTimeout(() => {
  // showWarningBanner();
  //}, 2000)
}

const canAutoCloseModal = ref();

function closeModal() {
  hideWarningBanner();
  canInterrupt(true);
  useSessionTimer(true);
  isModalVisible.value = false;
  canAutoCloseModal.value = false;

  if (isUserItemsVisible.value) {
    refreshUserItems();
  }
}

function closeModalConditional(insideModalWindowElement) {
  //Check if clicked inside the modal window
  if (insideModalWindowElement) {
    return; //Avoid handling
  }

  //Avoid closing modal window when user action needs to be perfomed
  if (isOpen.value || isOpening.value || isClosing.value) {
    return false;
  }

  //Close modal window
  closeModal();
}

async function showUserItems(options) {
  await refreshUserItems();

  //Conditional - must have at least one item to display
  if (options?.mustHaveItems) {
    if (userItems.value?.length > 0) {
      isUserItemsVisible.value = true;
      isAutoUserItems.value = options?.isAutoLoad;
    }
  }
  //Unconditional - display always
  else {
    isUserItemsVisible.value = true;
  }
}


const barcodeMatches = ref([]);
const isBarcodeModalVisible = ref(false);

function showBarcode(list) {
  $virtualKeyboard.hide();
  barcodeMatches.value = list;
  isBarcodeModalVisible.value = true;
}

function hideBarcode() {
  barcodeMatches.value = [];
  isBarcodeModalVisible.value = false;
}


const isPrivateView = ref(false);
const isPrivateModalVisible = ref(false);

function showPrivateTrays() {
  //selectCategory("_private");

  $virtualKeyboard.hide();

  selectedCategoryId.value = "_private"; //Actual category id or "_root"
  //parentCategoryId.value = null;
  currentCategory.value = null;

  let breadcrumbs = [ ];

  //Add breadcrumb
  breadcrumbs.push({ id: "_root", name: "Kategorije", level: 1 });
  breadcrumbs.push({ id: "_private", name: "Osebni predali", level: 2 });

  //Previous category
  parentCategoryId.value = "_root";

  //Refresh breadcrumbs
  categoryBreadcrumbs.value = breadcrumbs;

  isPrivateView.value = true;

  categories.value = [ ];
  rows.value = [ ];
}

function showPrivateModal(privateTray) {
  //Cannot access locked tray
  if (privateTray.status == "locked") {
    return;
  }

  //Cannot access other user's tray (unless admin)
  if (privateTray.status == "occupied" && privateTray.userId != currentUserId.value && !isAdmin.value) {
    return;
  }

  selectedTrayItem.value = privateTray;
  isPrivateModalVisible.value = true;

  //Check if any article selected
  selected.value = {
    dummy: "article",
    trays: [
      {
        cabinetId: privateTray.cabinetId,
        trayId: privateTray.trayId,
        name: privateTray.name
      }
    ]
  };

  //Tray must be selected
  selectedTrayName.value = privateTray.name; 
}

function closePrivateModal() {
  hideWarningBanner();
  canInterrupt(true);
  useSessionTimer(true);
  isPrivateModalVisible.value = false;

  if (isUserItemsVisible.value) {
    refreshUserItems();
  }
}

function closePrivateModalConditional(insideModalWindowElement) {
  //When in service mode allow always to close
  if (!isServiceMode.value) {
    //Check if clicked inside the modal window
    if (insideModalWindowElement) {
      return; //Avoid handling
    }

    //Avoid closing modal window when user action needs to be perfomed
    if (isOpen.value || isOpening.value || isClosing.value) {
      return false;
    }
  }

  //Close modal window
  closePrivateModal();
}

function showUserItemModal(item) {
  switch (item?.type) {
    case "article":
    case "transfer":
      let articlePreloaded = allPossibleArticles.value.find(x => x.id == item.articleItem.articleId) || {};
      let articleUserItem = item.articleItem || {};
      showModal({ ...articlePreloaded, ...articleUserItem });
      break;

    case "private":
      showPrivateModal(item.privateTray);
      break;
  }
}

const userPrivateTray = ref();
const userPrivateTrayName = computed(() => userPrivateTray.value?.name);
const availablePrivateTraysCount = computed(() => privateTrays.value?.filter(x => x.status == "active").length);
const isReturningPrivateTray = ref();
const isOpenPrivateTray = ref();

const canOpenPrivateTray = computed(() => {
  if (isReferencing.value || isOpen.value || isOpening.value || isClosing.value) {
    return false;
  }
  if (userPrivateTray.value) {
    return true;
  }
  if (availablePrivateTraysCount.value > 0) {
    return true;
  }
  return false;
});



function openPrivateTray() {
  isOpenPrivateTray.value = true;

  if (userPrivateTray.value) {
    isReturningPrivateTray.value = true;
  }
  else {
    isReturningPrivateTray.value = false;
    userPrivateTray.value = privateTrays.value?.find(x => x.status == "active");
  }

  if (!userPrivateTray.value) {
    return toast(new Error(t('catalog.no_tray_available')));
  }

  let privateTray = cabinetTrays.value?.find(x => x.trayId == userPrivateTray.value.trayId);
  if (!privateTray) {
    return toast(new Error(t('catalog.tray_not_found')));
  }


  selectedTrayItem.value = privateTray;

  //Check if any article selected
  selected.value = {
    dummy: "article",
    trays: [
      {
        cabinetId: privateTray.cabinetId,
        trayId: privateTray.trayId,
        name: privateTray.name
      }
    ]
  };

  //Tray must be selected
  selectedTrayName.value = privateTray.name; 


  openTray(privateTray);
}

const warningBannerType = ref("trayOpen");
const warningBannerMessage = ref();
const warningBannerSeverity = ref("info");

function showWarningBanner(window, options) {
  warningBannerModal.value = window || "articleWindow";
  warningBannerType.value = options?.type || "trayOpen"; //trayOpen, weightMeasure, custom
  if (warningBannerType.value == "trayOpen") {
    warningBannerSeverity.value = "info";
  }
  if (warningBannerType.value == "weightMeasure") {
    warningBannerSeverity.value = "warning";
  }
  if (warningBannerType.value == "custom" && options?.message) {
    warningBannerMessage.value = options?.message;
    warningBannerSeverity.value = options?.severity;
  }
  isWarningBannerVisible.value = true;
}

function hideWarningBanner() {
  warningBannerModal.value = null;
  isWarningBannerVisible.value = false;
}

function getAnyOpening() {
  return checkIfAny("isOpening");
}

function getAnyOpen() {
  return checkIfAny("isOpen");
}

function getAnyReferencing() {
  return checkIfAny("isReferencing");
}

function getAnyReferenced() {
  return checkIfAny("isReferenced");
}

function checkIfAny(propertyName) {
  if (cabinetControllers) {
    for (let key in cabinetControllers) {
      if (cabinetControllers[key][propertyName] === true) {
        return true;
      }
    }
  }
  return false;
}

const isAnyOpening = computed(getAnyOpening);

const isAnyOpen = computed(getAnyOpen);
const isAnyDoorOpen = ref();

const lastRequest = ref();
const lastOpenTrayArgs = ref();
const lastOpenTrayController = ref();
const lastOpenTrayAction = ref();

const isHaltAlarm = ref(null);
const isCurtainAlarm = ref(null);
const isDisruptAlarm = ref(null);
const isAnyAlarm = computed(() => {
  return isHaltAlarm.value || isCurtainAlarm.value || isDisruptAlarm.value;
});

function clearAlarm() {
  isHaltAlarm.value = false;
  isCurtainAlarm.value = false;
  isDisruptAlarm.value = false;
  useCabinetAlarm(false);
}

function continueAfterAlarm() {
  console.log("continueAfterAlarm", isHaltAlarm.value, lastOpenTrayArgs.value)

  //Only if emergency stop button pressed
  if (isHaltAlarm.value) {
    console.log("continueAfterAlarm", "resent last command", "canOpenTray", canOpenTray.value, "=>", true);
    let controller = lastOpenTrayController.value;
    if (controller) {
      //isOpen.value = false;
      //isClosing.value = false;
      isPrevOpen.value = false;
      //isOpening.value = true;
      canOpenTray.value = true;
      isEditStockQuantity.value = false; //TODO: Show close window
      //isEditStockQuantity.value = true; //TODO: temporary so user can close the window
      if (lastOpenTrayArgs.value && lastOpenTrayAction.value == "open") {
        //Resend last command
        let args = { ...lastOpenTrayArgs.value };
        args.skipParameters = true;
        console.log("continueAfterAlarm", "openTray", args);
        controller.openTray(args);
      }
      else if (lastRequest.value && lastRequest.value.tray) { //if (lastOpenTrayAction.value == "close")
        //Resend last command
        let args = lastRequest.value.tray;
        console.log("continueAfterAlarm", "closeTray", args);
        controller.closeTray(args);
      }
      else {
        console.log("continueAfterAlarm", "error: unknown", lastOpenTrayArgs.value, lastRequest.value);
      }
    }
    else {
      console.log("continueAfterAlarm", "error: no controller");
    }
  }

  console.log("continueAfterAlarm", "clear alarm");
  clearAlarm();
}

const hasDeliveryFeature = ref(false);
const hasIssuedOpenTrayAtLeastOnce = ref(false); //This will prevent auto logout if tray has been open before user opened catalog; auto logout should only be if user opens the tray via catalog (not service window)

/******************************************************************************************
 * Start opening the selected tray
 ******************************************************************************************/
async function openTray(tray) {
  //Check if allowed to progress
  if (!canOpenTray.value) {
    if (isHaltAlarm.value) {
      console.log("continueAfterAlarm", "canOpenTray", canOpenTray.value)
    }
    return;
  }

  hasIssuedOpenTrayAtLeastOnce.value = true;

  try {
    //Move to showModal
    // //Reset scale values
    // scaleItemsUserCount.value = 0;
    // scaleItemWeightCalibrated.value = 0;

    console.log("[debug] openTray", tray);

    //Get tray name
    let trayName = null;
    if (typeof tray == "string") {
      trayName = tray;
    }
    if (typeof tray == "object") {
      trayName = tray.name;
    }
    
    //Quick check for matching tray name, e.g. "B01"
    let m = /^([A-Z])?(\d+)$/.exec(trayName);
    if (!m) {
      throw new Error(t('catalog.tray_name_invalid', { name: trayName }));
    }

    //Find tray record by name
    let itemTray = typeof tray == "object" ? tray : selected.value?.trays?.find(x => x.name == trayName);
    if (!itemTray) {
      console.error("Zapis o predalu manjka", trayName, tray, selected.value);
      throw new Error(t('catalog.tray_record_missing'));
    }

    if (!itemTray.cabinetId) {
      throw new Error(t('catalog.tray_no_cabinet'));
    }

    let controller = cabinetControllers[itemTray.cabinetId];
    if (!controller) {
      throw new Error(t('catalog.controller_not_defined'));
    }

    isCabinetBorrowModeDefault.value = getCabinetMode(itemTray.cabinetId) == "vault";

    hasDeliveryFeature.value = (controller.model == "plt-m100" || controller.model == "plt-m280" || controller.model == "plt-m280t");
    //hasCloseTrayFeature.value = (controller.model == "plt-m100" || controller.model == "plt-m280" || controller.model == "plt-m280t");

    //Check if any door open
    try {
      let any = false;

      let { controllers } = await hcu.cabinet().getInstances2();
      for (let controller of controllers) {
        if (controller.model == "plt-b414" && controller.anyDoorOpen) {
          any = true;
          break;
        }
        if ((controller.model == "plt-l18" || controller.model == "plt-l64") && controller.anyDoorOpen) {
          any = true;
          break;
        }
      }

      isAnyDoorOpen.value = any;
      if (any) {
        toast(new Error(t('catalog.close_doors_first')));
        canOpenTray.value = true;
        return;
      }
    }
    catch (err) {
      console.error("hcu.cabinet.getStatus", err);
      if (err.message.startsWith("JSON.parse")) {
        throw new Error(t('catalog.hcu_not_responding'));
      }
      else {
        throw err;
      }
    }

    //Check if reference position is set
    /*
    try {
      let status2 = await hcu.cabinet(controller.mac).getStatus2();
      if (status2.model == "plt-b414" && status2.ref !== true) {
        isNotRef.value = true;
        throw new Error("Krmilnik ni na referenčni poziciji!");
      }
    }
    catch (err) {
      console.error("hcu.cabinet.getStatus", err);
      if (err.message.startsWith("JSON.parse")) {
        throw new Error(t('catalog.hcu_not_responding'));
      }
      else {
        throw err;
      }
    }
    */

    let now = new Date();
    let time = now.toISOString().split("T")[1].substr(0, 8);

    let payload = null;
    switch (controller.model) {
      case "plt-b414":
        let drumPosition = parseInt(m[2]);
        let doorNumber = alphabet.value.indexOf(m[1]) + 1;
        payload = { drumPosition, doorNumber };
        break;

      case "plt-l18":
      case "plt-l64":
        payload = { doorNumber: parseInt(trayName) };
        
        let maxDoorNumber = controller.cabinet?.maxDoorNumber;
        if (maxDoorNumber > 0) {
          maxDoorNumber = parseInt(maxDoorNumber);
        }
        else if (controller.model == "plt-l64") {
          maxDoorNumber = 64;
        }
        else {
          maxDoorNumber = 18;
        }

        //TODO: Temporary work-around for trays 19-36
        //Shift to next cabinet
        if (payload.doorNumber > maxDoorNumber) {
          payload.doorNumber -= maxDoorNumber;
        }
        break;

      case "plt-m100":
      case "plt-m280":
      case "plt-m280t":
        //selectedTrayUsage
        let trayNumber = parseInt(trayName);
        let curtainStart = 1;
        let insertLength = 10;
        if (tray.subdivision) {
          if (!tray.subdivision.args) {
            console.error("subdivision.args not defined", tray.subdivision);
            throw new Error(t('catalog.insert_not_set'));
          }

          let parts = tray.subdivision.args.split(":");
          curtainStart = parseInt(parts[0]);
          insertLength = parseInt(parts[1]);

          if (isNaN(curtainStart)) {
            console.error("curtainStart", tray.subdivision.args, curtainStart, curtainLength);
            throw new Error(t('catalog.insert_not_set'));
          }

          if (isNaN(insertLength)) {
            console.error("curtainStart", tray.subdivision.args, curtainStart, curtainLength);
            throw new Error(t('catalog.insert_not_set'));
          }

          /*
          //Curtain starting position
          curtainStart = tray.subdivision.start;
          if (!curtainStart && tray.subdivisions) {
            curtainStart = 1;
            for (let sub of tray.subdivisions) {
              if (sub.id == tray.subdivision.id) {
                break;
              }
              curtainStart += sub.length;
            }
          }

          //Length of the insert
          insertLength = tray.subdivision.length;
          
          if (controller.model == "plt-m100") {
            if (insertLength == 3) {
              let swp = curtainStart;
              curtainStart = 3; //One third size
              insertLength = swp; //1, 2, 3
            }
            else if (insertLength == 2) {
              let swp = curtainStart;
              curtainStart = 2; //One half size
              insertLength = swp; //1, 2
            }
            else if (insertLength == 1) {
              curtainStart = 1; //One full size
              insertLength = 1; //Can only be at position 1
            }
            else {
              throw new Error("Neveljavna dolžina vložka!");
            }
          }
          */
        }
        payload = { trayNumber, curtainStart, curtainLength: insertLength }; //TODO: rename curtainLength to insertLength
        break;

      default:
        throw new Error(t('catalog.controller_model_unknown', { model: controller.model }));
    }

    //Stock quantity before the change
    virtualStockQuantity.value = itemTray.quantity;
    initialStockQuantity.value = virtualStockQuantity.value;
    isStockMismatch.value = false;

    //Do not allow any external event interruptions during tray opening
    canInterrupt(false);

    //Avoid session timeout while a tray is open
    useSessionTimer(false);

    try {
      let trayFullName = itemTray.name;
      if (itemTray.subdivision?.name) {
        trayFullName += "-" + itemTray.subdivision?.name;
      }
      if (appState.isLogEnabled) {
        await logService.insertLog({
          a: "cabinet.openTray",
          t: "cabinet",
          s: "debug",
          uid: currentUserId.value,
          cid: itemTray.cabinetId,
          tid: itemTray.trayId || itemTray.id,
          tn: trayFullName || trayName, //Tray name including subdivision
          did: itemTray.subdivision?.id || itemTray.subdivisionId,
          msg: "Odpiranje predala {trayName}".replace("{trayName}", trayFullName), // + (itemTray.cabinetName ? " na omari " + itemTray.cabinetName : ""),
          ts: new Date()
        });
      }
    }
    catch (err) {
      console.error("[catalog] Log error", err);
    }

    //May not be actually true, but is good to display loading animation now for better user experience
    isOpening.value = true;

    lastRequest.value = {
      controller,
      cabinetId: itemTray.cabinetId,
      payload,
      tray
    };

    try {
      let status2 = await hcu.cabinet(controller.mac).getStatus2();
      if (status2.model == "plt-b414" && status2.ref !== true) {
        //throw new Error("Krmilnik ni na referenčni poziciji!");
        if (lastRequest.value) {
          lastRequest.value.openAfterRef = true;
          lastRequest.value.controller.goToRef();
          //canOpenTray.value = false;
        }
        return;
      }
    }
    catch (err) {
      console.error("hcu.cabinet.getStatus", err);
      if (err.message.startsWith("JSON.parse")) {
        throw new Error(t('catalog.hcu_not_responding'));
      }
      else {
        throw err;
      }
    }

    isReopen.value = false;

    lastOpenTrayAction.value = "open";
    lastOpenTrayArgs.value = payload;
    lastOpenTrayController.value = controller;

    //Request to open tray
    controller.openTray(payload);

    // canInterrupt(true);
    // useSessionTimer(true);
  }
  catch (err) {
    console.error("openTray", tray, err);
    toast(err); //dialog(err);
    canInterrupt(true);
    useSessionTimer(true);
  }
}

/******************************************************************************************
 * Re-open tray with last issued command
 ******************************************************************************************/
function reopenTray() {
  const controller = lastOpenTrayController.value;
  const payload = lastOpenTrayArgs.value;

  if (controller && payload) {
    isReopen.value = true;
    //Request to open tray
    controller.openTray(payload);
  }
}

/******************************************************************************************
 * Start closing last tray
 ******************************************************************************************/
async function closeTray(itemTray) {
  //Check if allowed to progress
  // if (!canCloseTray.value) {
  //   return;
  // }

  try {
    if (!itemTray.cabinetId) {
      throw new Error(t('catalog.tray_no_cabinet'));
    }

    let controller = cabinetControllers[itemTray.cabinetId];
    if (!controller) {
      throw new Error(t('catalog.controller_not_defined'));
    }

    let now = new Date();
    let time = now.toISOString().split("T")[1].substr(0, 8);

    let payload = null;
    switch (controller.model) {
      case "plt-m100":
      case "plt-m280":
      case "plt-m280t":
        //Back to the same tray
        break;

      default:
        throw new Error(t('catalog.controller_model_unknown', { model: controller.model }));
    }

    //Do not allow any external event interruptions during tray opening
    canInterrupt(false);

    //Avoid session timeout while a tray is open
    useSessionTimer(false);

    try {
      let trayName = itemTray.name;
      if (itemTray.subdivision?.name) {
        trayName += "-" + itemTray.subdivision?.name;
      }
      if (appState.isLogEnabled) {
        await logService.insertLog({
          a: "cabinet.closeTray",
          t: "cabinet",
          s: "debug",
          uid: currentUserId.value,
          //un: user.value?.displayName || user.value?.username,
          cid: itemTray.cabinetId,
          //cn: itemTray.cabinetName,
          tid: itemTray.trayId || itemTray.id,
          tn: trayName, //Tray name including subdivision
          did: itemTray.subdivision?.id,
          msg: "Zapiranje predala {trayName}".replace("{trayName}", trayName), // + (itemTray.cabinetName ? " na omari " + itemTray.cabinetName : ""),
          ts: new Date()
        });
      }
    }
    catch (err) {
      console.error("[catalog] Log error", err);
    }

    //May not be actually true, but is good to display loading animation now for better user experience
    isOpening.value = true;
    isClosing.value = true;

    lastRequest.value = {
      controller,
      cabinetId: itemTray.cabinetId,
      payload,
      tray: itemTray
    };

    lastOpenTrayAction.value = "close";
    lastOpenTrayArgs.value = payload;
    lastOpenTrayController.value = controller;    

    /*
    try {
      let status2 = await hcu.cabinet(controller.mac).getStatus2();
      if (status2.model == "plt-b414" && status2.ref !== true) {
        //throw new Error("Krmilnik ni na referenčni poziciji!");
        console.log("[re-open] go to ref");
        if (lastRequest.value) {
          lastRequest.value.openAfterRef = true;
          lastRequest.value.controller.goToRef();
          //canOpenTray.value = false;
        }
        return;
      }
    }
    catch (err) {
      console.error("hcu.cabinet.getStatus", err);
      if (err.message.startsWith("JSON.parse")) {
        throw new Error(t('catalog.hcu_not_responding'));
      }
      else {
        throw err;
      }
    }
    */

    //Request to open tray
    controller.closeTray(payload);

    // canInterrupt(true);
    // useSessionTimer(true);
  }
  catch (err) {
    toast(err); //dialog(err);
    canInterrupt(true);
    useSessionTimer(true);
  }
}

//Selected article
const selected = ref({ id: "100224", name: "Article name", sku: "100224" });

//const headers = ref(table.headers);
const rows = ref([
  // { id: "100224", name: "Vege sendvič", sku: "100224", stock: "3 kosi", imageUrl: "../assets/img/logo.png" },
  // { id: "100225", name: "Tuna sendvič", sku: "100225", stock: "zadnji kos", imageUrl: "../assets/img/logo.png" },
  // { id: "100225", name: "Tuna sendvič", sku: "100225", stock: "prazno" },
  // { id: "100225", name: "Tuna sendvič", sku: "100225", stock: "2 kos", imageUrl: "../assets/img/logo.png" },
  // { id: "100225", name: "Tuna sendvič", sku: "100225", stock: "10 kosov", imageUrl: "../assets/img/logo.png" },
  // { id: "100225", name: "Tuna sendvič", sku: "100225", stock: "10 kosov", imageUrl: "../assets/img/logo.png", attachments: [ { name: "Specifikacija" }, { name: "Navodila" } ] },
  // { id: "100225", name: "Tuna sendvič", sku: "100225", stock: "10 kosov", location: "Predal B12, vložek 2" },
  // { id: "100225", name: "Tuna sendvič", sku: "100225", stock: "10 kosov" },
]);

const rowsLoaded = ref([ ]);

//const rowsFiltered = computed(() => rows.value);

async function refreshRowFilter() {
  /*
  if (!isRootCategory.value) {
    let categoryId = selectedCategoryId.value;
    console.log("filter", categoryId)
    rows.value = [ ];
    if (categoryId == "CT123") {
      console.log("kovinski")
      onSearchRequest("kovin");
      return;
    }
    if (categoryId == "CT124") {
      console.log("plastic")
      //rowsFiltered.value = rows.value.filter(x => x.name.toLowerCase().indexOf("pl") >= 0);
      onSearchRequest("pla");
      return;
    }
    if (categoryId == "CT125") {
      console.log("les")
      //rowsFiltered.value = rows.value.filter(x => x.name.toLowerCase().indexOf("lesen") >= 0);
      onSearchRequest("lesen");
      return;
    }
    onSearchRequest("vijak");
    return;
  }
  else {
    console.log("all")
  }
  */

  rows.value = null;
  await loadArticles();
  rows.value = rowsLoaded.value;
}

async function onSearchRequest(value) {
  try {
    loadArticles();
  }
  catch (err) {
    //dialog(err.message)
  }
}

function onSearchTextChanged(value) {
  //Uncomment for instant search; comment to wait for "Enter" key press
  onSearchRequest(value);
}

async function onParameterChange(option, parameter) {
  $virtualKeyboard.hide();

  try {
    if (parameter && parameter.locked) {
      return;
    }
    option.selected ^= true;
    loadArticles();
  }
  catch (err) {
    //dialog(err.message)
  }
}

function selectCategory(categoryId) {
  $virtualKeyboard.hide();

  selectedCategoryId.value = categoryId; //Actual category id or "_root"
  parentCategoryId.value = null;
  isPrivateView.value = false;
  isUserItemsVisible.value = false;
  isAutoUserItems.value = false;

  currentCategory.value = categories.value?.length > 0 ? categories.value.find(x => x.id == categoryId) : null;
  
  let symbolicSmallImageUrl = null;
  let symbolicLargeImageUrl = null;
  let breadcrumbs = [ ];
  let list = [ ];

  function walk(levelCategories, level) {
    for (let category of levelCategories) {
      //Check if found category id
      if (category.id == categoryId) {
        if (category.items && category.items.length > 0) {
          for (let item of category.items) {
            list.push(item); //TODO: Clone
          }
        }

        let current = {
          ...category,
          level: level,
        };

        //Add breadcrumb
        breadcrumbs.push(current);

        //Find last category in depth that still has an image
        if (!symbolicSmallImageUrl && category.smallImageUrl) {
          symbolicSmallImageUrl = category.smallImageUrl;
          symbolicLargeImageUrl = category.largeImageUrl;
        }

        return [ current ];
      }

      //Search further
      if (category.items && category.items.length > 0) {
        let result = walk(category.items, level + 1);
        if (result) {
          let current = {
            ...category,
            level: level,
          };
          result.push(current);

          //Add breadcrumb
          breadcrumbs.push(current);

          //Find last category in depth that still has an image
          if (!symbolicSmallImageUrl && category.smallImageUrl) {
            symbolicSmallImageUrl = category.smallImageUrl;
            symbolicLargeImageUrl = category.largeImageUrl;
          }

          return result;
        }
      }

    }
  }
  
  let tree = walk([ categoryTree.value ], 0);

  //Sort breadcrumbs
  breadcrumbs.reverse();

  //Symbolic fallback image of article
  nearestParentCategorySmallImageUrl.value = symbolicSmallImageUrl;
  nearestParentCategoryLargeImageUrl.value = symbolicLargeImageUrl;

  //Previous category
  parentCategoryId.value = breadcrumbs && breadcrumbs.length > 1 ? breadcrumbs[breadcrumbs.length - 2].id : "_root";

  //Refresh breadcrumbs
  categoryBreadcrumbs.value = breadcrumbs;

  //Sort categories
  if (categoryId == "_root") {
    list = list.sort((a, b) => (a.globalPosition ?? a.position ?? 0) - (b.globalPosition ?? b.position ?? 0));
  }

  //Refresh listed categories
  categories.value = list;

  refreshRowFilter();
  refreshVisibleParameters();
}

function refreshVisibleParameters() {
  let list = [ ];

  let articles = rowsFiltered.value;

  //Determine when to display parameters
  const minArticlesCount = 9; //Must have at least some articles to filter
  const minParametersCount = 2; //Must have at least two parameters to choose from

  if (allPossibleParameters.value?.length > 0 && articles.length > minArticlesCount && isArticleSectionVisible.value) {
    //Final parameters of displayed articles
    let p = { };
    for (let article of articles) {
      if (article.parameters) {
        let plist = Object.keys(article.parameters);
        for (let pid of plist) {
          if (!p[pid]) {
            p[pid] = { };
          }
          p[pid][article.parameters[pid]] = true;
        }
      }
    }

    //Convert to a list
    let keys = Object.keys(p);
    for (let key of keys) {
      //Must have at least few parameters to display
      if (Object.keys(p[key]) < minParametersCount) {
        continue;
      }

      //Find parameter name
      let parameter = allPossibleParameters.value.find(x => x.id == key);
      if (parameter) {
        //Add to the list of parameters to choose from
        list.push({
          id: parameter.id,
          name: parameter.name,
          options: Object.keys(p[key]).map(x => ({ id: x, name: x }))
        });
      }
    }

  }

  parameters.value = list;
}

const pendingTransferRecord = ref();
const pendingTransferQuantity = ref();
const isStockTransferApplied = ref();
const isStockTransferRequest = ref();

function startStockTransfer(selected) {
  isStockTransferRequest.value = true;
  applyStock(selected);
}

function applyStockTransfer() {
  let currentStock = virtualStockQuantity.value || 0;
  let transferStock = pendingTransferQuantity.value || 0;
  virtualStockQuantity.value = currentStock + transferStock;
  isStockTransferApplied.value = true;
}

function undoStockTransfer() {
  let currentStock = virtualStockQuantity.value || 0;
  let transferStock = pendingTransferQuantity.value || 0;
  virtualStockQuantity.value = currentStock - transferStock;
  isStockTransferApplied.value = false;
}

const quantityFactor = ref(1);

function rotateQuantityFactor() {
  let factor = quantityFactor.value || 1;

  //Multiplication factor can be 1, 10, 100, 1000
  factor *= 10;
  if (factor >= 10000) {
    factor = 1;
  }

  quantityFactor.value = factor;
}

function updateQuantity(delta) {
  let qty = virtualStockQuantity.value;
  let isPositiveNumber = (qty === 0 || qty > 0);
  if (!isPositiveNumber) {
    qty = 0;
  }
  if (!delta) {
    delta = 0;
  }
  qty = parseInt(qty) + parseInt(delta) * quantityFactor.value;
  if (qty < 0) {
    qty = 0;
  }
  virtualStockQuantity.value = qty;
}

async function loadSettings() {
  //settings.value = await configService.getSettings();

  //Local cache of articles and images for quick loading and offline environment
  if (settings.value?.preferences?.localCache) {
    isLocalCacheEnabled.value = settings.value?.preferences?.localCache == "enabled";
  }

  //Automatic tray selection mode algorithm to be used
  if (settings.value.usage?.weightMeasure) {
    isScaleValueRequired.value = settings.value.usage?.weightMeasure == "required"; //required, optional
  }

  //Automatic tray selection mode algorithm to be used
  if (settings.value.catalog?.traySelectMode) {
    bestTrayPreference = settings.value.catalog?.traySelectMode;
  }

  //Send email on empty stock
  if (settings.value.catalog?.canSendEmptyStockEmail) {
    reportEmptyStock = true;
  }

  //Category design
  if (settings.value.debug?.categoryDesign) {
    categoryDesign.value = settings.value.debug.categoryDesign;
  }

  //Delay reopening cabinet tray if user didn't fulfill the requirements
  let delay = settings.value.preferences?.autoReopenDelay;
  autoReopenDelay.value = parseInt(delay) > 0 ? parseInt(delay) : null;
}

async function loadUser() {
  let userId = authStore.userId.value;
  if (userId) {
    let result = await userService.getUser(userId);
    user.value = result;
  }
}

async function loadUsers() {
  let result = await userService.getUsers();
  users.value = result.items;
}

//TODO: Call this function after private tray is closed or article item returned
async function refreshUserItems() {
  await loadUserItems();
}

//Load inventory from preloaded data - only for this cabinet, also without pending stock transfers
async function loadUserItems() {
  return loadUserInventory();
  /*
  let list = [ ];

  //Find occupied private trays
  let trays = privateTrays.value;
  for (let tray of trays) {
    if (tray.userId == currentUserId.value && tray.status == 'occupied') {
      list.push({ type: "private", privateTray: tray });
    }
  }

  //Find borrowed articles
  let items = rows.value;
  for (let item of items) {
    if (item.trays?.length) {
      for (let tray of item.trays) {
        if (tray.usage == "taken" && tray.userId == currentUserId.value) {
          list.push({ type: "article", articleItem: item });
          continue;
        }
      }
    }
  }

  userItems.value = list;
  */
}

const isLoadingUserItems = ref();

//Note: loadUserItems() and loadUserInventory() mutually exclusive
//Load inventory from server - all user's items from all cabinets
async function loadUserInventory() {
  let list = [ ];

  isLoadingUserItems.value = true;

  let userId = authStore.userId.value;
  if (userId) {
    let result = await userService.getUserInventory(userId);

    //Occupied private trays
    let privateTrays = result.privateTrays || [];
    for (let tray of privateTrays.filter(x => x.status == 'occupied')) {
      list.push({
        type: "private",
        privateTray: {
          ...tray,
          name: tray.trayName,
          status: "occupied"
        }
      });
    }
    
    //Borrowed articles
    let borrowedItems = (result.borrowedItems || []).filter(x => x.usage == "taken");
    for (let item of borrowedItems) {
      list.push({
        type: "article",
        articleItem: {
          ...item,
          name: item.articleName,
          ...unwrapImageLinks(item),
        }
      });
    }

    //Pending stock transfers
    let pendingTransfers = (result.pendingTransfers || []).filter(x => x.type == "transfer");
    for (let item of pendingTransfers) {
      console.log("item pending transfer", item)
      console.log("item pending transfer links", unwrapImageLinks(item))
      list.push({
        type: "transfer",
        articleItem: {
          ...item,
          name: item.articleName,
          ...unwrapImageLinks(item),
        }
      });
    }
  }

  userItems.value = list;

  isLoadingUserItems.value = false;
}

function unwrapImageLinks(item) {
  let result = { };
  if (!item) {
    return result;
  }
  if (item.largeImagePath) {
    result.largeImageUrl = resolveImageUrl(item.largeImagePath);
  }
  if (item.mediumImagePath) {
    result.mediumImageUrl = resolveImageUrl(item.mediumImagePath);
  }
  if (item.smallImagePath) {
    result.smallImageUrl = resolveImageUrl(item.smallImagePath);
  }
  if (item.thumbnailPath) {
    result.thumbnailUrl = resolveImageUrl(item.thumbnailPath);
  }
  return result;
}

const cabinetList = ref([ ]);

const areLockerCabinets = computed(() => cabinetList.value?.length > 0 && cabinetList.value.filter(x => x.model == "plt-l18" || x.model == "plt-l64").length == cabinetList.value.length);

function getCabinetMode(cabinetId) {
  let cabinet = cabinetList.value.find(x => x.id == cabinetId);
  return cabinet?.mode; //storage, vault
}

function getCabinetIds() {
  let idList = [ ];
  
  //Master (or single) cabinet
  if (cabinetId.value) {
    idList.push(cabinetId.value);
  }

  //Master + satellites
  if (cabinetIds.value?.length) {
    idList = [ ...idList, ...cabinetIds.value ];
  }

  return idList;
}

const articlesList = ref();
const cabinetFeatures = ref();
const cabinetStock = ref()
const cabinetTrays = ref();
const privateTrays = ref();
const privateTraysFiltered = computed(() => privateTrays.value);

function getAllStock(cabinetIdList, options) {
  return new Promise(async (resolve, reject) => {
    try {
      const tasks = [ ];
      for (let cabinetId of cabinetIdList) {
        tasks.push(cabinetService.getCabinetStock(cabinetId, options));
      }
      let results = await Promise.all(tasks);
      console.log('[loadCabinetStock] Results:', results);
      resolve(results);
    }
    catch (err) {
      console.error('[loadCabinetStock] Error:', err);
      reject(err);
    }
  });
}

async function loadCabinetStock() {
  let sw = new Stopwatch();
  console.log("[loadCabinetStock] Started", sw.elapsed());

  let cabinetIdList = getCabinetIds();
  let localCabinetIdList = [ ...cabinetIdList ];
  let privateTrayList = [ ];
  let cabinetTrayList = [ ];
  let articles = { };
  let features = { };
  let stock = { };

  let frsMode = true; //Backward compatible
  if (isShowAllArticlesInOrganization.value) {
    cabinetIdList = allCabinetIds.value;
    frsMode = 2; //Return all related and let the client filter
  }

  console.log("[loadCabinetStock] Local cabinet units", localCabinetIdList);
  console.log("[loadCabinetStock] All cabinet units", cabinetIdList);

  let cabinetStocks = [ ];
  try {
    cabinetStocks = await getAllStock(cabinetIdList, { articles: true, frs: frsMode });
    console.log("[loadCabinetStock] Get all stock", sw.elapsed() + "ms", sw.delta());
    console.log(cabinetStocks);
  }
  catch (err) {
    console.error("[loadCabinetStock] Get all stock", err);
  }

  let index = 0;
  for (let cabinetId of cabinetIdList) {
    try {
      //Include article records and find related stock (frs)
      let options = { articles: true, frs: frsMode };

      //Get cabinet stock for a specific tray
      let result = cabinetStocks[index++] || await cabinetService.getCabinetStock(cabinetId, options);
      if (result.error) {
        throw new Error(result.error);
      }
      
      features[cabinetId] = result.features;
      stock[cabinetId] = result;

      console.log("[loadCabinetStock] Cabinet", cabinetId, "load time", sw.elapsed() + "ms", sw.delta());

      //Combine arrays
      let resultTrays = result.trays || [ ];
      if (localCabinetIdList.includes(cabinetId)) {
        cabinetTrayList = [ ...cabinetTrayList, ...resultTrays ];
      }

      //Collect unique articles only
      for (let article of result.articles) {
        //Other related cabinets containing the same item on stock
        article.relatedStock = result.relatedStock?.filter(x => x.articleId == article.id && !localCabinetIdList.includes(x.cabinetId));
        // if (article.id == "ARVDHjztyE8D6a4g") { //Zaščitne rokavice laser 9
        //   console.log("[loadCabinetStock] Zaščitne rokavice laser 9", cabinetId, { id: article.id }, article.relatedStock, result.relatedStock);
        // }
        // if (article.relatedStock && result.relatedStock && article.relatedStock.length > 0 && article.relatedStock.length != result.relatedStock.length) {
        //   console.log("[loadCabinetStock] Related stock", cabinetId, article.id, article.relatedStock);
        // }
        articles[article.id] = article;
      }

      //Check if cabinet contains any private trays
      let cabinetPrivateTrays = result.trays.filter(x => x.isPrivate);
      if (cabinetPrivateTrays.length > 0) {
        for (let privateTray of cabinetPrivateTrays) {
          privateTrayList.push({
            cabinetId: privateTray.cabinetId || cabinetId,
            trayId: privateTray.trayId,
            name: privateTray.name,
            status: privateTray.status,
            userId: privateTray.privateUserId,
            occupiedTime: privateTray.occupiedTime,
          });
        }
      }

    }
    catch (err) {
      console.error("[articles] loadCabinetStock", cabinetId, err);
    }
  }

  console.log("[loadCabinetStock] Find cabinet stock", sw.elapsed() + "ms", sw.delta());

  //Convert object to list
  let uniqueArticles = Object.keys(articles).map(x => articles[x]);

  console.log("[loadCabinetStock] Find unique articles", sw.elapsed() + "ms", sw.delta());

  //Find all trays in relation with inventory items (articles)
  let cabinetTrayRelations = [ ];
  let tlist = cabinetTrayList.filter(x => x.items?.length);
  for (let tray of tlist) {
    let ilist = tray.items.filter(x => /* x.type == "article" && */ x.articleId); //TODO: Fix type = article because it disapears on some update somwehere
    for (let item of ilist) {
      cabinetTrayRelations.push({
        id: tray.id,
        trayId: tray.id || tray.trayId,
        itemId: item.id || item.itemId,
        cabinetId: tray.cabinetId,
        articleId: item.articleId,
        subdivisionId: item.subdivisionId,
        subdivision: item.subdivisionId ? tray.subdivisions?.find(sub => sub.id == item.subdivisionId) : null,
        ...tray,
        name: tray.name, //Override with tray name (not article name)
        status: tray.status,
        countMode: item.countMode,
        quantity: item.quantity,
        unit: item.unit,
        itemWeight: item.itemWeight,
        usage: item.usage,
        userId: item.userId,
        userTime: item.userTime,
        comment: item.comment,
      });
    }
  }

  //Map articles and trays
  for (let article of uniqueArticles) {
    article.trays = cabinetTrayRelations.filter(item => item.articleId == article.id);
  }

  console.log("[loadCabinetStock] Map articles and trays", sw.elapsed() + "ms", sw.delta());

  //Set reactive variables
  cabinetStock.value = stock;
  cabinetFeatures.value = features;
  cabinetTrays.value = cabinetTrayList;
  privateTrays.value = privateTrayList;
  hasPrivateTrays.value = privateTrayList.length > 0 && currentUserId.value; //Hide private trays on anonymous login
  articlesList.value = uniqueArticles;

  //User's private tray, if any
  userPrivateTray.value = privateTrayList.find(x => x.status == "occupied" && x.userId == currentUserId.value);

  console.log("[loadCabinetStock] Found user's private trays", sw.elapsed() + "ms", sw.delta());

  //Calculate performance cost
  let stockSumCost = 0;
  let vaultItemCost = 0;
  let relatedCost = 0;
  let keywordsCost = 0;

  //Post-processing
  allPossibleArticles.value = uniqueArticles.map(x => {
    //Sum of quantity
    let sw1 = new Stopwatch();
    let quantity = stockSum(x.trays);
    stockSumCost += sw1.elapsed();

    let sw2 = new Stopwatch();
    let vault = quantity > 0 ? false : isVaultItem(x.trays);
    vaultItemCost += sw2.elapsed();

    //Convert string to Date object - required for formatting
    if (x.trays && x.trays.length) {
      for (let t of x.trays) {
        if (t.userTime && typeof t.userTime == "string") {
          t.userTime = new Date(t.userTime);
        }
      }
    }

    //Group related stock by cabinet
    let sw3 = new Stopwatch();
    let relatedStockGroup = [ ];
    if (x.relatedStock?.length > 0) {
      for (let rs of x.relatedStock) {
        let group = relatedStockGroup.find(stock => stock.cabinetId == rs.cabinetId);
        if (group && group.unit == rs.unit) {
          group.quantity += rs.quantity;
        }
        else {
          relatedStockGroup.push(rs);
        }
      }
    }
    relatedCost += sw3.elapsed();

    //Build search keywords
    let trayNamesCombined = x.trays?.length > 0 ? x.trays.map(t => t.name).join(" ") : null;

    let sw4 = new Stopwatch();
    let searchKeywords = buildSearchKeywords([ x.id, x.name, x.sku, x.sku2, trayNamesCombined ]);
    keywordsCost += sw4.elapsed();
    
    return {
      ...x,
      id: x.id,
      articleId: x.id,
      name: x.name,
      sku: x.sku,
      sku2: x.sku2,
      trays: x.trays,
      totalQuantity: quantity,
      isVaultItem: vault,
      itemWeight: x.itemWeight,
      permissions: x.permissions,
      description: x.description,
      originalImageId: x.originalImageId,
      largeImagePath: x.largeImagePath,
      largeImageUrl: resolveImageUrl(x.largeImagePath),
      mediumImagePath: x.mediumImagePath,
      mediumImageUrl: resolveImageUrl(x.mediumImagePath),
      smallImagePath: x.smallImagePath,
      smallImageUrl: resolveImageUrl(x.smallImagePath),
      thumbnailPath: x.thumbnailPath,
      thumbnailUrl: resolveImageUrl(x.thumbnailPath),
      categories: x.categories,
      parameters: x.parameters,
      related: x.related, //Related artices
      relatedStock: x.relatedStock,
      relatedStockGroup: relatedStockGroup,
      relatedStockQuantity: x.relatedStock?.map(t => t.quantity).sum(), //Number of articles available in other cabinets
      relatedStockAvailable: !!x.relatedStock?.find(t => t.available), //Any article available to borrow from another cabinet?
      relatedStockAvailableToBorrow: !!x.relatedStock?.find(t => t.usage == "present"), //Any article available to borrow from another cabinet?
      keywords: searchKeywords, //Search words
      isArticle: true, //Required for search (to know whether search hit is a category or an article)
    };
  });

  console.log("[loadCabinetStock] Post-processing for articles", sw.elapsed() + "ms", sw.delta());
  console.log("[loadCabinetStock] Consumed time for building search words", keywordsCost + "ms");
  console.log("[loadCabinetStock] Consumed time for grouping related articles ", relatedCost + "ms");
  console.log("[loadCabinetStock] Consumed time for counting quantity", stockSumCost + "ms");
  console.log("[loadCabinetStock] Consumed time for detecting vault items", vaultItemCost + "ms");

  //TODO: Make it more efficient
  //Work-around to map categories and parameters with the article
  await preloadArticlesOnce();
  console.log("[loadCabinetStock] Preload articles once", sw.elapsed() + "ms", sw.delta());

  let aplist = allPossibleArticles.value;
  let apmap = allPossibleArticles2.value.reduce((acc, x) => (acc[x.id] = x), { });
  for (let article of aplist) {
    //let article2 = allPossibleArticles2.value.find(x => x.id == article.id); //Very slow!
    let article2 = apmap[article.id];
    if (article2) {
      article.categories = article2.categories;
      article.parameters = article2.parameters;
    }
  }

  console.log("[loadCabinetStock] Map articles categories parameters", sw.elapsed() + "ms", sw.delta());

  mapParameters();
  console.log("[loadCabinetStock] Map parameters", sw.elapsed() + "ms", sw.delta());

  console.log("[loadCabinetStock] Total load time", sw.elapsed() + "ms");
}

const primaryCabinetId = ref();

async function loadCabinetsSlow() {
  let idList = getCabinetIds();
  let list = [ ];

  let isInsertWeightSet = false;

  for (let cabinetId of idList) {
    try {
      let cabinet = await cabinetService.getCabinet(cabinetId);

      if (!primaryCabinetId.value) {
        primaryCabinetId.value = cabinet.id;
      }

      /*
      //TODO: Primary master cabinet only
      try {
        if (!hasLocation.value && cabinet.location) {
          //cabinetLocation.value = cabinet.location; //locationId, point

          let organization = await organizationService.getOrganization(cabinet.orgId);
          let location = organization.locations.find(x => x.id == cabinet.location.locationId);
          if (location) {
            cabinetLocation.value = {
              locationId: location.id,
              name: location.name,
              point: cabinet.location.point,
              imageBase64: location.imageBase64,
            }
          }
          // console.log("organization", cabinet.orgId, organization);
          // console.log("organization.location", );
        }
      }
      catch (err) {
        console.error("[location]", err);
      }
      */

      //Cabinet model, e.g. plt-b414
      if (!cabinet.model) {
        cabinet.model = cabinet.type; //Legacy type = model
      }

      //Empty insert weight in grams
      if (cabinet.config?.insertWeight > 0 && !isInsertWeightSet) {
        scaleInsertWeight.value = cabinet.config.insertWeight;
        isInsertWeightSet = true;
      }

      //Build a list of combined cabinets
      list.push(cabinet);
    }
    catch (err) {
      console.error("[articles] loadCabinet", cabinetId, err);
    }
  }

  cabinetList.value = list;
}

async function loadCabinets() {
  let idList = getCabinetIds();
  let list = [ ];
  let isInsertWeightSet = false;

  try {
    /*
    //Find master cabinet id
    let { controllers } = await hcu.cabinet().getInstances2();
    let master = controllers.find(x => x.master);
    if (!master?.id) {
      loadCabinetsSlow();
      return;
    }

    //Get master cabinet and its satellites
    let result = await cabinetService.getCabinets({ master: master.id });
    if (!primaryCabinetId.value) {
      primaryCabinetId.value = master.id;
    }
    */

    if (isShowAllArticlesInOrganization.value) {
      let allCabinets = await cabinetService.getCabinets({ fields: "_id,name" });
      allCabinetIds.value = allCabinets.items.map(x => x.id);
    }

    //Get master cabinet and its satellites
    let result = await cabinetService.getCabinets({ cabinets: idList.join(",") });
  
    let cabinets = result.items;
    for (let cabinet of cabinets) {
      /*
      //TODO: Primary master cabinet only
      try {
        if (!hasLocation.value && cabinet.location) {
          //cabinetLocation.value = cabinet.location; //locationId, point

          let organization = await organizationService.getOrganization(cabinet.orgId);
          let location = organization.locations.find(x => x.id == cabinet.location.locationId);
          if (location) {
            cabinetLocation.value = {
              locationId: location.id,
              name: location.name,
              point: cabinet.location.point,
              imageBase64: location.imageBase64,
            }
          }
          // console.log("organization", cabinet.orgId, organization);
          // console.log("organization.location", );
        }
      }
      catch (err) {
        console.error("[location]", err);
      }
      */

      if (!primaryCabinetId.value) {
        primaryCabinetId.value = cabinet.id;
      }

      //Cabinet model, e.g. plt-b414
      if (!cabinet.model) {
        cabinet.model = cabinet.type; //Legacy type = model
      }

      //Empty insert weight in grams
      if (cabinet.config?.insertWeight > 0 && !isInsertWeightSet) {
        scaleInsertWeight.value = cabinet.config.insertWeight;
        isInsertWeightSet = true;
      }

      //Build a list of combined cabinets
      list.push(cabinet);
    }

    //Attach master cabinet location and image to all its satellite cabinets
    for (let cabinet of cabinets.filter(x => x.parentId)) {
      let master = cabinets.find(x => x.id == cabinet.parentId);
      if (master?.image?.id) {
        cabinet.image = { ...master.image };
      }
      if (master?.location) {
        cabinet.location = { ...master.location };
      }
    }

  }
  catch (err) {
    console.error("[articles] loadCabinet", err);
  }

  cabinetList.value = list;
}

async function loadParameters() {
  let idList = getCabinetIds();
  let query = { };

  query.cabinets = idList.join(",");

  //Get combined parameters
  let result = await articleService.getParameters(query);

  function position(a, b) {
    if (a < b) return +1;
    if (a > b) return -1;
    return 0;
  }

  let list = [ ];
  if (result.items && result.items.length > 0) {
    list = result.items.sort(position).map(x => {
      return {
        id: x.id,
        name: x.name,
        options: [ ]
      };
    });
  }

  /*
  {
    id: "PR123",
    name: "Length",
    options: [
      { id: "PR-D1", name: "1 mm" },
      { id: "PR-D2", name: "2 mm" },
      { id: "PR-D3", name: "3 mm" },
      { id: "PR-D4", name: "4 mm" },
      { id: "PR-D5", name: "5 mm" },
      { id: "PR-D6", name: "6 mm" },
      { id: "PR-D7", name: "7 mm" },
      { id: "PR-D8", name: "8 mm" },
      { id: "PR-D9", name: "9 mm" },
      { id: "PR-D10", name: "10 mm" },
      { id: "PR-D11", name: "11 mm" },
      { id: "PR-D12", name: "12 mm" },
    ]
  }
  */

  allPossibleParameters.value = list;
}

async function loadCategories() {
  let idList = getCabinetIds();
  let query = { };

  query.cabinets = idList.join(",");

  //Get combined categories
  let result = await articleService.getCategories(query);

  // function resolveImageUrl(imagePath) {
  //   return imagePath ? articleService.baseUrl + "/api/v1/file/" + imagePath : null;
  // }

  //Category definitions
  let categories = result.items.sort((a, b) => (a.globalPosition ?? a.position ?? 0) - (b.globalPosition ?? b.position ?? 0)).map(x => {
    return {
      id: x.id,
      name: x.name,
      parentId: x.parentId,
      position: x.position,
      globalPosition: x.globalPosition, //Root level only, for sorting among different catalogues
      level: 1, //Calculated below
      hasChildren: false, //Calculated below
      originalImageId: x.originalImageId,
      largeImagePath: x.largeImagePath,
      largeImageUrl: resolveImageUrl(x.largeImagePath),
      mediumImagePath: x.mediumImagePath,
      mediumImageUrl: resolveImageUrl(x.mediumImagePath),
      smallImagePath: x.smallImagePath,
      smallImageUrl: resolveImageUrl(x.smallImagePath),
      thumbnailPath: x.thumbnailPath,
      thumbnailUrl: resolveImageUrl(x.thumbnailPath),
      articlesCount: 0, //Calculated below
      keywords: `${x.id} ${x.name}`.toLowerCase(),
      isCategory: true, //For search
    };
  });

  //Recursive search upwards to find category level
  let deepestLevel = 0;
  function findLevel(current, base) {
    if (current.parentId) {
      let parent = categories.find(x => x.id == current.parentId);
      if (parent) {
        parent.hasChildren = true;
        base.level++;
        deepestLevel = Math.max(base.level, deepestLevel);
        findLevel(parent, base);
      }
    }
  }
  for (let category of categories) {
    findLevel(category, category);
  }

  //Count and map articles in categories
  if (allPossibleArticles.value) {
    for (let article of allPossibleArticles.value) {
      for (let categoryId of article.categories) {
        let category = categories.find(x => x.id == categoryId);
        if (category) {
          category.articlesCount++;
        }
      }
    }
  }

  //From deepest level up update articles count for each tree branch
  for (let i = deepestLevel; i > 0; i--) {
    let nodes = categories.filter(x => x.level == i && x.articlesCount > 0 && x.parentId);
    for (let node of nodes) {
      let parent = categories.find(x => x.id == node.parentId);
      parent.articlesCount += node.articlesCount;
    }
  }

  /*
  console.log("Categories map", deepestLevel);
  console.log(categories.map(x => `[${x.level}] ${x.hasChildren ? 'x' : ' '} ${x.name} => ${x.articlesCount ? x.articlesCount + ' kos' : '--'}`).join("\r\n"))
  */

  //Flat list of categories
  allPossibleCategories.value = [ ...categories ];

  //Tree structure of categories
  buildCategoryTree(categories);
}

function buildCategoryTree(categories) {
  let tree = { items: [ ] };
  
  //tree.push({ id: "_root", name: "Korenski nivo", class: "italic" });

  function addItems(items, level, parentId, parentTreeNode) {
    let itemsSameParent = [ ];

    //Find items with the same parent
    for (let item of items) {
      if (item.parentId == parentId) {
        itemsSameParent.push(item);
      }
    }

    //Sort by position
    itemsSameParent = itemsSameParent.sort(function(a, b) {
      if (a.position < b.position) {
        return -1;
      }
      if (a.position > b.position) {
        return +1;
      }
      return 0;
    });

    //Attach to the tree leaf
    for (let item of itemsSameParent) {
      let treeNode = {
        ...item,
        // id: item.id,
        // name: item.name,
        // originalImageId: item.originalImageId,
        // largeImageUrl: item.largeImageUrl,
        // mediumImageUrl: item.mediumImageUrl,
        // smallImageUrl: item.smallImageUrl,
        // thumbnailUrl: item.thumbnailUrl,
        items: [ ]
      };
      parentTreeNode.items.push(treeNode);
      addItems(items, level + 1, item.id, treeNode);
    }
  }

  addItems(categories, 0, null, tree);
  //addItems(categories, 0, "_root", tree); //Same as "null" but required for backward compatibility

  categorySubtree.value = tree.items;
}

function findCategoryById(itemId) {
  let result = null;

  function findOne(items, level, parent) {
    let index = 0;
    for (let item of items) {
      if (item.id == itemId) {
        result = {
          id: item.id,
          name: item.name,
          parent: parent,
          item: item,
          index: index
        };
        return;
      }
      if (item.items && item.items.length) {
        findOne(item.items, level + 1, item);
      }
      index++;
    }
  }

  findOne(categorySubtree.value, 0, null);

  return result;
}

const categoryList = ref([
  /*
  { id: "development", name: "Na vrhu", class:"italic" },
  { id: "production", name: "Task 1" },
  { id: "production", name: "  Subtask A", class: "pl-4" },
  { id: "production", name: "  Subtask B", class: "pl-4" },
  { id: "production", name: "    Fine tools 1", class: "pl-8" },
  { id: "production", name: "    Fine tools 2", class: "pl-8" },
  { id: "production", name: "    Fine tools 3", class: "pl-8" },
  { id: "production", name: "Task 2" },
  { id: "production", name: "Task 3" },
  { id: "production", name: "Task 4" },
  { id: "development", name: "Spodaj", class:"italic" },
  */
]);

const categorySubtree = ref([
  /*{
    id: "task1",
    name: 'task 1',
    items: [
      {
        id: "task2",
        name: 'task 2',
        items: [],
      },
    ]
  },
  {
    id: "task3",
    name: 'task 3',
    items: [
      {
        id: "task4",
        name: 'task 4',
        items: [],
      },
    ],
  },
  {
    id: "task5",
    name: 'task 5',
    items: [],
  },*/
])

//Rooted category tree
const categoryTree = computed(() => {
  return {
    id: "_root",
    name: "Kategorije",
    items: categorySubtree.value
  };
});

const selectedTrayName = ref(null);
const selectedTrayItem = ref(null);
const selectedUsage = ref(null);

/******************************************************************************************
 * Event when tray is selected in drop down
 ******************************************************************************************/
function onTraySelect() {
  let option = null;
  if (availableTraySelect.value && availableTraySelect.value != "none") {
    option = availableTrayOptions.value?.find(x => x.id == availableTraySelect.value);
  }

  let tray = option?.tray;

  selectedTrayItem.value = tray;
  selectedTrayName.value = tray ? tray.name : null;
  selectedUsage.value = tray ? tray.usage : null;
  stockComment.value = tray ? tray.comment : null; //TODO: Comment is not based only on tray, but specifically on article of tray

  //Fix to keep displaying total stock on drop down option change
  isCabinetBorrowModeDefault.value = getCabinetMode(tray?.cabinetId) == "vault";
}

function setArticleUsage(usage) {
  // if (selectedTrayItem.value) {
  //   selectedTrayItem.value.usage = usage;
  // }
  selectedUsage.value = usage;
}

// function setTrayUsage(usage) {
//   selectedTrayUsage.value = usage;
// }

function applyTrayUsage(usage) {
  selectedTrayUsage.value = usage;
  //applyStock(selected.value);
}

function editComment() {
  commentText.value = stockComment.value || "";
  isCommentEdit.value = true;
}

async function saveComment(options) {
  isCommentEdit.value = false;
  stockComment.value = commentText.value;
  commentText.value = "";

  //Automatically store to database?
  if (options.submit === true) {
    //TODO: Avoid code duplication - same code as in stock
    //TODO: Insert log message "comment changed"
    //TODO: Avoid dialog on error, instead put the message as validation message below text area
    //TODO: Loading animation when submitting
    try {
      let article = selected.value;
      let tray = selectedTrayItem.value;

      let userId = currentUserId.value;
      let data = null;

      //Backward compatible with borrow mode - submit comment immediately
      if (isCabinetBorrowMode.value) {
        data = {
          trays: [
            {
              findTrayId: true,
              articleId: article.id,
              quantity: 0, //Always 0 so not to include count in sum
              unit: "pcs",
              itemWeight: article.itemWeight,
              name: tray.name, //Tray name, e.g. A1
              usage: selectedTrayUsage.value,
              userId: userId, //user.value?.id
              userTime: utc(),
              comment: stockComment.value //TODO: Comment is not based only on tray, but a specific article of tray
            }
          ]
        };

        let cid = selectedTrayItem.value.cabinetId;
        if (!cid) {
          throw new Error(t('catalog.cabinet_id_not_defined'));
        }

        let result = await cabinetService.updateCabinetTrayStock(cid, data, { reportEmptyStock, hint: "saveComment" });

        let trayName = selectedTrayName.value;

        //Update current state on UI
        let trayItem = selected.value?.trays?.find(x => x.name == trayName);
        if (trayItem) {
          //TODO: The following states are based on article in this tray; not the whole tray per se
          trayItem.comment = stockComment.value;
          trayItem.userId = userId;
          trayItem.userTime = utc();
        }
      }
    }
    catch (err) {
      console.error(err);
      toast(err); //dialog(err);
    }
  }
}

function cancelComment() {
  isCommentEdit.value = false;
  commentText.value = "";
}

const stockComment = ref();
const commentText = ref();
const isCommentEdit = ref(false);
const hasEditCommentConditions = computed(() => {
  //return isCabinetBorrowMode.value; //Comment editing available only for borrowed items
  return true; //Comment editing available for all items
});
const canEditComment = ref(true);
const selectedTrayUsage = ref(null);

const returnArticle = ref(null);
const returnArticleTrayName = ref(null);

const takeArticle = ref(null);
const takeArticleTrayName = ref(null);

const isReturnProcedure = ref(false);
const isTakingProcedure = ref(false);
const isChoosingAction = ref(false);

const rowsBorrowed = computed(() => {
  let userId = user.value?.id;
  return rows.value.filter(x => x.trays?.find(y => y.usage == "borrowed" && y.userId == userId));
  //return rows.value.filter(x => x.trays?.find(y => y.usage == "borrowed"));
});

const rowsAvailable = computed(() => {
  return rows.value.filter(x => x.trays?.find(y => y.usage == "new"));
});

const singleArticle = computed(() => {
  return rows.value && rows.value.length > 0 ? rows.value[0] : null;
});

const itemBorrowed = computed(() => {
  let list = rowsBorrowed.value;
  if (!list || list.length == 0) {
    return null;
  }

  let userId = user.value?.id;

  let item = list[0];
  let trays = item.trays.filter(y => y.usage == "borrowed" && y.userId == userId);
  //TODO: Algorithm to use longest waiting - first inserted - or first set to usage 'new'

  return {
    id: item.id,
    name: item.name,
    article: item,
    tray: trays[0]
  };
});

const itemAvailable = computed(() => {
  let list = rowsAvailable.value;
  if (!list || list.length == 0) {
    return null;
  }

  let item = list[0];
  let trays = item.trays.filter(y => y.usage == "new");
  //TODO: Algorithm to use longest waiting - first inserted - or first set to usage 'new'

  return {
    id: item.id,
    name: item.name,
    article: item,
    tray: trays[0]
  };
});

const availableSingleArticlesCount = computed(() => {
  //return rowsAvailable.value?.length;

  //Count trays with "new" items
  let counter = rowsLoaded.value.map(x => x.trays ? x.trays.filter(y => y.usage == "new").length : 0);

  let sum = 0;
  for (let c of counter) {
    sum += c;
  }

  return sum;
});

function startReturnArticleProcedure() {
  returnArticle.value = itemBorrowed.value;
  returnArticleTrayName.value = itemBorrowed.value?.tray?.name;
  isReturnProcedure.value = true;
  isTakingProcedure.value = false;
  openTray(returnArticleTrayName.value);
}

function takeNextArticle() {
  takeArticle.value = itemAvailable;
  takeArticleTrayName.value = itemAvailable.value?.tray?.name;
  isReturnProcedure.value = false;
  isTakingProcedure.value = true;
  openTray(takeArticleTrayName.value);
}

async function cancelReturnArticle() {
  isReturnProcedure.value = isOpen.value;
  isChoosingAction.value = false;
  //returnArticle.value = null;
  //takeArticle.value = null;
}

async function confirmReturnArticle() {
  isReturnProcedure.value = isOpen.value;
  isChoosingAction.value = false;
  //returnArticle.value = null;
  //takeArticle.value = null;
  //TODO: Save to database

  if (!itemBorrowed.value?.id) {
    return dialog(new Error("Borrowed item not found!"))
  }

  let article = rows.value.find(x => x.id == itemBorrowed.value.id);
  if (!article) {
    return dialog(new Error("Article not found!"))
  }

  let tray = article.trays.find(x => x.name == returnArticleTrayName.value);
  if (!tray) {
    return dialog(new Error("Tray not found!"))
  }

  tray.userId = user.value?.id;
  tray.usage = "used";
  //itemBorrowed.value == null

  try {
    let data = {
      trays: [
        {
          findTrayId: true,
          articleId: article.id,
          quantity: 0, //Avoid counting in sum
          unit: "pcs",
          itemWeight: article.itemWeight,
          name: tray.name, //Tray name, e.g. A1
          usage: tray.usage,
          userId: tray.userId,
          userTime: utc(),
        }
      ]
    };

    let result = await cabinetService.updateCabinetTrayStock(cabinetId.value, data, { reportEmptyStock });
  }
  catch (err) {
    return dialog(err);
  }

  try {
    if (appState.isLogEnabled) {
      await logService.insertLog({
        a: "cabinet.returnItem",
        t: "cabinet",
        s: "info",
        uid: currentUserId.value,
        //un: user.value?.displayName || user.value?.username,
        cid: tray.cabinetId,
        tid: tray.trayId || tray.id,
        tn: tray.name,
        msg: `Vračilo uporabljenega artikla "${article.name}" v predal "${tray.name}".`,
        ts: new Date()
      });
    }
  }
  catch (err) {
    console.error(err);
  }
}


async function cancelTakeArticle() {
  isTakingProcedure.value = isOpen.value;
  isChoosingAction.value = false;
}

async function confirmTakeArticle() {
  isTakingProcedure.value = isOpen.value;
  isChoosingAction.value = false;

  //TODO: Save to database

  if (!itemAvailable.value?.id) {
    return dialog(new Error("itemAvailable not found!"))
  }

  let article = rows.value.find(x => x.id == itemAvailable.value.id);
  if (!article) {
    return dialog(new Error("Article not found!"))
  }

  let tray = article.trays.find(x => x.name == takeArticleTrayName.value);
  if (!tray) {
    return dialog(new Error("Tray not found!"))
  }

  tray.userId = user.value?.id;
  tray.usage = "borrowed";

  try {
    let data = {
      trays: [
        {
          findTrayId: true,
          articleId: article.id,
          quantity: 0, //Avoid counting in sum
          unit: "pcs",
          itemWeight: article.itemWeight,
          name: tray.name, //Tray name, e.g. A1
          usage: tray.usage,
          userId: tray.userId,
          userTime: utc(),
        }
      ]
    };

    let result = await cabinetService.updateCabinetTrayStock(cabinetId.value, data, { reportEmptyStock });
  }
  catch (err) {
    return dialog(err);
  }

  try {
    if (appState.isLogEnabled) {
      await logService.insertLog({
        a: "cabinet.takeItem",
        t: "cabinet",
        s: "info",
        //uid: user.value?.displayName || user.value?.username,
        uid: currentUserId.value,
        cid: tray.cabinetId,
        tid: tray.trayId || tray.id,
        tn: tray.name,
        msg: `Izposoja artikla "${article.name}" is predala "${tray.name}".`,
        ts: new Date()
      });
    }
  }
  catch (err) {
    console.error(err);
  }
}

const canOpenTray = computed(() => {
  //Check if already in progress
  if (isOpening.value) {
  //if (isAnyOpening.value) {
  //if (getAnyOpening()) {
    return false;
  }
  //Check if any tray already open
  if (isOpen.value) {
  //if (isAnyOpen.value) {
  //if (getAnyOpen()) {
    return false;
  }
  //Check if any article selected
  if (!selected.value) {
    return false;
  }
  //Check if article is in any tray
  if (!selected.value.trays || selected.value.trays.length == 0) {
    return !!selected.value.tray; //Backward compatible
    //return false; //For updated versions
  }
  if (selected.value.trays.length > 0) {
    return selectedTrayName.value != null; //Must be selected
  }
  return true;
});


function enableModalCloseButton(visible) {
  let button = document.querySelector(".non-escapable-modal button.text-gray-400.bg-transparent");
  if (button) {
    button.style.display = visible ? "block" : "none";
  }
}

watch(isOpening, async (value, previous) => {
  console.log("[watch] isOpening", previous, "=>", value);
  enableModalCloseButton(!value);
});

watch(isClosing, async (value, previous) => {
  console.log("[watch] isClosing", previous, "=>", value);
  enableModalCloseButton(!value);
});

let openTimer = null;

async function logout(e) {
  try {
    //Hide menu
    useMenu(false);
    useSubmenu(false);
    useTopBar(false);
    
    //Redirect to login page
    $router.push({ name: "Login", query: { closeTrayAutoLogout: true } });
  }
  catch (err) {
    console.error(err);
  }
}

watch(isOpen, async (value, previous) => {
  console.log("[watch] isOpen", value);
  enableModalCloseButton(!value);

  //Clear banner
  hideWarningBanner();
  clearTimeout(openTimer);

  //TODO: also clear banner on user activity, e.g. touch
  //clearTimeout(openTimer);

  //TODO: Local events like "tray open", "tray closed", "tray opening", "tray closing"

  //Detected change from open to closed
  if (previous && !value) {
    isClosing.value = false;

    //Automatically hide modal window when tray is closed
    if (canAutoCloseModal.value) {
      closeModal();
    }
  }

  if (isOpenPrivateTray.value) {
    /*
    try {
      console.log("user private", userPrivateTray.value, currentUserId.value)
      //Save changes to database
      await saveChanges(userPrivateTray.value.cabinetId, {
        trays: [
          {
            trayId: userPrivateTray.value.trayId,
            name: userPrivateTray.value.name,
            status: isReturningPrivateTray.value ? "active" : "occupied",
            //userId: isReturningPrivateTray.value ? null : currentUserId.value,
            privateUserId: isReturningPrivateTray.value ? null : currentUserId.value,
            occupiedTime: isReturningPrivateTray.value ? null : utc(),
          }
        ]
      });
    }
    catch (err) {
      console.error(err);
      toast(err); //dialog(err);
    }

    if (isReturningPrivateTray.value) {
      userPrivateTray.value = null;
    }*/
  }

  //Only for borrw vault and private vault
  if (isCabinetBorrowMode.value || isCabinetPrivateMode.value) {

    //Change to "open"
    if (value) { //TODO: settings.value alarm

      //Only if defined in settings
      let alertTimeout = settings.value?.session?.alertTimeout;
      if (alertTimeout > 0) {
        //Show "tray open" warning after few seconds
        let ms = parseInt(alertTimeout) * 1000;
        openTimer = setTimeout(() => {
          showWarningBanner(isCabinetPrivateMode.value ? "privateWindow" : "articleWindow");
        }, ms);
      }

      //Change to active or occupied for private tray
      if (isCabinetPrivateMode.value) {
        if (allowPrivateCloseChoice.value) {
          //When user needs to choose something on the screen to complete the action
          isPrivateCloseChoice.value = true;
        }
        else {
          //Automatically toggle status upon opening cabinet tray - no user action on screen is needed
          try {
            let tray = selectedTrayItem.value;
            let newStatus = tray.status != "occupied" ? "occupied" : "active";
            let result = await cabinetService.updateCabinetStock(tray.cabinetId, {
              trays: [
                {
                  trayId: tray.trayId,
                  name: tray.name,
                  status: newStatus,
                  privateUserId: newStatus == "occupied" ? currentUserId.value : null,
                  occupiedTime: utc()
                }
              ]
            }, { reportEmptyStock });
            selectedTrayItem.value.status = newStatus;
          }
          catch (err) {
            console.error("updateCabinetStock", err);
          }
        }
      }

    }

    //Change to "close"
    if (!value && settings.value?.session?.trayAutoLogout == true) {
      //Auto log out after closing the tray
      logout();
    }

  }

  //Borrow mode
  if (isReturnProcedure.value) {
    //Tray just opened
    if (value && !previous) {
      isChoosingAction.value = true;
    }

    //Tray just closed
    if (!value && previous) {
      isReturnProcedure.value = isChoosingAction.value;
    }
  }

  //Borrow mode
  if (isTakingProcedure.value) {
    //Tray just opened
    if (value && !previous) {
      isChoosingAction.value = true;
    }

    //Tray just closed
    if (!value && previous) {
      isTakingProcedure.value = isChoosingAction.value;
    }
  }

  if (isModalVisible.value && !isEditStockQuantity.value && previous == true && value == false) {
    closeModal(); //Automatically close modal window when door is closed
  }

  /*
  if (!value && previous) {
    try {
      if (appState.isLogEnabled) {
        let result = await logService.insertLog({
          a: "cabinet.tray",
          t: "cabinet",
          s: "info",
          uid: user.value?.displayName || user.value?.username,
          tid: tray.name,
          msg: `Predal "${tray.name}" je zaprt.`,
          ts: new Date()
        });
      }
    }
    catch (err) {
      console.error(err);
    }
  }
  */
});

watch(isEditStockQuantity, (value) => {
  console.log("[watch] isEditStockQuantity", value);
  if (value && autoConfirm.value) {
    //Auto-click button
    if (isCabinetBorrowMode.value && canSaveStockQuantity.value) {
      if (selectedTrayUsage.value != 'taken') {
        applyTrayUsage('taken');
        applyStock(selected.value, { keepEditMode: true }); //Keep edit mode so user can edit comment
      }
      else if (selectedTrayUsage.value != 'present') {
        applyTrayUsage('present');
        applyStock(selected.value, { keepEditMode: true });
      }
    }
  }
  if (!value) {
    stopReopenTimer();
  }
});

watch(selectedSortOption, (value, previous) => {
  sortArticles();
});

const userGloveSize = computed(() => {
  return user.value?.preferences?.gloveSize; //S, M, L
});

watch(isUserPrefSize, (value, previous) => {
  let size = allPossibleParameters.value?.find(x => x.name == "Velikost");

  if (size) {
    //Selected fixed size, cannot be changed
    size.locked = isUserPrefSize.value;
  }

  if (size && size.options && size.options.length && userGloveSize.value) {
    let option = size.options.find(x => x.name == userGloveSize.value);
    if (option) {
      option.selected = (value == true);

      //This will "loadArticles" on every change
      //onParameterChange(option);

      //Better to first mark all selections, then call "loadArticles" once
      let otherOptions = size.options.filter(x => x.id != option.id);
      for (let other of otherOptions) {
        other.selected = false;
      }
      
      //Apply filter
      try {
        loadArticles();
      }
      catch (err) {
        //dialog(err.message)
      }
    }
  }
});

async function saveChanges(cabinetId, changes) {
  try {
    let result = await cabinetService.updateCabinetStock(cabinetId, changes, { reportEmptyStock });
  }
  catch (err) {
    console.error("saveChanges", err);
    toast(err); //dialog(err);
    throw err;
  }
}

function selectSearchResult(row) {
  if (row.isCategory) {
    selectCategory(row.id);
    searchText.value = "";
  }
  else if (row.isArticle) {
    showModal(row);
  }
}

function stockSum(trays) {
  if (!trays || trays.length == 0) {
    return 0;
  }

  /*
  let sum = 0;
  for (let tray of trays) {
    //TODO: Tray unit pcs or kos
    sum += parseInt(tray.quantity || 0);
  }

  return sum;
  */

  let quantity = trays.filter(t => t.quantity >= 0).map(t => parseInt(t.quantity || 0)).sum() || 0;

  //Vault mode: count "present" as +1
  //quantity += trays.filter(t => t.usage == "present").map(t => 1).sum();

  return quantity;
}

function isVaultItem(trays) {
  if (!trays || trays.length == 0) {
    return false;
  }
  let any = trays.filter(t => t.usage);
  return any.length > 0 ? true : false;
}

/******************************************************************************************
 * Get descriptive tray name, e.g. "Tray 4-36-V1: empty"
 ******************************************************************************************/
function getTrayDescriptiveName(tray) {
  if (!tray) {
    return "(neznano)";
  }

  //Count mode: numerable or take/return
  let mode = tray.countMode && tray.countMode != "default" ? tray.countMode : getCabinetMode(tray.cabinetId);

  //Cabinet name
  let hasManyCabinets = cabinetList.value?.length > 1 && !areLockerCabinets.value; //More than one? Also note that combined lockers are treated as one unit (incremental tray numbers)

  //Prepend cabinet name before tray name in case of satellite cabinets
  //let trayBaseName = cabinetNamePrefix = (hasManyCabinets && tray.cabinetName ? tray.cabinetName + " predal " : "Predal ");

  //Base name
  let trayBaseName = "Predal ";

  let statusSuffix = (tray.quantity > 0 ? tray.quantity + " " + "kos" : "prazno");

  let cabinetIndex = hasManyCabinets ? cabinetList.value.findIndex(x => x.id == tray.cabinetId) : -1;

  let cabinetIndexPrefix = cabinetIndex >= 0 ? (cabinetIndex + 1).toString() + "-" : "";

  //Tray name, e.g. L1
  let trayNamePart = tray.name;

  //Subdivision (insert) name
  let subdivisionPart = "";
  if (tray.subdivision) {
    subdivisionPart = "-" + tray.subdivision.name;
  }

  //Stock
  if (mode == "vault") {
    switch (tray.usage) {
      default:
        statusSuffix = "nedoločeno";
        break;

      case "none":
        statusSuffix = "prazno";
        break;

      case "present":
        statusSuffix = "v predalu";
        break;

      case "taken":
        statusSuffix = "na izposoji";
        break;
    }
  }

  //Build full tray name, e.g. "Tray 4-36-V1: empty" or "Tray 5-12: 1 piece"
  let result = `${trayBaseName}${cabinetIndexPrefix}${trayNamePart}${subdivisionPart}: ${statusSuffix}`;
  return result;
}

/******************************************************************************************
 * Generate unique tray option id combined by cabinet id, tray id and subdivision id
 ******************************************************************************************/
function getTrayOptionId(tray) {
  if (!tray) {
    return "none";
  }
  let result = `${tray.cabinetId}-${tray.trayId}-${tray.subdivisionId}`;
  return result;
}

function hasTrayQuantity(tray) {
  if (!tray) {
    return false;
  }

  let mode = tray.countMode && tray.countMode != "default" ? tray.countMode : getCabinetMode(tray.cabinetId);

  if (mode == "vault") {
    switch (tray.usage) {
      default:
      case "none":
        return null;

      case "present":
        return true;

      case "taken":
        return false;
    }
  }

  return tray.quantity > 0 ? true : false;
}

/******************************************************************************************
 * Get options for drop down when selecting which tray to open
 ******************************************************************************************/
const availableTrayOptions = computed(() => {
  let options = [ ];
  let list = selected.value.trays;
  if (list && list.length) {
    list = sortTrays(list);
    for (let tray of list) {

      //Get tray icon
      let icon = null;
      if (tray.status == "locked") {
        icon = "fa fa-lock text-red-500";
      }
      else {
        let has = hasTrayQuantity(tray);
        icon = has ? "fa fa-check text-green-500" : "fa fa-xmark text-red-500";
        if (has === null) icon = "fa fa-question text-amber-500";
      }

      //Add tray options to select
      options.push({
        cabinetId: tray.cabinetId,
        cabinetName: tray.cabinetName,
        //permissions: tray.permissions,
        status: tray.status,
        //id: tray.name, //TODO: Rename to trayId and use tray.id instead of name
        id: getTrayOptionId(tray), //id for drop down
        name: getTrayDescriptiveName(tray),
        icon: icon,
        tray: tray
      });
    }
  }
  return options;
});

const availableTraySelect = ref(null);

function sortTrays(trays) {
  if (!trays || trays.length == 0) {
    return [ ];
  }

  if (trays.length == 1) {
    return trays;
  }

  return trays.sort((a, b) => {
    if (a.name < b.name) {
      return -1;
    }

    if (a.name > b.name) {
      return +1;
    }

    return 0;
  });
}

const hasParameterSelected = computed(() => {
  if (!selected.value || !selected.value.parameters) {
    return false;
  }

  let keys = Object.keys(selected.value.parameters);
  return keys.length > 0;
});

const parametersSelected = computed(() => {
  if (!selected.value || !selected.value.parameters) {
    return false;
  }

  let list = [ ];

  let keys = Object.keys(selected.value.parameters);
  for (let key of keys) {
    let parameter = allPossibleParameters.value.find(x => x.id == key);
    if (parameter) {
      list.push({
        name: parameter.name,
        value: selected.value.parameters[key]
      });
    }
  }

  return list;
});



const cabinetLocation = ref();
//const hasLocation = computed(() => !!cabinetLocation.value);
const hasLocation = ref(false);
const isLocationView = ref(false);

function showLocation() {
  isLocationView.value = true;
  hasLocation.value = true;
}

function hideLocation() {
  isLocationView.value = false;
  hasLocation.value = false;
}

const isLoadingLocation = ref(false);
const relatedStock = ref([ ]);
const relatedStockIndex = ref(0);
const relatedStockCount = computed(() => relatedStock.value?.length);

const relatedStockCurrent = computed(() => relatedStock.value ? relatedStock.value[relatedStockIndex.value] : null);

function nextRelatedStock() {
  if (relatedStockIndex.value + 1 < relatedStockCount.value) {
    relatedStockIndex.value++;
    isMapView.value = true;
  }
}

function prevRelatedStock() {
  if (relatedStockIndex.value - 1 >= 0) {
    relatedStockIndex.value--;
    isMapView.value = true;
  }
}

async function showRelatedStock(item) {
  relatedStock.value = item.relatedStockGroup || item.relatedStock;
  isLoadingLocation.value = true;
  isMapView.value = true;
  showLocation();

  try {
    let list = [ ];

    //TODO: Cache other cabinets
    for (let rs of item.relatedStock) {
      let cabinet = await cabinetService.getCabinet(rs.cabinetId);

      //Override location and image from it's master cabinet
      if (cabinet.parentId /* && !cabinet.image?.id */) {
        let master = await cabinetService.getCabinet(cabinet.parentId);
        //console.log("[img] cabinet master", master?.name, master?.image);
        if (master?.image?.id) {
          cabinet.image = { ...master.image };
        }
        if (master?.location) {
          cabinet.location = { ...master.location };
        }
      }

      //Load location map and point (location marker)
      if (cabinet.location?.locationId) {
        rs.location = cabinet.location; //locationId, point

        let organization = await organizationService.getOrganization(cabinet.orgId);
        let location = organization.locations.find(x => x.id == cabinet.location.locationId);
        if (location) {
          rs.location = {
            locationId: location.id,
            name: location.name,
            point: cabinet.location.point,
            imageBase64: location.imageBase64,
          }
        }
      }
      else {
        rs.location = null;
      }

      rs.cabinet = cabinet;
      list.push(rs);
    }

    relatedStockIndex.value = 0;
    relatedStock.value = list;
  }
  catch (err) {
    console.error("[location]", err);
  }
  finally {
    isLoadingLocation.value = false;
  }
}

const isMapView = ref(true);

function showCabinetMap() {
  isMapView.value = true;
}

function showCabinetPicture() {
  isMapView.value = false;
}

function showRelatedArticles() {
  isRelatedArticlesView.value = true;
}

function hideRelatedArticles() {
  isRelatedArticlesView.value = false;
}

const isRelatedArticlesView = ref(false);
const hasRelatedArticles = computed(() => {
  return selected.value && selected.value.related && selected.value.related.length > 0;
});

function mapRelated(related) {
  let list = [ ];
  /*
  for (let id of related) {
    let item = allPossibleArticles.value.find(x => x.id == id);
    if (item) {
      list.push(item);
    }
  }
  */
  list = allPossibleArticles.value.filter(x => related.includes(x.id));
  return list;
}

function referencingDone() {
  isNotRef.value = false;
}

function getUserDisplayName(userId) {
  if (!userId) {
    return null;
  }
  if (userId == "_none") {
    return "Ni podatka";
  }
  if (users.value?.length) {
    let user = users.value.find(x => x.id == userId);
    return user.displayName || user.username || user.email || user.id;
  }
  return null;
}

const canReturnArticle = computed(() => {
  if (isCabinetBorrowMode.value) {
    let article = selected.value;
    let tray = selectedTrayItem.value;
    if (tray?.usage == "taken") {
      // let userId = authStore.userId.value;
      // return tray.userId == userId ? true : false;
    }
    return true;
  }
  else {
    return true;
  }
});

function verifyTrayPermissions(permissions, userId) {
  let hasAccess = false;

  //No restrictions if no permissions set
  if (!permissions || permissions.length == 0) {
    hasAccess = true;
  }
  else {
    //Check permissions for this specific user
    hasAccess = userId && permissions.find(x => x.userId && x.userId == userId) ? true : false;

    //Check permissions for the group
    if (!hasAccess) {
      let userGroups = authStore.user.value?.groups || [];
      console.log("[debug] userGroups", userGroups);
      let allowedGroups = permissions.filter(x => x.userGroupId).map(x => x.userGroupId);
      console.log("[debug] allowedGroups", allowedGroups);
      if (userGroups.length > 0 && allowedGroups.length > 0) {
        //Must be the group where user is in
        for (let allowedGroup of allowedGroups) {
          if (userGroups.findIndex(x => x == allowedGroup) >= 0) {
            console.log("[debug] hasAccess", true);
            hasAccess = true;
            break;
          }
        }
      }
    }
  }

  return hasAccess;
}

watch(selectedTrayItem, (tray) => {
  let hasAccess = false;

  //Define permissions
  if (tray) {
    let userId = currentUserId.value;

    console.log("[catalog] define permissions selectedTrayItem", selectedTrayItem.value, tray);

    //Verify permissions on tray level
    hasAccess = verifyTrayPermissions(tray.permissions, userId);
    console.log("[catalog] has permissions for tray", hasAccess);

    //Also check permissions for selected insert bin (subdivision)
    if (hasAccess && tray.subdivision?.permissions?.length) {
      hasAccess = verifyTrayPermissions(tray.subdivision.permissions, userId);
      console.log("[catalog] has permissions for insert", hasAccess);
    }

    //Check if taken by another user - only the same user (or admin) can return item
    if (tray.usage == "taken" && tray.userId != userId) {
      hasAccess = false;
      isTakenByOtherUser.value = true;
    }
    else {
      isTakenByOtherUser.value = false;
    }

    //Admin has full permissions
    if (isAdmin.value) {
      hasAccess = true;
    }

    //Take or return option
    selectedTrayUsage.value = tray.usage; //Preselected
    //selectedTrayUsage.value = null; //Force user to choose before confirm

    //Clear editable comment
    stockComment.value = tray.comment || "";
  }

  canOpenTrayStatus.value = (tray && tray.status != "locked");
  canOpenTrayPermissions.value = hasAccess;
});

const canApplyStock = computed(() => {
  if (isCabinetBorrowMode.value) {
    return selectedTrayUsage.value != null ? true : false;
  }
  return true;
});

const allowPrivateCloseChoice = ref(true); //TODO: Settings
const isPrivateCloseChoice = ref(false);

async function closePrivateTray(status) {
  try {
    // if (!cabinetId.value) {
    //   throw new Error("Id omare ni definiran!");
    // }

    //console.log("closePrivateTray", status);

    let tray = selectedTrayItem.value;
    let result = await cabinetService.updateCabinetStock(tray.cabinetId, {
      trays: [
        {
          trayId: tray.trayId,
          name: tray.name, //Tray name, e.g. A1
          status: status, //occupied, active (free)
          privateUserId: status == "occupied" ? currentUserId.value : null,
          occupiedTime: status == "occupied" ? utc() : null
        }
      ]
    }, { reportEmptyStock });

    //console.log("selectedTrayItem", selectedTrayItem.value);

    selectedTrayItem.value.status = status;
    isPrivateCloseChoice.value = false;

    // if (status == "active") {
    //   userPrivateTray?.status == 'occupied'
    //   userPrivateTray.value = null;
    // }
    if (userPrivateTray.value) {
      userPrivateTray.value.status = status; //Apply status
    }

    /*
    //Update current state on UI
    let trayName = selectedTrayName.value;
    console.log("[catalog] selectedTrayItem", selectedTrayItem.value);
    console.log("[catalog] trayName", trayName);
    let trayItem = selected.value?.trays?.find(x => x.name == trayName);
    console.log("[catalog] trayItem", trayItem);
    if (trayItem) {
      //console.log("found local tray to update comment", userId, stockComment.value)
      //TODO: The following states are based on article in this tray; not the whole tray per se
      // trayItem.comment = stockComment.value;
      // trayItem.userId = userId;
      // trayItem.userTime = now;
    }
    */
  }
  catch (err) {
    console.error("[catalog] closePrivateTray", err);
    toast(err); //dialog(err);
  }
}

function noImage(selected) {
  if (!selected) {
    return;
  }
  selected.originalImageUrl = null;
  selected.largeImageUrl = null;
  selected.mediumImageUrl = null;
  selected.smallImageUrl = null;
  selected.thumbnailUrl = null;
  /*
  originalImageId: x.originalImageId,
  largeImagePath: x.largeImagePath,
  largeImageUrl: resolveImageUrl(x.largeImagePath),
  mediumImagePath: x.mediumImagePath,
  mediumImageUrl: resolveImageUrl(x.mediumImagePath),
  smallImagePath: x.smallImagePath,
  smallImageUrl: resolveImageUrl(x.smallImagePath),
  thumbnailPath: x.thumbnailPath,
  thumbnailUrl: resolveImageUrl(x.thumbnailPath),
  */
}
</script>

<template>
  <div class="screen-container">

    <!-- Error message -->
    <div v-if="error">
      <pre class="p-5 mt-4 mb-10 font-sans border rounded-md bg-red-100 text-red-700 text-sm dark:bg-red-800 dark:border-red-900 dark:text-white"><i class="fa fa-xmark-circle mr-4"></i>{{ error.message }}</pre>
    </div>

    <!-- Loading animation -->
    <div class="grid place-items-center w-full h-[calc(100vh-120px)] " v-else-if="isLoading && !isNotRef">
      <Spinner size="12"></Spinner>
    </div>

    <div class="w-full h-full" v-else-if="!isLoading && isNotRef && !error">
      <!-- <ReferencePosition @done="referencingDone" /> -->
    </div>

    <!-- Header -->
    <div class="w-full mx-auto flex items-center justify-between" v-if="!isLoading && !isNotRef && (!isCabinetBorrowMode || !isSingleArticle) && !isPrivateVisible && !isUserItemsVisible&& !error">

      <div class="text-lg font-bold dark:text-white">{{ $t('menu.catalog') }}</div>

      <div class="flex flex-row justify-items-strech space-x-2">

        <!-- Search box -->
        <AutocompleteTextBox v-model="searchText" :placeholder="$t('catalog.search_placeholder')" :loading="false" :auto-search="true" :resizable="false" :suggestions="suggestions" @text-changed="onSearchTextChanged" @search-request="onSearchRequest"></AutocompleteTextBox>

        <!-- Display mode -->
        <Button type="secondary" v-if="displayMode == 'list'" @click="setDisplayMode('grid')" class="w-14"><i class="fa fa-list"></i></Button>
        <Button type="secondary" v-if="displayMode == 'grid'" @click="setDisplayMode('list')" class="w-14"><i class="fa fa-grip"></i></Button>

        <!-- Filter icon -->
        <Button type="secondary" v-if="!isFilterVisible" @click="() => isFilterVisible = true" class="w-14"><i class="fa fa-filter"></i></Button>
        <Button type="primary" v-if="isFilterVisible" @click="() => isFilterVisible = false" class="w-14"><i class="fa fa-filter"></i></Button>

        <!-- User items -->
        <Button type="secondary" v-if="!isUserItemsVisible" @click="showUserItems()" class="w-14"><i class="fa fa-user"></i></Button>

      </div>
    </div>

    <!-- Article list -->
    <div v-if="!isLoading && !isNotRef && !error">



      <!-- Categories -->
      <div v-if="isCategorySectionVisible && !isSearchFilter && !isPrivateVisible && !isUserItemsVisible">

        <!-- Breadcrumbs -->
        <div class="mt-6">
          <Breadcrumbs class="text-lg font-medium" :items="categoryBreadcrumbs" @click="(item) => selectCategory(item.id)"></Breadcrumbs>
        </div>

        <!-- Category button container -->
        <div class="relative grid sm:grid-cols-2 lg:grid-cols-3 gap-4 w-full text-center mt-2 mb-2">

          <!-- Special category buttons for navigation -->
          <div class="grid grid-cols-2" v-if="!isRootCategory">

            <!-- Back to the root -->
            <a href="#" @click.prevent="selectCategory('_root')" class="p-0 mr-2 grid category-button special-button">
              <div class="grid place-items-center" style="height: 120px;">
                <div>
                  <h3 class="mt-2 category-button-text"><i class="fa-solid fa-house"></i></h3>
                  <h5 class="mt-4 mb-2 category-button-hint"><i class="fa fa-angles-left"></i> Na začetek</h5>
                </div>
              </div>
            </a>

            <!-- One step back -->
            <a href="#" @click.prevent="selectCategory(parentCategoryId)" class="p-0 ml-2 grid category-button special-button">
              <div class="grid place-items-center" style="height: 120px;">
                <div>
                  <h3 class="mt-2 category-button-text"><i class="fa-solid fa-arrow-turn-up flip-x"></i></h3>
                  <h5 class="mt-4 mb-2 category-button-hint"><i class="fa fa-angles-left"></i> Nazaj</h5>
                </div>
              </div>
            </a>

          </div>

          <!-- Special category button: private trays -->
          <div class="grid" v-if="isRootCategory && hasPrivateTrays">

            <!-- Private trays -->
            <a href="#" @click.prevent="showPrivateTrays()" class="p-0 grid category-button special-button">
              <div class="grid place-items-center" style="height: 120px;">
                <div>
                  <h3 class="mt-2 category-button-text"><i class="fa-solid fa-user-shield"></i></h3>
                  <h5 class="mt-4 mb-2 category-button-hint">Osebni predali</h5>
                </div>
              </div>
            </a>

          </div>

          <!-- Category buttons -->
          <template v-for="(item, i) in categoriesFiltered">
            <!-- class row-category -->
            <a href="#" v-if="categoryDesign == 1" @click.prevent="selectCategory(item.id)" class="p-0 grid category-button"
              :class="{ 'grid-cols-1': !item.smallImageUrl, 'grid-cols-2': item.smallImageUrl, 'category-button-background': isDevelopmentMode }">
              <!-- Trick to test if image loads -->
              <img alt="" :src="item.smallImageUrl" @error="noImage(item)" class="w-1 h-1 fixed load-image-test" style="top: -3000px; left: -3000px;" />
              <div class="w-full" v-if="item.smallImageUrl">
                <div class="w-full" style="height: 120px; padding: 10px;">
                  <div class="category-image-placeholder w-full" style="height: 100px;" :style="'background: url(\'' + item.smallImageUrl + '\'); background-repeat:no-repeat; background-position: center center; background-size: contain;'"></div>
                </div>
              </div>
              <!-- <div class="w-full text-center aspect-square grid place-items-center p-3 rounded-md" v-if="item.smallImageUrl">
                <img :src="item.smallImageUrl" alt="" class="rounded-md" style="max-height: 120px;" />
              </div> -->
              <div class="grid place-items-center" style="height: 120px;">
                <div>
                  <h3 class="mt-2 px-2 category-button-text-md" v-if="item.name.length > 30">{{ item.name }}</h3>
                  <h3 class="mt-2 px-2 category-button-text" v-else>{{ item.name }}</h3>
                  <h5 class="mt-4 mb-2 category-button-hint" v-if="!item.smallImageUrl">Prikaži artikle <i class="fa fa-angles-right"></i></h5>
                </div>
              </div>
            </a>

            <!-- Design 2 -->
            <a href="#" v-else-if="categoryDesign == 2" @click.prevent="selectCategory(item.id)" class="p-0 grid category-button relative">
              <div class="w-full absolute top-0 left-0 rounded-lg" v-if="item.smallImageUrl">
                <div class="w-full relative rounded-lg" style="height: 120px;">
                  <div class="category-image-placeholder rounded-lg w-full" style="height: 120px;" :style="'background: url(\'' + item.smallImageUrl + '\'); background-repeat:no-repeat; background-position: center center; background-size: cover;'"></div>
                  <div class="dark:bg-blue-900 dark:border-blue-700 dark:hover:border-blue-500 dark:hover:bg-blue-800 rounded-lg absolute right-0 top-0 w-full" style="height: 120px;
                  background: linear-gradient(135deg, rgba(35,56,118,0.65) 0%, rgba(35,56,118,1) 70%);
                  ">
                  </div>
                </div>
              </div>
              <div class="grid place-items-center relative z-100" style="height: 120px;">
                <div>
                  <h3 class="mt-2 px-2 category-button-text-md" v-if="item.name.length > 30">{{ item.name }}</h3>
                  <h3 class="mt-2 px-2 category-button-text" v-else>{{ item.name }}</h3>
                  <h5 class="mt-4 mb-2 category-button-hint" v-if="!item.smallImageUrl">Prikaži artikle <i class="fa fa-angles-right"></i></h5>
                </div>
              </div>
            </a>

            <!-- Design 3 -->
            <a href="#" v-else @click.prevent="selectCategory(item.id)" class="p-0 grid category-button relative w-full">
              <div class="w-full absolute top-0 left-0 rounded-lg w-[65%]" v-if="item.smallImageUrl">
                <div class="w-full relative rounded-lg" style="height: 120px;">
                  <div class="category-image-placeholder rounded-lg w-full" style="height: 120px;" :style="'background: url(\'' + item.smallImageUrl + '\'); background-repeat:no-repeat; background-position: center center; background-size: cover;'"></div>
                  <div class="dark:bg-blue-900 dark:border-blue-700 dark:hover:border-blue-500 dark:hover:bg-blue-800 absolute right-0 top-0 w-[100%]" style="height: 120px;
                  background: linear-gradient(90deg, rgba(35,56,118,0) 0%, rgba(35,56,118,0.8) 70%, rgba(35,56,118,1) 100%);
                  ">
                  </div>
                </div>
              </div>
              <div class="grid place-items-center relative top-0 right-0 z-100 w-full" style="height: 120px;">
              </div>
              <div class="grid place-items-center absolute top-0 right-0 z-100 w-[50%]" style="height: 120px;">
                <div>
                  <h3 class="mt-2 px-2 category-button-text-md" v-if="item.name.length > 30">{{ item.name }}</h3>
                  <h3 class="mt-2 px-2 category-button-text" v-else>{{ item.name }}</h3>
                  <h5 class="mt-4 mb-2 category-button-hint" v-if="!item.smallImageUrl">Prikaži artikle <i class="fa fa-angles-right"></i></h5>
                </div>
              </div>
            </a>

          </template>

        </div>

      </div>



      <!-- Parameters as buttons -->
      <template v-for="(parameter, pi) in parameters" v-if="isParameterSectionVisible">
        <div v-key="parameter.id" v-if="parameter.options && parameter.options.length">
          <div class="mt-8"><span class="text-lg font-medium">{{ parameter.name }}</span></div>
          <div class="relative grid sm:grid-cols-4 lg:grid-cols-12 gap-2 w-full text-center mt-2 mb-2">
            <a href="#" v-for="(option, i) in parameter.options" @click.prevent="() => onParameterChange(option)" class="parameter-option truncate" :class="{ 'option-selected': !!option.selected }">
              {{ option.name }}
            </a>
          </div>
        </div>
      </template>



      <!-- Articles title -->
      <div class="mb-8" v-if="!isSearchFilter && isArticleSectionVisible && !isPrivateVisible && !isUserItemsVisible">
        <div class="mt-8 dark:text-gray-400">
          <span class="text-lg font-medium">Artikli</span>
          <span class="pl-2 relative" style="bottom: 1px" v-if="rowsFiltered?.length > 0">({{ rowsFiltered.length }})</span>
        </div>

        <!-- Display mode: grid -->
        <div class="relative grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 w-full mt-2 mb-32" v-if="displayMode == 'grid' && rowsFiltered?.length > 0">
          <a href="#" v-for="(row, i) in rowsFiltered" @click.prevent="showModal(row)" class="p-6 bg-white border border-gray-200 rounded-lg shadow hover:border-blue-600 dark:bg-gray-800 dark:border-gray-700 dark:hover:bg-gray-700">
            <!-- Article image -->
            <div class="mb-4">
              <div class="w-full text-center aspect-square grid place-items-center dark:bg-white dark:p-3 dark:rounded-md" v-if="row.smallImageUrl || row.thumbnailUrl">
                <img :src="row.smallImageUrl || row.thumbnailUrl" alt="" class="rounded" style="max-height: 120px;" />
              </div>
              <div class="w-full text-center aspect-square grid place-items-center dark:bg-white dark:p-3 dark:rounded-md" v-else-if="allowImageFallback && nearestParentCategorySmallImageUrl">
                <img :src="nearestParentCategorySmallImageUrl" alt="" class="rounded" style="max-height: 120px;" />
              </div>
              <div class="grid place-items-center text-gray-200 dark:text-gray-900 text-center w-full aspect-square" :class="{ 'text-blue-200': selectedItemId == row.id && promptDeleteItemId != row.id, 'text-red-300': promptDeleteItemId == row.id }" style="font-size: 108px;" v-else>
                <i class="fa fa-image"></i>
              </div>
            </div>

            <!-- Article name and SKU -->
            <h3 class="text-sm tracking-tight text-blue-700 dark:text-blue-300 mb-1" v-if="row.sku">{{ row.sku }}</h3>
            <h5 class="text-sm tracking-tight text-gray-900 dark:text-white">{{ row.name }}</h5>
          </a>
        </div>

        <!-- Display mode: list -->
        <div class="relative grid grid-cols-1 gap-4 w-full mt-2 mb-32" v-else-if="displayMode == 'list' && rowsFiltered?.length > 0">
          <a href="#" v-for="(row, i) in rowsFiltered" @click.prevent="showModal(row)" class="py-4 flex bg-white border border-gray-200 rounded-lg shadow hover:border-blue-600 dark:bg-gray-800 dark:border-gray-700 dark:hover:bg-gray-700">

            <!-- Article image -->
            <div class="grid place-items-center text-center mx-4 dark:bg-white dark:p-1 dark:rounded-md" style="width: 50px; height: 50px" v-if="row.thumbnailUrl">
              <img :src="row.thumbnailUrl" alt="" class="w-full max-h-[50px] dark:max-h-[40px]" style="object-fit: contain; object-position: center;" />
            </div>
            <!-- <div class="grid place-items-center text-center w-24 mx-4" style="width: 50px; height: 50px" v-else-if="allowImageFallback && currentCategoryImageUrl">
              <img :src="currentCategoryImageUrl" alt="" style="max-width: 50px; max-height: 50px;" />
            </div> -->
            <div class="grid place-items-center text-center mx-4" style="width: 50px; height: 50px" v-else-if="allowImageFallback && nearestParentCategorySmallImageUrl">
              <img :src="nearestParentCategorySmallImageUrl" alt="" style="max-width: 50px; height: 50px;" />
            </div>
            <div class="grid place-items-center text-gray-200 dark:text-gray-900 text-center w-24 mx-4" :class="{ 'text-blue-200': selectedItemId == row.id && promptDeleteItemId != row.id, 'text-red-300': promptDeleteItemId == row.id }" style="font-size: 48px; width: 50px; height: 50px;" v-else>
              <i class="fa fa-image"></i>
            </div>

            <!-- Article name and SKU -->
            <div class="w-full pr-5 flex flex-row justify-between items-center gap-2">
              <div>
                <h5 class="text-md tracking-tight text-gray-900 dark:text-white" v-if="row.name">{{ row.name }}</h5>
                <h3 class="mt-0 text-slate-400" v-if="row.sku">{{ row.sku }}</h3>
              </div>
              <div v-if="!row.isVaultItem">
                <h5 class="text-gray-500">{{ row.totalQuantity > 0 ? row.totalQuantity : 0 }}&nbsp;kos</h5>
                <h3 v-if="row.sku">&nbsp;</h3>
              </div>
            </div>

          </a>
        </div>

        <!-- No article found -->
        <div class="relative sm:grid sm:grid-cols-1 gap-4 w-full mt-2 mb-32" v-else>
          <span class="text-slate-500">(brez artiklov)</span>
        </div>

      </div>


      <!-- Private trays to select -->
      <div class="mb-8 h-[800px]" v-else-if="!isSearchFilter && isPrivateVisible">

        <!-- Back button navigation -->
        <div class="mt-0 dark:text-gray-400">
          <Button type="primary" @click="selectCategory('_root')"><i class="fa fa-arrow-left"></i></Button>
          <span class="ml-4 text-lg font-normal">Nazaj na katalog</span>
        </div>

        <!-- <div class="mt-8 dark:text-gray-400">
          <span class="text-lg font-medium">Osebni predali</span>
          <span class="pl-2 relative" style="bottom: 1px" v-if="!autoSelectPrivateTray && privateTraysFiltered?.length">({{ privateTraysFiltered.length }})</span>
        </div> -->

        <!-- Display mode: list -->
        <div class="relative grid grid-cols-1 gap-4 w-full mt-2 mb-32" v-if="!autoSelectPrivateTray && privateTraysFiltered?.length > 0">
          <a href="#" v-for="(row, i) in privateTraysFiltered" @click.prevent="showPrivateModal(row)" class="py-4 flex bg-white border border-gray-200 rounded-lg shadow hover:border-blue-600 dark:bg-gray-800 dark:border-gray-700 dark:hover:bg-gray-700">

            <!-- Status icon -->
            <div class="grid place-items-center text-gray-200 dark:text-gray-900 text-center w-24 mx-4"
              :class="{
                'text-gray-300': row.status == 'locked',
                'text-red-500': row.status == 'occupied' && row.userId != currentUserId,
                'text-blue-500': row.status == 'occupied' && row.userId == currentUserId,
                'text-green-500': row.status == 'active'
              }" style="font-size: 32px; width: 50px; height: 50px;">
              <i class="fa fa-user" :class="{ 'text-blue-500': row.status == 'occupied' && row.userId == currentUserId }" v-if="row.status == 'occupied' || row.userId == currentUserId"></i>
              <i class="fa fa-lock" v-else-if="row.status == 'locked'"></i>
              <i class="fa fa-inbox" v-else-if="row.status == 'active'"></i>
            </div>

            <!-- Tray name -->
            <div class="w-full pr-5 flex flex-row justify-between items-center gap-2">
              <div>
                <h5 class="text-md tracking-tight text-gray-900 dark:text-white" v-if="row.name">{{ row.name }}</h5>
                <h3 class="mt-0 text-blue-500" v-if="row.status == 'occupied' && row.userId == currentUserId">Moj predal</h3>
                <h3 class="mt-0 text-red-500" v-else-if="row.status == 'occupied'">Zaseden</h3>
                <h3 class="mt-0 text-gray-400" v-else-if="row.status == 'locked'">Zaklenjen</h3>
                <h3 class="mt-0 text-green-500" v-else-if="row.status == 'active'">Prost predal</h3>
              </div>
            </div>

          </a>
        </div>

        <!-- Display mode: auto select -->
        <div class="relative grid grid-cols-1 gap-4 w-full h-full mt-2 mb-32" v-if="autoSelectPrivateTray && privateTraysFiltered?.length > 0">

          <div class="grid place-items-center w-full h-full">
            <div class="text-center">
              <div class="grid place-items-center my-5" style="height: 64px;">
                <i class="fa fa-user text-blue-600" style="font-size: 64px;" v-if="!isReferencing && isOpen && !isPrevOpen && selectedTrayItem?.status == 'occupied'"></i>
                <i class="fa fa-user text-green-600" style="font-size: 64px;" v-else-if="!isReferencing && isOpen && !isPrevOpen && selectedTrayItem?.status == 'active'"></i>
                <Spinner size="12" v-else-if="isReferencing || isOpening || isClosing" />
                <!-- <i class="fa fa-inbox text-blue-600" style="font-size: 32px;"></i> -->
                <i class="fa fa-user-shield text-green-600 opacity-30" style="font-size: 64px;" v-if="!isReferencing && !isOpen && !isOpening && !isClosing && userPrivateTray?.status == 'occupied'"></i>
              </div>
              <div class="text-2xl">
                <span v-if="isReferencing">Postavljam omaro v referenco...</span>
                <span v-else-if="!isReferencing && !isOpen && isOpening">Odpiram predal <span class="font-semibold">{{ userPrivateTrayName }}</span>...</span>
                <span v-else-if="!isReferencing && isOpen && !isPrevOpen && selectedTrayItem?.status == 'occupied'">
                  <span>Odprt predal <span class="font-semibold">{{ userPrivateTrayName }}</span> je namenjen vam.</span><br />
                  <small class="text-sm">Zapri predal za nadaljevanje</small>
                </span>
                <span v-else-if="!isReferencing && isOpen && !isPrevOpen && selectedTrayItem?.status == 'active'">
                  <span>Odprt predal <span class="font-semibold">{{ userPrivateTrayName }}</span> je prost.</span><br />
                  <small class="text-sm">Zapri predal za nadaljevanje</small>
                </span>
                <span v-else-if="!isReferencing && isPrevOpen && !isOpening"><i class="fa fa-triangle-exclamation text-amber-500"></i>Za nadaljevanje najprej zapri prejšnji predal.</span>
                <span v-else-if="!isReferencing && !isOpen && !isOpening && userPrivateTray?.status == 'occupied'">V predalu <span class="font-semibold">{{ userPrivateTrayName }}</span> je vaša vsebina.</span>
                <span v-else-if="!isReferencing && !isOpen && !isOpening && availablePrivateTraysCount == 0">Noben predal ni na voljo.</span>
                <!-- <span v-else-if="!isReferencing && !isOpen && !isOpening && availablePrivateTraysCount > 0">Na voljo {{ availablePrivateTraysCount }} prosti predal.</span> -->
                <span v-else-if="!isReferencing && !isOpen && !isOpening && availablePrivateTraysCount > 0">Na voljo so prosti predali.</span>
              </div>
              <div class="text-xl my-5" style="min-height: 40px;">
                <!-- Open button -->
                <Button size="xl" :type="!isReferencing && !isOpen && !isOpening && userPrivateTray?.status == 'occupied' ? 'success' : 'primary'" @click="openPrivateTray()" v-if="!isPrivateCloseChoice && canOpenPrivateTray">Odpri</Button>
                
                <!-- Message to close tray -->
                <span v-if="!isReferencing && isOpen && !isOpening && !isPrivateCloseChoice">Za nadaljevanje zapri predal.</span>
                
                <!-- Choice to select private tray status -->
                <div class="mt-16" v-if="!isReferencing && !isOpening && isPrivateCloseChoice">
                  <span class="text-xl">{{ $t('catalog.tray_state_select') }}</span>
                  <div class="grid grid-cols-2 mt-4">
                    <div class="text-right pr-8 pl-20">
                      <Button size="xl" type="warning" @click="closePrivateTray('occupied')">Zaseden</Button>
                      <p class="text-md mt-4 text-gray-500">{{ $t('catalog.keep_tray_option') }}</p>
                    </div>
                    <div class="text-left pl-8 pr-16 border-l border-gray-300">
                      <Button size="xl" type="success" @click="closePrivateTray('active')">Prost</Button>
                      <p class="text-md mt-4 text-gray-500">{{ $t('catalog.empty_tray_option') }}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>

        <!-- No article found -->
        <div class="relative sm:grid sm:grid-cols-1 gap-4 w-full mt-2 mb-32" v-if="!privateTraysFiltered || privateTraysFiltered.length == 0">
          <span class="text-slate-500">Ni osebnih predalov na voljo.</span>
        </div>

      </div>




      <!-- User's items list -->
      <div class="mb-8 pb-8 h-[800px]" v-else-if="!isSearchFilter && isUserItemsVisible">

        <!-- Back button navigation -->
        <div class="mt-0 dark:text-gray-400" v-if="!isAutoUserItems">
          <Button type="primary" @click="selectCategory('_root')"><i class="fa fa-arrow-left"></i></Button>
          <span class="ml-4 text-lg font-normal">Nazaj na katalog</span>
        </div>

        <div class="dark:text-gray-400" :class="{ 'mt-8': !isAutoUserItems }">
          <span class="text-lg font-medium">Uporabnikovi predali in izposojeni artikli</span>
          <span class="pl-2 relative" style="bottom: 1px" v-if="!autoSelectPrivateTray && privateTraysFiltered?.length">({{ privateTraysFiltered.length }})</span>
          <!-- <div class="text-md">Seznam trenutno zasedenih osebnih predalov in izposojenih artiklov s strani uporabnika</div> -->
        </div>

        <!-- Display mode: list -->
        <div class="relative grid grid-cols-1 gap-4 w-full mt-2 mb-2" v-if="userItems?.length > 0">

          <!-- List private trays -->
          <div v-if="userItems.filter(x => x.type == 'private').length" class="flex flex-col gap-2">
            <div class="text-md">Seznam osebnih predalov v uporabi</div>
            <a href="#" v-for="(row, i) in userItems.filter(x => x.type == 'private')" @click.prevent="showUserItemModal(row)" class="py-4 flex bg-white border border-gray-200 rounded-lg shadow hover:border-blue-600 dark:bg-gray-800 dark:border-gray-700 dark:hover:bg-gray-700">
              <template v-if="row.type == 'private' && row.privateTray">

                <!-- Status icon -->
                <div class="grid place-items-center text-gray-200 dark:text-gray-400 text-center w-24 mx-4"
                  :class="{
                    'text-gray-300 dark:text-gray-400': row.privateTray.status == 'locked',
                    'text-red-500 dark:text-red-600': row.privateTray.status == 'occupied' && row.privateTray.userId != currentUserId,
                    'text-blue-500 dark:text-blue-500': row.privateTray.status == 'occupied' && row.privateTray.userId == currentUserId,
                    'text-green-500 dark:text-green-500': row.privateTray.status == 'active'
                  }" style="font-size: 32px; width: 50px; height: 50px;">
                  <i class="fa fa-user" :class="{ 'text-blue-500 dark:text-blue-500': row.privateTray.status == 'occupied' && row.privateTray.userId == currentUserId }" v-if="row.privateTray.status == 'occupied' || row.privateTray.userId == currentUserId"></i>
                  <i class="fa fa-lock" v-else-if="row.privateTray.status == 'locked'"></i>
                  <i class="fa fa-inbox" v-else-if="row.privateTray.status == 'active'"></i>
                </div>

                <!-- Tray name -->
                <div class="w-full pr-5 flex flex-row justify-between items-center gap-2">
                  <div>
                    <h5 class="text-md tracking-tight text-gray-900 dark:text-white" v-if="row.privateTray.name">Predal <strong>{{ row.privateTray.name }}</strong> <span v-if="row.privateTray.cabinetName">na omari <strong>{{ row.privateTray.cabinetName }}</strong></span></h5>
                    <h3 class="mt-0 text-blue-500" v-if="row.privateTray.status == 'occupied' && row.privateTray.userId == currentUserId">Osebni predal</h3>
                  </div>
                </div>

              </template>
            </a>
          </div>

          <!-- List borrowed articles -->
          <div v-if="userItems.filter(x => x.type == 'article').length" class="flex flex-col gap-2">
            <div class="text-md">Seznam izposojenih artiklov</div>
            <a href="#" v-for="(row, i) in userItems.filter(x => x.type == 'article')" @click.prevent="showUserItemModal(row)" class="py-4 flex bg-white border border-gray-200 rounded-lg shadow hover:border-blue-600 dark:bg-gray-800 dark:border-gray-700 dark:hover:bg-gray-700">
              <template v-if="row.type == 'article' && row.articleItem">

                <!-- Article image -->
                <div class="grid place-items-center text-center w-[50px] aspect-square mx-4 dark:bg-white dark:p-1 dark:rounded-md" style="max-height: 50px" v-if="row.articleItem.thumbnailUrl">
                  <img :src="row.articleItem.thumbnailUrl" alt="" class="w-full aspect-square" style="max-width: 50px;" />
                </div>
                <!-- <div class="grid place-items-center text-center w-24 mx-4" style="width: 50px; height: 50px" v-else-if="allowImageFallback && currentCategoryImageUrl">
                  <img :src="currentCategoryImageUrl" alt="" style="max-width: 50px; max-height: 50px;" />
                </div> -->
                <!-- <div class="grid place-items-center text-center w-24 mx-4" style="width: 50px; height: 50px" v-else-if="allowImageFallback && nearestParentCategorySmallImageUrl">
                  <img :src="nearestParentCategorySmallImageUrl" alt="" style="max-width: 50px; max-height: 50px;" />
                </div> -->
                <div class="grid place-items-center text-gray-200 dark:text-gray-900 text-center w-[50px] aspect-square mx-4" :class="{ 'text-blue-200': selectedItemId == row.articleItem.id }" style="font-size: 48px; width: 50px; height: 50px;" v-else>
                  <i class="fa fa-image"></i>
                </div>

                <!-- Article name and SKU -->
                <div class="w-full pr-5 flex flex-row justify-between items-center gap-2">
                  <div>
                    <h5 class="text-md tracking-tight text-gray-900 dark:text-white" v-if="row.articleItem.name">{{ row.articleItem.name }}</h5>
                    <h3 class="mt-0 text-slate-400" v-if="row.articleItem.sku">{{ row.articleItem.sku }}</h3>
                  </div>
                  <!-- <div v-if="row.articleItem.totalQuantity > 0">
                    <h5 class="text-gray-500">{{ row.articleItem.totalQuantity }}&nbsp;kos</h5>
                    <h3 v-if="row.articleItem.sku">&nbsp;</h3>
                  </div> -->
                </div>

                <!-- Tray name -->
                <!-- <div class="w-full pr-5 flex flex-row justify-between items-center gap-2">
                  <div>
                    <h5 class="text-md tracking-tight text-gray-900 dark:text-white" v-if="row.articleItem.name">{{ row.articleItem.name }}</h5>
                    <h3 class="mt-0 text-blue-500" v-if="row.articleItem.status == 'occupied' && row.userId == currentUserId">Osebni predal</h3>
                  </div>
                </div> -->

              </template>
            </a>
          </div>

          <!-- List articles for transfer -->
          <div v-if="userItems.filter(x => x.type == 'transfer').length" class="flex flex-col gap-2">
            <div class="text-md">Seznam artiklov za prenos</div>
            <a href="#" v-for="(row, i) in userItems.filter(x => x.type == 'transfer')" @click.prevent="showUserItemModal(row)" class="py-4 flex bg-white border border-gray-200 rounded-lg shadow hover:border-blue-600 dark:bg-gray-800 dark:border-gray-700 dark:hover:bg-gray-700">
              <template v-if="row.type == 'transfer' && row.articleItem">

                <!-- Article image -->
                <div class="grid place-items-center text-center w-[50px] aspect-square mx-4 dark:bg-white dark:p-1 dark:rounded-md" style="max-height: 50px" v-if="row.articleItem.thumbnailUrl">
                  <img :src="row.articleItem.thumbnailUrl" alt="" class="w-full aspect-square" style="max-width: 50px;" />
                </div>
                <!-- <div class="grid place-items-center text-center w-24 mx-4" style="width: 50px; height: 50px" v-else-if="allowImageFallback && currentCategoryImageUrl">
                  <img :src="currentCategoryImageUrl" alt="" style="max-width: 50px; max-height: 50px;" />
                </div> -->
                <!-- <div class="grid place-items-center text-center w-24 mx-4" style="width: 50px; height: 50px" v-else-if="allowImageFallback && nearestParentCategorySmallImageUrl">
                  <img :src="nearestParentCategorySmallImageUrl" alt="" style="max-width: 50px; max-height: 50px;" />
                </div> -->
                <div class="grid place-items-center text-gray-200 dark:text-gray-900 text-center w-[50px] aspect-square mx-4" :class="{ 'text-blue-200': selectedItemId == row.articleItem.id }" style="font-size: 48px; width: 50px; height: 50px;" v-else>
                  <i class="fa fa-image"></i>
                </div>

                <!-- Article name and SKU -->
                <div class="w-full pr-5 flex flex-row justify-between items-center gap-2">
                  <div>
                    <h5 class="text-md tracking-tight text-gray-900 dark:text-white" v-if="row.articleItem.name">{{ row.articleItem.name }}</h5>
                    <h3 class="mt-0 text-slate-400" v-if="row.articleItem.sku">{{ row.articleItem.sku }}</h3>
                  </div>
                  <!-- <div v-if="row.articleItem.totalQuantity > 0">
                    <h5 class="text-gray-500">{{ row.articleItem.totalQuantity }}&nbsp;kos</h5>
                    <h3 v-if="row.articleItem.sku">&nbsp;</h3>
                  </div> -->
                </div>

                <!-- Tray name -->
                <!-- <div class="w-full pr-5 flex flex-row justify-between items-center gap-2">
                  <div>
                    <h5 class="text-md tracking-tight text-gray-900 dark:text-white" v-if="row.articleItem.name">{{ row.articleItem.name }}</h5>
                    <h3 class="mt-0 text-blue-500" v-if="row.articleItem.status == 'occupied' && row.userId == currentUserId">Osebni predal</h3>
                  </div>
                </div> -->

                <div class="w-full pr-5 flex flex-row justify-end items-center gap-2">
                  <div class="text-right">
                    <span class="text-md tracking-tight text-gray-900 dark:text-white">{{ Math.abs(row.articleItem.quantity) || 0 }}</span> kos
                    <i class="fa fa-arrow-right-from-bracket text-amber-500 ml-2"></i>
                  </div>
                </div>

              </template>
            </a>
          </div>

        </div>

        <!-- No article found -->
        <div class="relative sm:grid sm:grid-cols-1 gap-4 w-full mt-2 mb-2" v-if="!userItems || userItems.length == 0">
          <span class="text-slate-500">Ni zapisov za prikaz.</span>
        </div>
        
        <!-- Continue -->
        <div class="mt-8 dark:text-gray-400 flex justify-end w-full" v-if="isUserItemsVisible && isAutoUserItems">
          <Button type="primary" @click="selectCategory('_root')">Nadaljuj z uporabo omare</Button>
        </div>

        <!-- Debug -->
        <!-- <pre class="border rounded p-4 bg-amber-100">{{ userItems }}</pre> -->

        <div class="distancer-bottom h-20">
        </div>

      </div>



      <!-- Search results -->
      <div class="mb-8" v-if="isSearchFilter">
        <div class="mt-8" v-if="true"><span class="text-lg font-medium dark:text-gray-200">Iskalni zadetki</span><span class="pl-2 relative" style="bottom: 1px">({{ searchResults && searchResults.length ? searchResults.length : "brez zadetkov" }})</span></div>

        <Button type="primary" class="mt-2" @click="searchText = ''">Počisti iskalni niz</Button>

        <!-- List of search results -->
        <div class="relative grid grid-cols-1 gap-4 w-full mt-8 mb-8" v-if="searchResults && searchResults.length">
          <a href="#" v-for="(row, i) in searchResults" @click.prevent="selectSearchResult(row)"
            class="py-4 relative flex bg-white dark:bg-gray-800 border rounded-lg shadow hover:border-blue-600 dark:border-gray-700"
            :class="{ 'row-article': row.isArticle, 'row-category': row.isCategory }">

            <!-- Search result image -->
            <div class="grid place-items-center text-center w-24 mx-4" style="width: 50px; height: 50px" v-if="row.thumbnailUrl">
              <img :src="row.thumbnailUrl" alt="" style="max-width: 50px; max-height: 50px;" />
            </div>
            <div class="grid place-items-center text-gray-200 dark:text-gray-700 text-center w-24 mx-4" :class="{ 'text-blue-200': selectedItemId == row.id && promptDeleteItemId != row.id, 'text-red-300': promptDeleteItemId == row.id }" style="font-size: 48px; width: 50px; height: 50px;" v-else>
              <i class="fa fa-image"></i>
            </div>

            <!-- Search result name -->
            <div class="w-full pr-3 grid items-center">
              <h5 class="text-md tracking-tight text-gray-900 dark:text-gray-800 dark:text-white" v-if="row.name">{{ row.name }}</h5>
              <h3 class="mt-0 text-slate-400 dark:text-gray-200" v-if="row.isCategory">Kategorija</h3>
              <h3 class="mt-0 text-slate-400 dark:text-gray-200" v-if="row.isArticle">Artikel {{ row.sku }}</h3>
            </div>

            <!-- <i class="fa fa-magnifying-glass absolute right-0 bottom-0 m-2" :class="{ 'text-gray-900/10': row.isArticle, 'text-amber-100': row.isCategory }" style="font-size: 24px; pointer-events: none;"></i> -->
            <!-- <i class="fa fa-filter text-gray-200 absolute right-0 bottom-0 m-2" style="font-size: 24px; pointer-events: none;"></i> -->

          </a>

          <!-- Scrollable offset - height of keyboard -->
          <div class="w-full" style="height: 320px;"></div>
        </div>


        <!-- Table rows -->
        <!-- <div class="relative overflow-x-auto shadow-md sm:rounded-lg mt-8">
          <table class="w-full text-sm text-left text-gray-500 dark:text-gray-400">
            <thead class="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
              <tr>
                <th scope="col" class="px-6 py-3 text-center" style="width: 70px;">
                  Slika
                </th>
                <th scope="col" class="px-6 py-3">
                  Šifra
                </th>
                <th scope="col" class="px-6 py-3 w-full">
                  Opis
                </th>
                <th scope="col" class="pl-24 px-6 py-3 text-right">
                  Zaloga
                </th>
              </tr>
            </thead>
            <tbody>
              <tr class="bg-white border-bs dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600" v-for="(row, index) in searchResults" @click="showModal(row)">
                <td class="flex flex-row items-end justify-end px-0 py-0 space-x-0">
                  <img src="../assets/img/logo.png" alt="" style="width: 70px; height: 70px;" v-if="row.thumbnailUrl" />
                  <div class="grid place-items-center text-gray-200" style="font-size: 48px; width: 70px; height: 70px;" v-else>
                    <i class="fa fa-image"></i>
                  </div>
                </td>
                <td class="px-6 py-2">
                  {{ row.sku }}
                </td>
                <td class="px-6 py-2 font-medium text-gray-900 whitespace-nowrap dark:text-white">
                  {{ row.name }}
                </td>
                <td class="px-6 py-2 text-right">
                  {{ row.stock }}
                </td>
              </tr>
            </tbody>
          </table>
        </div> -->


      </div>


      <!-- Page buttons -->
      <div style="position: absolute; bottom: 16px; right: 20px;" v-if="false">
        <Pagination :itemsPerPage="8" :itemsCount="rows.length" class="overflow-x-auto"></Pagination>
      </div>
      <div class="text-gray-400 mb-1" style="position: absolute; bottom: 16px; left: 20px;" v-if="false">
        <!-- <span>Found 3 items. Displaying page 1 of 100.</span> -->
        <!-- <span>8 artiklov ustreza iskanim kriterijem</span> -->
        <!-- <span>Skupaj 8 artiklov</span> -->
      </div>

      <!-- Distancer -->
      <div class="distancer" style="height: 10px;">
      </div>

    </div>

    <!-- Article modal window. Note that "persistent not-escapable" class are replaced with additional conditions and handled by the function "closeModalConditional" -->
    <Modal id="articleWindow" size="3xl" class="non-escapable-modal dark:bg-gray-900" :class="{ 'fix-padding-bottom': isScaleBanner }" v-if="isModalVisible && selected && !isAnyAlarm" @close="closeModalConditional"  @click.outside="closeModalConditional">
      <template #header>
        <div class="flex items-center text-lg dark:text-white">
          Artikel <span class="font-semibold ml-2" v-if="selected.sku">{{ selected.sku }}</span>
        </div>
      </template>
      <template #body>

        <!-- Related articles -->
        <div class="w-full dark:text-white" v-if="isRelatedArticlesView && selected.related" style="min-height: 250px; max-height: 1000px;">
          <h3 class="font-bold">Sorodni artikli</h3>
          <div class="relative grid grid-cols-1 gap-4 w-full mt-2 mb-2">
            <a href="#" v-for="(row, i) in mapRelated(selected.related)" @click.prevent="showModal(row)" class="py-4 flex bg-white border border-gray-200 rounded-lg shadow hover:border-blue-600 dark:bg-gray-800 dark:border-gray-700 dark:hover:bg-gray-700">

              <!-- Article image -->
              <div class="grid place-items-center text-center w-24 mx-4" style="width: 50px; height: 50px" v-if="row.thumbnailUrl">
                <img :src="row.thumbnailUrl" alt="" style="max-width: 50px; max-height: 50px;" />
              </div>
              <!-- <div class="grid place-items-center text-center w-24 mx-4" style="width: 50px; height: 50px" v-else-if="allowImageFallback && nearestParentCategoryLargeImageUrl">
                <img :src="nearestParentCategoryLargeImageUrl" alt="" style="max-width: 50px; max-height: 50px;" />
              </div> -->
              <div class="grid place-items-center text-gray-200 dark:text-gray-700 text-center w-24 mx-4" style="font-size: 48px; width: 50px; height: 50px;" v-else>
                <i class="fa fa-image"></i>
              </div>

              <!-- Article name and SKU -->
              <div class="w-full pr-3 grid items-center">
                <h5 class="text-md tracking-tight text-gray-900 dark:text-white" v-if="row.name">{{ row.name }}</h5>
                <h3 class="mt-0 text-slate-400" v-if="row.sku">{{ row.sku }}</h3>
              </div>

            </a>
          </div>
        </div>



        <!-- Location -->
        <div class="w-full dark:text-white" v-else-if="isLocationView" style="min-height: 250px; max-height: 1000px;">
          <div v-if="isLoadingLocation" class="grid place-items-center w-full" style="height: 250px;">
            <Spinner size="12" />
          </div>
          <div class="flex flex-col justify-between" style="min-height: 250px;" v-else>
            <!-- Location name -->
            <h3 class="font-bold" v-if="relatedStockCurrent.location">{{ relatedStockCurrent.location.name }}</h3>
            <div class="dummy-placeholder" v-else></div>

            <!-- Location picture -->
            <div class="relative grid grid-cols-1 gap-4 w-full mt-2 mb-2">
              <!-- Show cabinet location/map -->
              <div class="w-full relative mt-2 overflow-hidden" v-if="relatedStockCurrent.location && isMapView">
                <div class="w-full relative">
                  <img :src="relatedStockCurrent.location.imageBase64" :alt="relatedStockCurrent.location.name" class="w-full h-auto" />
                  <div v-if="relatedStockCurrent.location.point"
                      class="absolute z-10 pointer-events-none w-10 h-auto aspect-square rounded-full" :style="{
                        left: `${relatedStockCurrent.location.point.x}%`,
                        top: `${relatedStockCurrent.location.point.y}%`
                      }"><i class="fa fa-map-marker-alt text-blue-500 text-2xl relative" style="left: -8px; top: -24px"></i>
                  </div>
                </div>
                <!-- Clickable thumbnail picture -->
                <!-- <div class="w-full absolute right-0 bottom-0 overflow-hidden max-w-[150px] max-h-[150px] bg-white border border-1 border-gray-300 rounded-md" v-if="relatedStockCurrent.cabinet?.image?.id" @click="showCabinetPicture()">
                  <div class="p-2">
                    <img :src="resolveImageUrl(relatedStockCurrent.cabinet.image.smallImagePath)" alt="" class="w-full h-auto" />
                  </div>
                </div> -->
                <!-- Full width picture -->
                <div class="w-full overflow-hidden max-w-full max-h-[450px] bg-white" v-if="relatedStockCurrent.cabinet?.image?.id">
                  <img :src="resolveImageUrl(relatedStockCurrent.cabinet.image.mediumImagePath)" alt="" class="w-full h-auto" />
                </div>
              </div>
              <!-- Show cabinet picture -->
              <div class="w-full relative mt-2" v-else-if="relatedStockCurrent.location && !isMapView">
                <img :src="resolveImageUrl(relatedStockCurrent.cabinet.image.mediumImagePath)" alt="" class="w-full h-auto" />
                <div class="absolute right-0 -top-12 overflow-hidden" v-if="relatedStockCurrent.cabinet?.image">
                  <Button type="secondary" @click="showCabinetMap()">
                    <i class="fa fa-map-marker-alt text-blue-500 text-xl"></i>
                  </Button>
                </div>
              </div>
              <!-- Show picture because map is missing -->
              <div class="w-full relative mt-2 overflow-hidden max-h-[980px]" v-else-if="relatedStockCurrent.cabinet?.image?.id">
                <img :src="resolveImageUrl(relatedStockCurrent.cabinet.image.mediumImagePath)" alt="" class="w-full h-auto" />
              </div>
              <!-- Missing map or picture -->
              <div class="text-center w-full" v-else>
                Izbrana omara nima naložene slike.
              </div>
            </div>

            <!-- Cabinet name and navigation buttons -->
            <!-- <div class="flex flex-row justify-between gap-2 w-full">
              <div class="grid place-items-center">
                <h3><span class="font-bold">{{ relatedStockCurrent.cabinet.name }}</span><span class="font-normal"> - zaloga {{ relatedStockCurrent.quantity }} kos</span></h3>
              </div>
              <div class="flex flex-row gap-2" v-if="relatedStockCount > 1">
                <Button type="primary" @click="prevRelatedStock()" :enabled="relatedStockIndex - 1 >= 0"><i class="fa fa-chevron-left"></i></Button>
                <Button type="primary" @click="nextRelatedStock()" :enabled="relatedStockIndex + 1 < relatedStockCount"><i class="fa fa-chevron-right"></i></Button>
              </div>
            </div> -->
            <div class="dummy-placeholder"></div>

          </div>
        </div>




        <!-- Default view -->
        <div class="w-full dark:text-white" style="min-height: 250px; max-height: 1000px;" v-else>
          <div class="flex flex-row w-full gap-8 mb-4">

            <!-- Large image -->
            <div class="grid relative w-full place-items-center" v-if="!isRelatedArticlesView && isLargeImageView" @click="() => isLargeImageView = false">
              <p class="article-title py-2 mb-4 w-full text-center font-medium">{{ selected.name }}</p>
              <img :src="selected.largeImageUrl" alt="" style="max-width: 100%; max-height: 700px;" class="dark:bg-white dark:p-3 dark:rounded-md" />
              <!-- <div class="absolute bottom-1 right-2 text-gray-300" v-if="selected.mediumImageUrl">
                <i class="fa fa-compress"></i>
              </div> -->
            </div>

            <!-- Article image -->
            <div class="grid" v-if="!isRelatedArticlesView && !isLargeImageView">
              <div class="relative grid place-items-center text-gray-200 border dark:border-gray-800 rounded-md" style="width: 220px; height: 220px;" v-if="!isRelatedArticlesView">
                <div class="dark:bg-white p-3 rounded-md overflow-hidden" style="width: 220px; height: 220px;" v-if="selected.mediumImageUrl">
                  <img :src="selected.mediumImageUrl" alt="" class="w-full h-full" style="object-fit: contain; object-position: center;" @error="() => noImage(selected)" @click="() => isLargeImageView = true" />
                </div>
                <img :src="nearestParentCategoryLargeImageUrl" alt="" class="dark:bg-white p-3 rounded-md" style="width: 220px; max-height: 220px;" @error="() => nearestParentCategoryLargeImageUrl = null" v-else-if="allowImageFallback && nearestParentCategoryLargeImageUrl" />
                <div class="dark:text-gray-800" style="font-size: 48px;" v-else>
                  <i class="fa fa-image"></i>
                </div>
                <!-- <div class="absolute bottom-1 right-2 text-gray-300" v-if="selected.mediumImageUrl" @click="() => isLargeImageView = true">
                  <i class="fa fa-expand"></i>
                </div> -->
              </div>

              <!-- Attachments, e.g. usage manual pdf -->
              <div class="grid gap-2 mt-4 w-full" v-if="false">
                <Button @click="openAttachment" type="secondary" class="text-left">
                  <i class="fa fa-paperclip mr-2"></i> Specifikacija
                </Button>
                <Button @click="openAttachment" type="secondary" class="text-left">
                  <i class="fa fa-paperclip mr-2"></i> Navodila
                </Button>
              </div>

            </div>

            <!-- Article title, description, specifications -->
            <div class="flex flex-col grow gap-4 relative" v-if="!isRelatedArticlesView && !isLargeImageView">
              <!-- Title -->
              <p class="article-title font-medium">{{ selected.name }}</p>

              <!-- Description -->
              <p class="article-description" v-if="selected.description">{{ selected.description }}</p>

              <!-- Specification -->
              <p class="article-specification" v-if="hasParameterSelected && parametersSelected.length">
                <h4 class="font-semibold">Specifikacija</h4>
                <div v-for="spec in parametersSelected">{{ spec.name }}: {{ spec.value }}</div>
              </p>

              <!-- Stock quantity -->
              <p class="article-stock" v-if="!isCabinetBorrowMode">
                <div v-if="stockSum(selected.trays) > 0">
                  <span v-if="selected?.trays?.length > 1">Skupna zaloga</span><span v-else>Na zalogi</span><span class="font-medium ml-1">{{ stockSum(selected.trays) }}</span> {{ selected.stockUnit || "kos" }}
                </div>
                <div v-else-if="selected?.relatedStockAvailable && !isEditStockQuantity && !isRelatedArticlesView && !isReferencing && !isOpening && !isOpen" class="w-full">
                  <div class="border border-amber-100 bg-amber-100 text-amber-800 text-sm rounded-lg px-4 py-3 dark:bg-yellow-300 dark:text-amber-900 dark:border-amber-800">
                    <i class="fa fa-location-dot mr-2 text-amber-500 dark:text-amber-900"></i>
                    <span>Artikla ni v tej omari - na voljo v drugi omari.</span>
                  </div>
                  <div class="flex justify-end w-full mt-2">
                    <Button type="warning" @click="showRelatedStock(selected)">Preveri <i class="fa fa-arrow-right ml-2"></i></Button>
                  </div>
                </div>
              </p>

              <!-- Status of borrowed article -->
              <div class="article-stock-status" v-if="isCabinetBorrowMode && selectedTrayItem && selectedTrayItem.usage && selectedTrayItem.countMode != 'storage'">
                <div v-if="selected?.relatedStockAvailable && !isEditStockQuantity && !isRelatedArticlesView && !isReferencing && !isOpening && !isOpen" class="w-full">
                  <div class="border border-amber-100 bg-amber-100 text-amber-800 text-sm rounded-lg px-4 py-3 dark:bg-yellow-300 dark:text-amber-900 dark:border-amber-800">
                    <i class="fa fa-location-dot mr-2 text-amber-500 dark:text-amber-900"></i>
                    <span>Artikla je na voljo še v drugi omari.</span>
                  </div>
                  <div class="flex justify-end w-full mt-2">
                    <Button type="warning" @click="showRelatedStock(selected)">Preveri <i class="fa fa-arrow-right ml-2"></i></Button>
                  </div>
                </div>

                <div class="label mb-1">Stanje</div>
                <Tag color="green" v-if="selectedTrayItem.usage == 'present'">V predalu</Tag>
                <Tag color="yellow" v-else-if="selectedTrayItem.usage == 'taken'">Na izposoji</Tag>
                <Tag color="red" v-else>{{ selectedTrayItem.usage }}</Tag>
              </div>

              <!-- Status of borrowed article -->
              <div class="article-stock-user" v-if="isCabinetBorrowMode && selectedTrayItem && selectedTrayItem.userId && selectedTrayItem.usage == 'taken'">
                <div class="label mb-1">Vzel</div>
                <Tag color="yellow"><i class="fa fa-user mr-1"></i> {{ getUserDisplayName(selectedTrayItem.userId) }}</Tag>
                <Tag color="yellow" class="ml-2" v-if="selectedTrayItem.userTime && selectedTrayItem.userTime instanceof Date"><i class="fa fa-clock mr-1"></i> {{ formatLocaleDate(selectedTrayItem.userTime) }} ob {{ formatLocaleTime(selectedTrayItem.userTime) }}</Tag>
              </div>

              <!-- Edit quantity -->
              <div v-if="isEditStockQuantity && !isCabinetBorrowMode">
                <div>Urejanje zaloge</div>
                <!-- <div class="flex flex-row justify-stretch mt-2">
                  <PushButton type="secondary" class="whitespace-nowrap" @click="updateQuantity(-10)">-10</PushButton>
                  <PushButton class="ml-2 text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium text-sm w-full sm:w-auto px-5 py-2.5 text-center dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800" style="border-radius: 7px 0 0 7px;" @click="updateQuantity(-1)"><i class="fa fa-arrow-down"></i></PushButton>
                  <TextBox v-model="virtualStockQuantity" placeholder="Brez" class="w-full bg-white !rounded-sm border border-gray-300 text-gray-900 text-sm block p-2.5 focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-800 dark:text-white dark:border-slate-500" />
                  <PushButton class="mr-2 text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm w-full sm:w-auto px-5 py-2.5 text-center dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800" style="border-radius: 0 7px 7px 0;" @click="updateQuantity(+1)"><i class="fa fa-arrow-up"></i></PushButton>
                  <PushButton type="secondary" class="whitespace-nowrap" @click="updateQuantity(+10)">+10</PushButton>
                </div> -->
                <div class="flex flex-row justify-stretch mt-2">
                  <PushButton class="text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium text-sm w-full sm:w-auto px-5 py-2.5 text-center dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800" style="border-radius: 7px 0 0 7px;" @click="updateQuantity(-1)"><i class="fa fa-arrow-down"></i></PushButton>
                  <TextBox v-model="virtualStockQuantity" type="numeric" placeholder="--" class="w-full text-center font-medium !py-1.5 !text-lg tracking-widest !rounded-sm border border-gray-300 text-gray-900 text-sm block p-2.5 focus:ring-blue-500 focus:border-blue-500 dark:bg-slate-800 dark:text-white dark:border-slate-500"
                    :class="{
                      '!bg-red-200 !text-red-800 dark:!bg-red-900 dark:!text-red-300': virtualStockQuantityState == 'empty',
                      '!bg-amber-100 !text-amber-800 dark:!bg-yellow-900 dark:!text-yellow-300': virtualStockQuantityState == 'low',
                      '!bg-amber-200 !text-amber-800 dark:!bg-yellow-900 dark:!text-yellow-300': virtualStockQuantityState == 'warning',
                      '!bg-sky-100 dark:!bg-sky-900': virtualStockQuantityState == 'normal',
                    }" />
                  <PushButton class="mr-2 text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm w-full sm:w-auto px-5 py-2.5 text-center dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800" style="border-radius: 0 7px 7px 0;" @click="updateQuantity(+1)"><i class="fa fa-arrow-up"></i></PushButton>
                  <Button type="secondary" class="whitespace-nowrap w-40" @click="rotateQuantityFactor()">&times; {{ quantityFactor }}</Button>
                  <Button type="primary" class="whitespace-nowrap ml-2" @click="applyStockTransfer()" v-if="pendingTransferQuantity > 0 && !isCabinetBorrowMode && !isStockTransferApplied"><i class="fa fa-arrow-right-to-bracket rotate-180 mr-2"></i>{{ pendingTransferQuantity }}</Button>
                  <Button type="warning" class="whitespace-nowrap ml-2" @click="undoStockTransfer()" v-if="pendingTransferQuantity > 0 && !isCabinetBorrowMode && isStockTransferApplied"><i class="fa fa-rotate-left mr-2"></i>{{ pendingTransferQuantity }}</Button>
                </div>

                <!-- Stock mismatch: weighted stock does not match previously stored quantity -->
                <div class="mt-2" v-if="isStockMismatch">
                  <i class="fa fa-triangle-exclamation text-amber-400 ml-14 mr-2"></i><span class="text-amber-700 dark:text-amber-400">Zaloga se ne ujema!</span>
                </div>

                <!-- Weight scale calibration -->
                <!-- <div class="mt-8 px-4 pt-3 pb-4 border border-amber-300 bg-amber-100 rounded-md" v-if="isScaleReady && !isScaleCalibration && isItemWeightCalibrated && !isScaleBanner">
                  <div class="flex flex-col gap-2">

                    <div class="grid grid-cols-2 gap-4">
                      <div>
                        <span>Masa [g]</span>
                        <div class="flex flex-row justify-stretch gap-2">
                          <TextBox class="w-full" v-model="scaleLastValueIntegerFormatted" :readonly="true" />
                          <Button type="warning" @click="tare()">Tare</Button>
                        </div>
                      </div>
                      <div v-if="isItemWeightCalibrated">
                        <span>Št. kosov na tehtnici</span>
                        <div class="flex flex-row justify-stretch gap-2">
                          <TextBox class="w-full" v-model="scaleItemsCountFormatted" placeholder="--" :readonly="true" :enabled="scaleItemsCountFormatted !== null" />
                          <Button type="success" @click="applyScaleItemsCount()"><i class="fa fa-check"></i></Button>
                        </div>
                      </div>
                      <div v-if="isItemWeightCalibrated" class="grid">
                        <div v-if="canCalibrateWeightScale" class="grid place-items-center">
                          <div class="w-full text-left">Masa kosa <Tag color="yellow">{{ scaleItemWeightCalibrated }}</Tag> g</div>
                        </div>
                      </div>
                      <div v-if="isItemWeightCalibrated">
                        <span v-if="scaleItemsCountTaken < 0">Št. dodanih kosov</span>
                        <span v-else>Št. odvzetih kosov</span>
                        <div class="flex flex-row justify-stretch gap-2">
                          <TextBox class="w-full" v-model="scaleItemsCountTakenFormatted" placeholder="--" :readonly="true" />
                        </div>
                      </div>
                    </div>

                  </div>

                </div> -->

                <!-- Weight scale calibration -->
                <!-- <div class="mt-8 px-4 pt-3 pb-4 border border-amber-300 bg-amber-100 dark:bg-yellow-900 rounded-md" v-if="isScaleReady && isScaleCalibration && !isScaleBanner">
                  <div class="font-semibold mb-2">Tehtnica - umerjanje kosov</div>
                  <div class="flex flex-col gap-2">
                    
                    <div>
                      <span>Izmerjena masa [g]</span>
                      <div class="flex flex-row justify-stretch gap-2">
                        <TextBox class="w-full" v-model="scaleLastValueFormatted" :readonly="true" />
                        <Button type="warning" @click="tare()">Tare</Button>
                      </div>
                    </div>

                    <div>
                      <span>Masa praznega vložka [g]</span>
                      <div class="flex flex-row justify-stretch gap-2">
                        <TextBox class="w-full" v-model="scaleInsertWeightFormatted" />
                        <Button type="warning" @click="applyEmptyInsertWeight()">Nastavi</Button>
                      </div>
                    </div>

                    <div class="grid grid-cols-2 gap-4">
                      <div>
                        <span>Masa kosa [g]</span>
                        <TextBox class="w-full" v-model="scaleItemWeightFormatted" :readonly="true" />
                      </div>
                      <div>
                        <span>Št. kosov v vložku</span>
                        <div class="flex flex-row justify-stretch gap-2">
                          <TextBox class="w-full" v-model="scaleItemsUserCountFormatted" />
                          <Button type="warning" @click="scaleItemUpdate(-1)"><i class="fa fa-arrow-down"></i></Button>
                          <Button type="warning" @click="scaleItemUpdate(+1)"><i class="fa fa-arrow-up"></i></Button>
                        </div>
                      </div>
                    </div>

                    <div class="flex flex-row justify-end gap-2 mt-2">
                      <Button type="danger" @click="cancelScaleCalibration()"><i class="fa fa-xmark mr-3"></i> Prekliči</Button>
                      <Button type="success" @click="applyScaleCalibration()"><i class="fa fa-check mr-3"></i> Potrdi</Button>
                    </div>

                  </div>

                </div> -->

                <!-- Weight scale in practical use -->
                <!--
                <div class="mt-8 px-4 pt-3 pb-4 border border-amber-300 bg-amber-100 rounded-md" v-if="isScaleReady && isItemWeightCalibrated && false">
                  <div class="font-semibold mb-2">Tehtnica - v razvoju<i class="fa fa-weight ml-2 text-amber-500"></i></div>
                  <div class="flex flex-col gap-2">
                    
                    <div>
                      <span>Izmerjena masa [g]</span>
                      <div class="flex flex-row justify-stretch gap-2">
                        <TextBox class="w-full" v-model="scaleLastValueFormatted" :readonly="true" />
                        <Button type="warning" @click="tare()">Tare</Button>
                      </div>
                    </div>

                    <div>
                      <span>Število kosov</span>
                      <div class="flex flex-row justify-stretch gap-2">
                        <TextBox class="w-full" v-model="scaleItemsCountFormatted" :readonly="true" />
                        <Button type="warning" @click="applyScaleItemsCount()">Uporabi</Button>
                      </div>
                    </div>

                  </div>

                </div>
                -->

              </div>

              <!-- Comment -->
              <div class="article-stock-status" v-if="!isCommentEdit && hasEditCommentConditions && selectedTrayItem && stockComment && stockComment.length > 0">
                <div class="label mb-1">Komentar</div>
                <pre class="text-sm font-sans text-slate-700 dark:text-white">{{ stockComment }}</pre>
              </div>

              <!-- Edit comment -->
              <div v-if="hasEditCommentConditions && isCommentEdit">
                <div>{{ $t('catalog.comment_label') }}</div>
                <div class="w-full mt-2">
                  <TextAreaTouchFriendly v-model="commentText" :placeholder="$t('catalog.comment_placeholder')" class="w-full" :rows="5" />
                </div>
                <div class="flex flex-row justify-end gap-2 mt-2">
                  <Button :type="'secondary'" @click="cancelComment()"><i class="fa fa-xmark mr-2"></i>{{ $t('generic.cancel') }}</Button>
                  <Button :type="'success'" @click="saveComment({ submit: isCabinetBorrowMode })"><i class="fa fa-check mr-2"></i>{{ $t('generic.confirm') }}</Button>
                </div>
              </div>

              <!-- Edit comment button -->
              <Button type="secondary" @click="editComment()" v-if="hasEditCommentConditions && canEditComment && !isCommentEdit && !isReferencing && isOpen && !isPrevOpen && isEditStockQuantity"><i class="fa fa-pen-to-square mr-2"></i>Uredi komentar</Button>

              <!-- Access permissions -->
              <div class="article-access-permission border border-amber-100 bg-amber-100 text-amber-800 text-sm rounded-lg px-4 py-3 dark:bg-yellow-300 dark:text-amber-900 dark:border-amber-800" v-if="!canOpenTrayPermissions && selectedTrayItem">
                <i class="fa fa-lock mr-2 text-amber-500 dark:text-amber-900"></i>
                <span v-if="isTakenByOtherUser">Na izposoji od drugega uporabnika</span>
                <span v-else>Uporabnik nima pravic dostopa do predala</span>
              </div>

              <!-- Borrow mode -->
              <!-- <div v-if="isEditStockQuantity && isCabinetBorrowMode && isOpen">
                <div>Izberi dejanje</div>
                <div class="grid grid-cols-4 gap-2 mt-2">
                  <Button :type="selectedTrayUsage == 'taken' ? 'warning' : 'secondary'" :enabled="selectedTrayUsage == 'taken'" @click="setTrayUsage('taken')"><i class="fa fa-arrow-right-from-bracket mr-2"></i>Vzel</Button>
                  <Button :type="selectedTrayUsage == 'present' ? 'success' : 'secondary'" :enabled="selectedTrayUsage == 'present'" @click="setTrayUsage('present')"><i class="fa fa-arrow-right-to-bracket mr-2"></i>Vrnil</Button>
                </div>
              </div> -->
              
            </div>
          </div>



          <!-- Weight scale banner -->
          <div class="w-full" v-if="isEditStockQuantity && !isCabinetBorrowMode && isScaleBanner">
            <div style="margin: 0 -24px;">

              <!-- Weight scale calibration -->
              <div class="mt-8 px-[24px] pt-5 pb-6 border-t border-gray-200 bg-amber-100 dark:border-gray-600 dark:bg-gray-800/80" v-if="isScaleReady && !isScaleCalibration && isItemWeightCalibrated">
                <div class="flex flex-col gap-2">

                  <div class="flex flex-row gap-4">
                    <div>
                      <span>Masa na tehtnici [g]</span>
                      <div class="flex flex-row justify-stretch gap-0">
                        <input type="text" class="w-full text-right font-medium tracking-widest
                          bg-white border border-gray-300 text-gray-900 text-sm rounded-lg block p-2.5
                          dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                          style="border-radius: 7px 0 0 7px;" v-model="scaleLastValueIntegerFormatted" :readonly="true" disabled />
                        <Button type="warning" @click="tare()" style="border-radius: 0 7px 7px 0;">Tare</Button>
                      </div>
                    </div>
                    <div v-if="isItemWeightCalibrated">
                      <span v-if="scaleItemsCountTaken < 0">Št. dodanih kosov</span>
                      <span v-else>Št. odvzetih kosov</span>
                      <div class="flex flex-row justify-stretch gap-0">
                        <input type="text" class="w-full flex-1 text-right font-medium tracking-widest
                          bg-white border border-gray-300 text-gray-900 text-sm rounded-lg block p-2.5
                          dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                          style="border-radius: 7px 0 0 7px;"
                          :class="{
                            '!text-green-600 dark:!text-green-400': scaleItemsCountTaken < 0,
                            '!text-amber-700 dark:!text-yellow-300': scaleItemsCountTaken > 0
                          }" v-model="scaleItemsCountTakenFormatted" placeholder="--" readonly disabled />
                          <div v-if="scaleItemsCountTaken < 0" class="text-green-600 dark:text-white dark:border-gray-500 border border-gray-300 bg-gray-50 outline-none font-medium rounded-lg text-sm !w-15 px-5 py-2.5 text-center dark:bg-blue-600" style="border-radius: 0 7px 7px 0; border-left-style: none; width: 57px;"><i class="fa fa-arrow-right-to-bracket rotate-90"></i></div>
                          <div v-else-if="scaleItemsCountTaken > 0" class="text-amber-700 dark:text-white dark:border-gray-500 border border-gray-300 bg-gray-50 outline-none font-medium rounded-lg text-sm !w-15 px-5 py-2.5 text-center dark:bg-blue-600" style="border-radius: 0 7px 7px 0; border-left-style: none; width: 57px;"><i class="fa fa-arrow-right-from-bracket -rotate-90"></i></div>
                          <div v-else class="relative text-gray-500 border border-gray-300 bg-gray-50 dark:text-gray-200 dark:border-gray-500 outline-none font-medium rounded-lg text-sm !w-15 px-4 py-2.5 text-center dark:bg-blue-600" style="border-radius: 0 7px 7px 0; border-left-style: none; width: 57px;"><svg version="1.1" viewBox="0 0 512 512" class="w-[14px] absolute top-[14px] left-[22px] fill-white -rotate-90" xmlns="http://www.w3.org/2000/svg">
                            <!--!Font Awesome Free v6.7.2 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license/free Copyright 2026 Fonticons, Inc.-->
                            <path d="m160 96c17.7 0 32-14.3 32-32s-14.3-32-32-32h-64c-53 0-96 43-96 96v256c0 53 43 96 96 96h64c17.7 0 32-14.3 32-32s-14.3-32-32-32h-64c-17.7 0-32-14.3-32-32v-256c0-17.7 14.3-32 32-32z"/>
                          </svg></div>
                      </div>
                    </div>
                    <div v-if="isItemWeightCalibrated">
                      <span>Št. kosov na tehtnici</span>
                      <div class="flex flex-row justify-stretch gap-0">
                        <!-- <TextBox class="w-full text-right tracking-widest" v-model="scaleItemsCountFormatted" placeholder="--" :readonly="true" :enabled="scaleItemsCountFormatted !== null" /> -->
                        <input type="text" class="w-full text-right font-medium tracking-widest
                          bg-white border border-gray-300 text-gray-900 text-sm rounded-lg block p-2.5
                          dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                          style="border-radius: 7px 0 0 7px;" v-model="scaleItemsCountFormatted" placeholder="--" readonly disabled />
                        <Button type="success" style="border-radius: 0 7px 7px 0;" @click="applyScaleItemsCount()"><i class="fa fa-check"></i></Button>
                      </div>
                    </div>
                  </div>

                </div>

              </div>

              <!-- Weight scale calibration -->
              <div class="mt-8 px-4 pt-3 pb-4 border-t border-gray-200 bg-amber-100 dark:border-gray-600 dark:bg-gray-800/80" v-if="isScaleReady && isScaleCalibration">
                <div class="font-semibold mb-2">Tehtnica - umerjanje kosov</div>
                <div class="flex flex-col gap-2">
                  
                  <div>
                    <span>Izmerjena masa [g]</span>
                    <div class="flex flex-row justify-stretch gap-2">
                      <TextBox class="w-full text-left font-mono font-medium tracking-wider" v-model="scaleLastValueFormatted" :readonly="true" :enabled="false" />
                      <Button type="warning" @click="tare()">Tare</Button>
                    </div>
                  </div>

                  <div>
                    <span>Masa praznega vložka [g]</span>
                    <div class="flex flex-row justify-stretch gap-2">
                      <TextBox class="w-full text-left font-mono font-medium tracking-wider" v-model="scaleInsertWeightFormatted" />
                      <Button type="warning" @click="applyEmptyInsertWeight()">Nastavi</Button>
                    </div>
                  </div>

                  <div class="grid grid-cols-2 gap-4">
                    <div>
                      <span>Masa kosa [g]</span>
                      <TextBox class="w-full text-left font-mono font-medium tracking-wider" v-model="scaleItemWeightFormatted" :readonly="true" :enabled="false" />
                      <!-- <div class="flex flex-row justify-stretch gap-2">
                        <TextBox class="w-full" v-model="scaleItemWeightFormatted" :readonly="true" />
                        <Button type="success" @click="applyScaleCalibration()"><i class="fa fa-check"></i></Button>
                      </div> -->
                    </div>
                    <div>
                      <span>Št. kosov v vložku</span>
                      <div class="flex flex-row justify-stretch gap-2">
                        <TextBox class="w-full text-left font-mono font-medium tracking-wider" v-model="scaleItemsUserCountFormatted" type="numeric" />
                        <Button type="warning" @click="scaleItemUpdate(-1)"><i class="fa fa-arrow-down"></i></Button>
                        <Button type="warning" @click="scaleItemUpdate(+1)"><i class="fa fa-arrow-up"></i></Button>
                      </div>
                    </div>
                  </div>

                  <div class="flex flex-row justify-between gap-2 mt-2">
                    <div v-if="isItemWeightCalibrated" class="grid">
                      <div v-if="canCalibrateWeightScale" class="grid place-items-center">
                        <div class="w-full text-left">Shranjena masa kosa <Tag color="yellow">{{ scaleItemWeightCalibrated }}</Tag> g</div>
                      </div>
                    </div>
                    <div v-else><!-- Dummy --></div>
                    <div class="flex flex-row justify-end gap-2 mt-2">
                      <Button type="danger" @click="cancelScaleCalibration()"><i class="fa fa-xmark mr-3"></i> Prekliči</Button>
                      <Button type="success" @click="applyScaleCalibration()"><i class="fa fa-check mr-3"></i> Potrdi</Button>
                    </div>
                  </div>

                </div>

              </div>


            </div>



          </div>

        </div>
      </template>
      <template #footer>
        <div class="flex justify-between">
          <Button :type="!isCommentEdit ? 'danger' : 'secondary'" :enabled="!isCommentEdit" @click="cancelStock()" v-if="isEditStockQuantity && isCabinetBorrowMode && false">Prekliči</Button>
          <Button type="secondary" @click="showLocation()" v-if="!isEditStockQuantity && !isReferencing && !isOpening && !isOpen && !isLocationView && hasLocation">Lokacija</Button>
          <Button type="secondary" @click="showRelatedArticles()" v-if="!isEditStockQuantity && !isReferencing && !isOpening && !isOpen && !isRelatedArticlesView && hasRelatedArticles && !hasLocation">Sorodni&nbsp;artikli</Button>
          <Button type="secondary" @click="hideLocation()" v-if="!isEditStockQuantity && !isReferencing && !isOpening && !isOpen && isLocationView && hasLocation"><i class="fa fa-arrow-left inline mr-2"></i>Nazaj</Button>
          <Button type="secondary" @click="hideRelatedArticles()" v-if="!isEditStockQuantity && !isReferencing && !isOpening && !isOpen && isRelatedArticlesView && !hasLocation"><i class="fa fa-arrow-left inline mr-2"></i>Nazaj</Button>
          <Button type="warning" @click="setWeightScaleCalibrationMode(true)" v-if="!isRelatedArticlesView && !isLargeImageView && isEditStockQuantity && !isCabinetBorrowMode && isScaleReady && !isScaleCalibration && canCalibrateWeightScale"><i class="fa fa-gear mr-2"></i>Umeri tehtnico</Button>
          <Button type="secondary" :enabled="false" v-if="!isRelatedArticlesView && !isLargeImageView && isEditStockQuantity && !isCabinetBorrowMode && isScaleReady && isScaleCalibration && canCalibrateWeightScale"><i class="fa fa-gear mr-2"></i>Umeri tehtnico</Button>

          <div class="flex justify-items-end w-full" style="min-height: 30px;">

            <!-- Middle block -->
            <div class="m-auto">
              <span v-if="isReferencing" class="text-gray-500 bg-white outline-none rounded-lg text-sm font-medium px-5 py-2.5 hover:text-gray-900 focus:z-10 dark:bg-gray-700 dark:text-gray-300">
                <Spinner size="6" style="display: inline-block;" />
                <span style="display: inline-block; position: relative; top: 3px; left: 10px;">Postavljam omaro v referenco...</span>
              </span>
              <span v-else-if="!isReferencing && isOpen && isClosing" class="text-gray-500 bg-white outline-none rounded-lg text-sm font-medium px-5 py-2.5 hover:text-gray-900 focus:z-10 dark:bg-gray-700 dark:text-gray-300">
                <Spinner size="6" style="display: inline-block;" />
                <span style="display: inline-block; position: relative; top: 3px; left: 10px;" v-if="hasDeliveryFeature">Pospravljam predal {{ selectedTrayName }}</span>
                <span style="display: inline-block; position: relative; top: 3px; left: 10px;" v-else>Zapiram predal {{ selectedTrayName }}</span>
              </span>
              <span v-else-if="!isReferencing && !isOpen && isOpening" class="text-gray-500 bg-white outline-none rounded-lg text-sm font-medium px-5 py-2.5 hover:text-gray-900 focus:z-10 dark:bg-gray-700 dark:text-gray-300">
                <Spinner size="6" style="display: inline-block;" />
                <span style="display: inline-block; position: relative; top: 3px; left: 10px;" v-if="hasDeliveryFeature">Dostavljam predal {{ selectedTrayName }}</span>
                <span style="display: inline-block; position: relative; top: 3px; left: 10px;" v-else>Odpiram predal {{ selectedTrayName }}</span>
              </span>
              <span v-else-if="!isReferencing && isOpen && !isPrevOpen && !isClosing && !isEditStockQuantity" class="text-gray-500 bg-white outline-none rounded-lg text-sm font-medium px-5 py-2.5 hover:text-gray-900 focus:z-10 dark:bg-gray-700 dark:text-gray-300">
                <i class="fa fa-lock-open text-green-500" style="position: relative; top: 2px;"></i> <span style="display: inline-block; position: relative; top: 3px; left: 10px;">{{ $t('catalog.tray_open_message') }}</span>
              </span>
              <span v-else-if="!isReferencing && isPrevOpen && !isOpening && !isClosing" class="text-gray-500 bg-white outline-none rounded-lg text-sm font-medium px-5 py-2.5 hover:text-gray-900 focus:z-10 dark:bg-gray-700 dark:text-gray-300">
                <i class="fa fa-triangle-exclamation text-amber-500" style="position: relative; top: 3px;"></i> <span style="display: inline-block; position: relative; top: 3px; left: 10px;">Za nadaljevanje najprej zapri prejšnji predal.</span>
              </span>
              <div v-else-if="isLocationView && relatedStockCurrent">
                <h3><span class="font-bold">{{ relatedStockCurrent.cabinet?.name }}</span><span class="font-normal" v-if="relatedStockCurrent.quantity > 0"> - zaloga {{ relatedStockCurrent.quantity }} kos</span></h3>
              </div>
              <div v-else-if="!isReferencing && isOpen && isScaleReady && isScaleValueRequired && isItemWeightCalibrated && !canApplyStock" class="animate-blink">
                <i class="fa fa-triangle-exclamation text-amber-800" style="position: relative; top: 3px;"></i> <span class="text-amber-900" style="display: inline-block; position: relative; top: 3px; left: 10px;">Tehtanje je obvezno!</span>
              </div>
              <div v-else-if="isEditStockQuantity && isCabinetBorrowMode && autoConfirm">
                <span style="display: inline-block; position: relative; top: 3px; left: 10px;" v-if="isCommentEdit">Potrdi urejanje komentarja</span>
                <span style="display: inline-block; position: relative; top: 3px; left: 10px;" v-else>Za nadaljevanje zapri predal</span>
              </div>
              <!-- <div v-else-if="!isReferencing && isOpen && !isCabinetBorrowMode && canTransferStock && !isStockTransferApplied && !isScaleCalibration && hasStockQuantityChanged && canApplyStock" class="flex flex-row justify-center gap-2 whitespace-nowrap">
                <CheckBox v-model="isStockTransferRequest" label="Prenos zaloge" class="" />
              </div> -->
            </div>

            <!-- Near buttons -->
            <div v-if="!isEditStockQuantity && !isRelatedArticlesView && !isReferencing && !isOpening && !isOpen && !hasLocation && selected.trays?.length > 0" class="mr-2">
              <DropDown :placeholder="$t('catalog.select_tray_placeholder')" :options="availableTrayOptions" v-model="availableTraySelect" :enabled="!isEditStockQuantity && availableTrayOptions.length > 1" class="w-80" style="min-width: 240px;" @change="onTraySelect()" />
            </div>

            <!-- Action buttons -->
            <!-- <Button type="danger" :enabled="true" @click="isHaltAlarm = true" class="mr-2" v-if="isAdmin">Alarm</Button> -->
            <div class="flex flex-row gap-2" v-if="!isEditStockQuantity && !isRelatedArticlesView && !isReferencing && !isOpening && !isOpen && !hasLocation && selected.trays?.length > 0">
              <Button type="primary" :enabled="canOpenTray && canReturnArticle && canOpenTrayPermissions && canOpenTrayStatus" @click="openTray(selectedTrayItem)">{{ $t('catalog.open_tray') }}</Button>
            </div>
            <div class="flex flex-row gap-2" v-if="!isEditStockQuantity && !isReferencing && !isOpening && !isOpen && !(selected.trays?.length > 0)">
              <Button type="secondary" @click="closeModal()">{{ $t('generic.close') }}</Button>
            </div>
            <div class="flex flex-row gap-2" v-if="isEditStockQuantity && !(isEditStockQuantity && isCabinetBorrowMode)">
              <Button type="warning" :enabled="canApplyStock && !isScaleCalibration && isScaleAllowContinue" @click="startStockTransfer(selected)" v-if="!isReferencing && isOpen && !isCabinetBorrowMode && canTransferStock && !isStockTransferApplied && !isScaleCalibration && hasStockQuantityChanged && canApplyStock">Prenos zaloge</Button>
              <Button type="primary" :enabled="canApplyStock && !isScaleCalibration && isScaleAllowContinue" @click="applyStock(selected)">{{ $t('generic.confirm') }}</Button>
            </div>

            <!-- <Button :type="canEditComment && !isCommentEdit ? 'warning' : 'secondary'" :enabled="canEditComment && !isCommentEdit" class="ml-2" @click="editComment()" v-if="isEditStockQuantity && isCabinetBorrowMode"><i class="fa fa-pen-to-square mr-2"></i>Komentar</Button> -->

            <!-- <Button :type="selectedTrayUsage != 'taken' && !isCommentEdit ? 'primary' : 'secondary'" :enabled="selectedTrayUsage != 'taken' && !isCommentEdit" class="ml-2" @click="applyTrayUsage('taken')" v-if="isEditStockQuantity && isCabinetBorrowMode"><i class="fa fa-arrow-right-from-bracket mr-2"></i>Vzel</Button> -->
            <!-- <Button :type="selectedTrayUsage != 'present' && !isCommentEdit ? 'primary' : 'secondary'" :enabled="selectedTrayUsage != 'present' && !isCommentEdit" class="ml-2" @click="applyTrayUsage('present')" v-if="isEditStockQuantity && isCabinetBorrowMode"><i class="fa fa-arrow-right-to-bracket mr-2"></i>Vrnil</Button> -->
            <Button :type="selectedTrayUsage != 'taken' && !isCommentEdit ? 'primary' : 'secondary'" :enabled="true" class="ml-2" @click="applyTrayUsage('taken')" v-if="isEditStockQuantity && isCabinetBorrowMode && selectedTrayUsage != 'taken' && !isCommentEdit && !autoConfirm"><i class="fa fa-arrow-right-from-bracket mr-2"></i>Vzel</Button>
            <Button :type="selectedTrayUsage != 'present' && !isCommentEdit ? 'primary' : 'secondary'" :enabled="true" class="ml-2" @click="applyTrayUsage('present')" v-if="isEditStockQuantity && isCabinetBorrowMode && selectedTrayUsage != 'present' && !isCommentEdit && !autoConfirm"><i class="fa fa-arrow-right-to-bracket mr-2"></i>Vrnil</Button>

            <div class="flex flex-row gap-2" v-if="isLocationView">
              <Button type="primary" @click="prevRelatedStock()" :enabled="relatedStockCount > 1 && relatedStockIndex - 1 >= 0"><i class="fa fa-chevron-left"></i></Button>
              <Button type="primary" @click="nextRelatedStock()" :enabled="relatedStockCount > 1 && relatedStockIndex + 1 < relatedStockCount"><i class="fa fa-chevron-right"></i></Button>
            </div>
        
          </div>
        </div>
      </template>
    </Modal>

    <!-- persistent not-escapable -->
    <Modal id="privateWindow" size="3xl" class="non-escapable-modal dark:bg-gray-900" v-if="isPrivateModalVisible && !isAnyAlarm" @close="closePrivateModalConditional" @click.outside="closePrivateModalConditional">
      <template #header>
        <div class="flex items-center text-lg dark:text-white">
          Osebni predal
        </div>
      </template>
      <template #body>
        <div class="grid place-items-center w-full gap-8 mb-4 dark:text-white" style="min-height: 250px; max-height: 1000px;" v-if="selectedTrayItem">

          <div v-if="selectedTrayItem.status == 'active'">
            Predal je na voljo.
          </div>
          <div class="text-blue-500" v-else-if="selectedTrayItem.status == 'occupied' && selectedTrayItem.userId == currentUserId">
            Predal je v uporabi.
          </div>
          <div class="text-center" v-else-if="selectedTrayItem.status == 'occupied'">
            <div class="mb-2" v-if="selectedTrayItem.userId || currentUserId"><!-- Note: that query returns only for the current user, so currentUserId is work-around -->
              <i class="fa fa-user dark:text-gray-300 mr-2"></i> {{ getUserDisplayName(selectedTrayItem.userId || currentUserId) }}
            </div>
            <div class="mb-2" v-if="selectedTrayItem.occupiedTime">
              <!--<i class="fa fa-clock dark:text-gray-300 mr-2"></i>-->{{ formatRelativeTime(selectedTrayItem.occupiedTime) }}
            </div>
            <div class="text-red-500 dark:text-red-500">
              Predal je zaseden.
            </div>
          </div>
          <div class="text-red-500 dark:text-red-500" v-else-if="selectedTrayItem.status == 'locked'">
            Predal je zaklenjen.
          </div>

          <!-- <pre>{{ selectedTrayItem }}</pre> -->

        </div>
      </template>
      <template #footer>
        <div class="flex justify-items-end w-full" style="min-height: 30px;" v-if="selected">
          <Button type="secondary" @click="closePrivateModalConditional()" v-if="!isReferencing && !isOpening && !isOpen">Zapri okno</Button>
          <div class="m-auto">
            <span v-if="isReferencing" class="text-gray-500 bg-white outline-none rounded-lg text-sm font-medium px-5 py-2.5 hover:text-gray-900 focus:z-10 dark:bg-gray-700 dark:text-gray-300">
              <Spinner size="6" style="display: inline-block;" /> <span style="display: inline-block; position: relative; top: 3px; left: 10px;">Postavljam omaro v referenco...</span>
            </span>
            <span v-else-if="!isReferencing && !isOpen && isOpening" class="text-gray-500 bg-white outline-none rounded-lg text-sm font-medium px-5 py-2.5 hover:text-gray-900 focus:z-10 dark:bg-gray-700 dark:text-gray-300">
              <Spinner size="6" style="display: inline-block;" /> <span style="display: inline-block; position: relative; top: 3px; left: 10px;">Odpiram predal {{ selectedTrayName }}</span>
            </span>
            <span v-else-if="!isReferencing && isOpen && !isPrevOpen" class="text-gray-500 bg-white outline-none rounded-lg text-sm font-medium px-5 py-2.5 hover:text-gray-900 focus:z-10 dark:bg-gray-700 dark:text-gray-300">
              <i class="fa fa-lock-open text-green-500" style="position: relative; top: 2px;"></i> <span style="display: inline-block; position: relative; top: 3px; left: 10px;">{{ $t('catalog.tray_open_message') }}</span>
            </span>
            <span v-else-if="!isReferencing && isPrevOpen && !isOpening" class="text-gray-500 bg-white outline-none rounded-lg text-sm font-medium px-5 py-2.5 hover:text-gray-900 focus:z-10 dark:bg-gray-700 dark:text-gray-300">
              <i class="fa fa-triangle-exclamation text-amber-500" style="position: relative; top: 3px;"></i> <span style="display: inline-block; position: relative; top: 3px; left: 10px;">Za nadaljevanje najprej zapri prejšnji predal.</span>
            </span>
          </div>
          <div>
          <!-- <Button type="primary" :enabled="canOpenTray && canReturnArticle && canOpenTrayPermissions && canOpenTrayStatus" @click="openTray(selectedTrayItem)" v-if="!isReferencing && !isOpening && !isOpen">{{ $t('catalog.open_tray') }}</Button> -->
          <!-- <Button type="primary" :enabled="canApplyStock" @click="applyStock(selected)" v-if="isEditStockQuantity && !(isEditStockQuantity && isCabinetBorrowMode)">Potrdi</Button> -->
          </div>
        </div>
      </template>
    </Modal>



    <!-- persistent not-escapable -->
    <Modal id="alarmWindow" size="3xl" class="non-escapable-modal dark:bg-gray-900" v-if="isAnyAlarm">
      <template #header>
        <div class="flex items-center text-lg dark:text-white">
          Prekinitev
        </div>
      </template>
      <template #body>
        <div class="grid place-items-center w-full gap-8 mb-4 dark:text-white" style="min-height: 250px; max-height: 1000px;">

          <div>
            <div class="text-center">
              Zaznana je bila prekinitev.
            </div>

            <div class="text-center font-bold text-red-500 mt-2">
              <div v-if="isHaltAlarm">
                <i class="fa fa-exclamation-triangle mr-2"></i>Ročna zaustavitev
              </div>
              <div v-if="isCurtainAlarm">
                <i class="fa fa-exclamation-triangle mr-2"></i>Prekinjena zavesa
              </div>
              <div v-if="isDisruptAlarm">
                <i class="fa fa-exclamation-triangle mr-2"></i>Prekinitev napajanja na krmilniku
              </div>
            </div>

          </div>

        </div>
      </template>
      <template #footer>
        <div class="flex justify-end gap-2 w-full" style="min-height: 30px;">
          <Button type="secondary" @click="clearAlarm()" v-if="isAdmin">Prekliči</Button>
          <Button type="primary" :enabled="true" @click="continueAfterAlarm(selectedTrayItem)">Nadaljuj</Button>
        </div>
      </template>
    </Modal>


    <!-- Barcode choice -->
    <Modal v-if="isBarcodeModalVisible" @close="hideBarcode" @click.outside="hideBarcode">
      <template #header>
        <div class="flex items-center text-lg dark:text-white">
          Črtna koda
        </div>
      </template>
      <template #body>
        <div class="w-full gap-8 mb-4 dark:text-white" style="min-height: 250px; max-height: 1000px;">

          <!-- List matched barcodes -->
          <div v-if="barcodeMatches.length" class="flex flex-col gap-2">
            <div class="text-md">Seznam artiklov s črtno kodo <strong>{{ lastBarcode }}</strong></div>
            <a href="#" v-for="(row, i) in barcodeMatches" @click.prevent="showModal(row)" class="py-4 flex bg-white border border-gray-200 rounded-lg shadow hover:border-blue-600 dark:bg-gray-800 dark:border-gray-700 dark:hover:bg-gray-700">
              <template v-if="row">

                <!-- Article image -->
                <div class="grid place-items-center text-center w-24 mx-4 dark:bg-white dark:p-1 dark:rounded-md" style="max-height: 50px" v-if="row.thumbnailUrl">
                  <img :src="row.thumbnailUrl" alt="" class="w-full aspect-square" style="max-width: 50px;" />
                </div>
                <!-- <div class="grid place-items-center text-center w-24 mx-4" style="width: 50px; height: 50px" v-else-if="allowImageFallback && currentCategoryImageUrl">
                  <img :src="currentCategoryImageUrl" alt="" style="max-width: 50px; max-height: 50px;" />
                </div> -->
                <!-- <div class="grid place-items-center text-center w-24 mx-4" style="width: 50px; height: 50px" v-else-if="allowImageFallback && nearestParentCategorySmallImageUrl">
                  <img :src="nearestParentCategorySmallImageUrl" alt="" style="max-width: 50px; max-height: 50px;" />
                </div> -->
                <div class="grid place-items-center text-gray-200 dark:text-gray-900 text-center w-24 mx-4" style="font-size: 48px; width: 50px; height: 50px;" v-else>
                  <i class="fa fa-image"></i>
                </div>

                <!-- Article name and SKU -->
                <div class="w-full pr-5 flex flex-row justify-between items-center gap-2">
                  <div>
                    <h5 class="text-md tracking-tight text-gray-900 dark:text-white" v-if="row.name">{{ row.name }}</h5>
                    <h3 class="mt-0 text-slate-400" v-if="row.sku">{{ row.sku }}</h3>
                  </div>
                  <!-- <div v-if="row.articleItem.totalQuantity > 0">
                    <h5 class="text-gray-500">{{ row.articleItem.totalQuantity }}&nbsp;kos</h5>
                    <h3 v-if="row.articleItem.sku">&nbsp;</h3>
                  </div> -->
                </div>

                <!-- Tray name -->
                <!-- <div class="w-full pr-5 flex flex-row justify-between items-center gap-2">
                  <div>
                    <h5 class="text-md tracking-tight text-gray-900 dark:text-white" v-if="row.articleItem.name">{{ row.articleItem.name }}</h5>
                    <h3 class="mt-0 text-blue-500" v-if="row.articleItem.status == 'occupied' && row.userId == currentUserId">Osebni predal</h3>
                  </div>
                </div> -->

              </template>
            </a>
          </div>

        </div>
      </template>
      <template #footer>
        <div class="flex justify-end gap-2 w-full" style="min-height: 30px;">
          <Button type="secondary" @click="hideBarcode()">Zapri</Button>
        </div>
      </template>
    </Modal>


    <!-- <Fullscreen>
      <div class="relative w-full h-screen">
        <div class="grid place-items-center h-screen">
          <div>
            <Spinner size="10" style="margin: 0 auto;" />
            <div class="mt-5">{{ $t("Odpiram predal...") }}</div>
          </div>
        </div>
        <div class="absolute bottom-0 left-0 mx-auto text-center px-7 py-5 w-full flex flex-row justify-between">
          <div>Elapsed: 00:00:00</div>
          <div class="flex flex-row gap-4">
            <div>X: 0</div>
            <div>Y: 0</div>
            <div>Z: 0</div>
          </div>
          <div>ETA: 00:00:00</div>
        </div>
      </div>
    </Fullscreen> -->

    <Drawer title="Filter prikaza" :visible="isFilterVisible" @close="() => isFilterVisible = false">
      <div class="grid grid-cols-1 gap-4">

        <!-- Sort by -->
        <div>
          <Label>{{ $t('catalog.sort_by') }}</Label>
          <!-- <Label>Vrstni red</Label> -->
          <DropDown v-model="selectedSortOption" :options="sortOptions" :placeholder="$t('catalog.sort_by')" class="w-full"></DropDown>
        </div>

        <!-- Article options -->
        <div>
          <Label>Vsi artikli</Label>
          <div class="flex flex-row space-x-2">
            <CheckBox v-model="isShowAllArticles" label="Prikaži vse artikle (brez kategorij)" class="w-full"></CheckBox>
          </div>
        </div>

        <!-- User preference -->
        <!-- <div v-if="isCabinetBorrowMode">
          <Label>Uporabniški profil</Label>
          <div class="flex flex-row space-x-2">
            <CheckBox v-model="isUserPrefSize" label="Prikaži le velikost od uporabnika" class="w-full"></CheckBox>
          </div>
        </div> -->

        <!-- Parameters -->
        <div class="grid grid-cols-1 gap-4">
          <!-- Parametric buttons -->
          <template v-for="(parameter, pi) in parameters">
            <div v-key="parameter.id" v-if="parameter.options && parameter.options.length">
              <Label>{{ parameter.name }}</Label>
              <div class="relative grid sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-4 gap-2 w-full text-center">
                <a href="#" v-for="(option, i) in parameter.options" @click.prevent="() => onParameterChange(option, parameter)" :class="{ 'option-selected': !!option.selected, 'parameter-option-compact': !parameter.locked, 'parameter-option-compact-locked': parameter.locked }">
                  {{ option.name || "--" }}
                </a>
              </div>
            </div>
          </template>
        </div>

      </div>
    </Drawer>

    <WarningBanner :severity="warningBannerSeverity" :emphasize="warningBannerType == 'trayOpen'" :modal="warningBannerModal" v-if="isWarningBannerVisible">
      <div class="w-full" v-if="warningBannerType == 'trayOpen'">
        <i class="fa fa-exclamation-triangle text-yellow-300 mr-2"></i> <span>{{ $t('catalog.tray_is_open') }}</span>
      </div>
      <div class="w-full" v-if="warningBannerType == 'weightMeasure'">
        <i class="fa fa-scale-balanced text-black mr-2"></i> <span>Tehtanje je obvezno!</span>
      </div>
      <div class="w-full" v-if="warningBannerType == 'custom'">
        <span v-if="warningBannerMessage">{{ warningBannerMessage }}</span>
      </div>
    </WarningBanner>

    <div class="distancer keyboard-height" v-if="isKeyboardVisible"></div>

  </div>
</template>

<style scoped>
.screen-container {
  @apply grid relative dark:bg-gray-900 dark:text-white p-5;
}

.flip-x {
  -moz-transform: scale(-1, 1);
  -webkit-transform: scale(-1, 1);
  -o-transform: scale(-1, 1);
  -ms-transform: scale(-1, 1);
  transform: scale(-1, 1);
}

.category-button {
  @apply border rounded-lg shadow
    bg-white border-slate-300 hover:border-blue-600
    dark:bg-blue-900 dark:border-blue-700 dark:hover:border-blue-500 dark:hover:bg-blue-800;
}

.category-button.special-button {
  @apply border rounded-lg shadow
    bg-amber-100 border-slate-300 hover:border-blue-600
    dark:bg-gray-800 dark:border-gray-700 dark:hover:bg-gray-700;
}

.category-button .category-button-text {
  @apply text-lg font-bold dark:text-white;
}

.category-button .category-button-text-md {
  @apply /* text-md */ font-bold dark:text-white;
}

.category-button .category-button-hint {
  @apply text-sm font-medium text-slate-700 uppercase dark:text-white;
}

.category-button-background {
  background: linear-gradient(300deg, rgba(254,243,199,1) 0%, rgba(255,255,255,1) 62%);
}

.parameter-option {
  @apply px-2 py-3 border border-gray-200 rounded-lg shadow bg-white dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-blue-900 outline-none; /* dark:bg-gray-800 dark:border-gray-700 dark:hover:bg-gray-700 */
}

.parameter-option-compact {
  @apply px-3 py-2 border border-gray-200 text-sm rounded-lg shadow bg-white dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-blue-900 outline-none;
}

.parameter-option-compact-locked {
  @apply px-3 py-2 border border-gray-100 text-sm rounded-lg shadow bg-gray-50 dark:bg-gray-800 dark:border-gray-900 dark:text-gray-500 outline-none;
}

.parameter-option.option-selected,
.parameter-option-compact.option-selected {
  @apply border-blue-400 bg-blue-300 hover:bg-blue-200 dark:bg-blue-800 dark:border-blue-700 dark:hover:bg-blue-900;
}

.row-category {
  @apply border-gray-200;
  background: linear-gradient(320deg, #fef3c7 0%, rgba(255,255,255,1) 70%);
}

.row-article {
  @apply border-gray-200;
  background: linear-gradient(320deg, #e2e8f0 0%, rgba(255,255,255,1) 70%);
}

.dark .row-category {
  @apply bg-gray-800 border-gray-700;
  border-color: rgb(26, 86, 219);
  background: rgb(35, 56, 118);
}

.dark .row-article {
  @apply bg-gray-800 border-gray-700;
  background: rgb(31, 41, 55);
}
</style>
